/**
 * Data-driven definitions for SEO landing pages.
 * Each entry powers one route under /solutions/* or /industries/*.
 *
 * Schema-markup nodes come from `getSchemaForPage()`; `getSeoForPath()` in
 * seo/siteSeo.ts composes them into the route's single JSON-LD graph.
 * Hero images reference existing assets in /public/screenshots/ or STITCH_IMAGES.
 */

import { STITCH_IMAGES } from './stitch-images';
import {
  ANNUAL_DISCOUNT_PERCENT,
  BASIC_TIER,
  ENTERPRISE_ONLY_MODULES,
  PRO_TIER,
  TRIAL_POLICY,
} from './pricing';
import { breadcrumbListSchema, type BreadcrumbItem } from '../seo/breadcrumbs';

const SITE_ORIGIN = 'https://www.clear-talent.com';
const ORG_NAME = 'CLEAR Talent';
const ORG_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
const SOFTWARE_ID = `${SITE_ORIGIN}/#software`;

// The Enterprise-only succession module, named as /pricing lists it, so the
// talent management page cannot promise it on a plan that does not include it.
const SUCCESSION_MODULE =
  ENTERPRISE_ONLY_MODULES.find((module) => /succession/i.test(module)) ?? 'Talent & Succession Planning';

export interface PageCta {
  text: string;
  href: string;
}

export interface Bullet {
  title: string;
  description: string;
}

/**
 * A product screen shown inside a section, with numbered call-outs in reading
 * order. Only real captures of the software belong here: each annotation names
 * a label that is on the screen and says what it does, so an illustration would
 * make every call-out untrue. `width` and `height` carry the capture's aspect
 * ratio so the image reserves its space before it loads.
 */
export interface SectionFigure {
  src: string;
  srcSet?: string;
  alt: string;
  width: number;
  height: number;
  caption?: string;
  annotations?: Bullet[];
}

export interface Section {
  id: string;
  eyebrow?: string;
  heading: string;
  intro?: string;
  paragraphs?: string[];
  bullets?: Bullet[];
  steps?: Bullet[];
  figure?: SectionFigure;
  variant?: 'default' | 'tinted' | 'dark';
  cta?: PageCta;
}

export interface Faq {
  q: string;
  a: string;
}

export interface SolutionPage {
  slug: string;
  category: 'solutions' | 'industries';
  primaryKeyword: string;
  secondaryKeywords: string[];
  seoTitle: string;
  metaDescription: string;
  eyebrow: string;
  h1: string;
  heroIntro: string;
  heroImage: string;
  /**
   * Describes what the hero image actually shows — the screen and what is on
   * it, or the illustration — for screen readers and image search. Never the
   * page's keyword: the H1 already states the topic, and an alt built from it
   * reads as keyword repetition while describing nothing.
   */
  heroImageAlt: string;
  heroStats?: { value: string; label: string }[];
  primaryCta: PageCta;
  secondaryCta?: PageCta;
  sections: Section[];
  faqs: Faq[];
  internalLinks: { label: string; href: string }[];
  /**
   * What the software actually does on this page, for the SoftwareApplication
   * `featureList`. Without it the list falls back to the page's keyword
   * variants — which describes the search term a buyer typed, not the product,
   * and reads as keyword repetition in structured data.
   */
  schemaFeatures?: string[];
  /**
   * ISO date the page content last changed materially. When set, the generated
   * sitemap carries it as `<lastmod>` (see `routeLastModified` in
   * entry-server.tsx), so a rewrite gives crawlers a reason to come back. Leave
   * it unset rather than guessing: a lastmod that is not accurate teaches search
   * engines to ignore the field across the whole sitemap.
   */
  dateModified?: string;
  /**
   * Where the page's offer is sold, for a regional page. It becomes the
   * `areaServed` of an Offer on the SoftwareApplication node — schema.org has no
   * `areaServed` on SoftwareApplication itself — priced from `constants/pricing.ts`
   * like every other price the site publishes.
   */
  areaServed?: { '@type': 'Country' | 'AdministrativeArea'; name: string }[];
}

/**
 * Home > Solutions > page, for the visible trail and the BreadcrumbList alike.
 * Industry pages sit under Solutions too: /solutions is the hub that lists them,
 * and there is no /industries index to point at. The page is named by its
 * primary keyword — the label the hub links it with — rather than its eyebrow,
 * which reads as a tagline ("Meet Ava", "For Retail") out of context.
 */
export const getSolutionBreadcrumbs = (page: SolutionPage): BreadcrumbItem[] => [
  { name: 'Home', path: '/' },
  { name: 'Solutions', path: '/solutions' },
  { name: page.primaryKeyword, path: `/${page.category}/${page.slug}` },
];

const buildSchema = (page: SolutionPage) => {
  const url = `${SITE_ORIGIN}/${page.category}/${page.slug}`;
  // `screenshot` has to be a picture of the software, so only the real product
  // captures under /screenshots/ qualify — several pages lead with illustration.
  const screenshot: Record<string, string> = page.heroImage.startsWith('/screenshots/')
    ? { screenshot: `${SITE_ORIGIN}${page.heroImage}` }
    : {};
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      '@id': SOFTWARE_ID,
      name: ORG_NAME,
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: 'Performance Management Software',
      operatingSystem: ['Web', 'iOS', 'Android'],
      url: SITE_ORIGIN,
      description: page.metaDescription,
      featureList: page.schemaFeatures ?? [page.primaryKeyword, ...page.secondaryKeywords],
      ...screenshot,
      ...(page.areaServed
        ? {
            offers: {
              '@type': 'Offer',
              name: `${BASIC_TIER.name} plan`,
              url: `${SITE_ORIGIN}/pricing`,
              price: BASIC_TIER.monthlyUsd,
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              areaServed: page.areaServed,
            },
          }
        : {}),
      provider: { '@id': ORG_ID },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: page.seoTitle,
      description: page.metaDescription,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': SOFTWARE_ID },
      inLanguage: 'en',
    },
    breadcrumbListSchema(getSolutionBreadcrumbs(page)),
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    },
  ];
};

export const SOLUTION_PAGES: SolutionPage[] = [
  // ─────────────────────────────────────────────
  // 1. Performance Management Software
  // ─────────────────────────────────────────────
  {
    slug: 'performance-management-software',
    category: 'solutions',
    primaryKeyword: 'Performance Management Software',
    secondaryKeywords: [
      'employee performance management system',
      'performance management platform',
      'performance management tools',
      'PMS software',
      'performance tracking software',
      'HR performance software',
    ],
    seoTitle: 'Performance Management Software | CLEAR Talent',
    metaDescription:
      'Align goals, run 360° reviews, assess competencies and develop talent in one platform. A+ security rated. Book a demo with CLEAR Talent.',
    eyebrow: 'Performance Management',
    h1: 'Performance Management Software Built for Modern HR Teams',
    heroIntro:
      "Performance reviews shouldn't be a once-a-year scramble. CLEAR Talent gives you a single, connected platform to set goals, run fair reviews, assess competencies and coach your people — without the spreadsheets, the chase emails, or the bias.",
    heroImage: '/screenshots/03-dashboard-main.webp',
    heroImageAlt:
      'CLEAR Talent admin dashboard with an action centre of priorities, launch-readiness steps, a 30-day organisation pulse of adoption and review-cycle figures, and a competency framework chart',
    heroStats: [
      { value: 'All-in-one', label: 'Goals, KPIs, reviews & 360°' },
      { value: '16', label: 'Languages supported' },
      { value: 'A+', label: 'Security rating' },
    ],
    primaryCta: { text: 'Book a live demo', href: '/demo?source=performance-management-software' },
    secondaryCta: { text: 'Start free trial', href: 'https://login.clear-talent.com/register' },
    sections: [
      {
        id: 'why-switch',
        eyebrow: 'Why teams switch',
        heading: 'Why HR leaders switch to CLEAR Talent',
        paragraphs: [
          'Legacy HRIS modules were built for record-keeping, not performance. CLEAR Talent is a purpose-built performance management system that turns strategy into measurable execution.',
          'Every goal ties to a business objective, every rating is backed by evidence, and every review produces a decision managers can actually use.',
        ],
      },
      {
        id: 'one-platform',
        eyebrow: 'One platform',
        heading: 'One platform. Every performance workflow.',
        bullets: [
          {
            title: 'Strategic Linked Goals (SLG)',
            description:
              'Cascade company OKRs down to individual goals in minutes. Managers see alignment in real time; employees see why their work matters.',
          },
          {
            title: 'Competency Assessment Framework (CAF)',
            description:
              'Evidence-based competency ratings replace gut feel. Build job-specific scales once and reuse them across the organisation.',
          },
          {
            title: '360° Feedback',
            description:
              'Collect input from peers, managers, reports and stakeholders. Anonymised, structured, and tied back to competencies.',
          },
          {
            title: 'Automated Reviews & Reports',
            description:
              'One-click PDF and Excel exports for mid-year, year-end, calibration meetings and board reviews.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'outcomes',
        eyebrow: 'Outcomes',
        heading: 'Operational outcomes from a connected workflow',
        paragraphs: [
          'CLEAR Talent is built for faster cycles, higher completion rates, and more defensible ratings. The platform automates the admin — reminders, chase emails, roll-up scoring, report generation — so managers spend their review time on people, not paperwork.',
        ],
        cta: { text: 'Get your custom ROI estimate', href: '/contact?reason=roi&source=performance-management-software' },
      },
      {
        id: 'security',
        eyebrow: 'Trust',
        heading: 'Enterprise-grade security, by default',
        paragraphs: [
          'End-to-end TLS encryption, signed 24-hour file URLs, Google Cloud Platform hosting, continuous threat monitoring via GCP Security Command Center Premium, and an A+ Mozilla Observatory score.',
          'Your people data is protected by enterprise-grade controls, on every plan, by default.',
        ],
        variant: 'dark',
      },
    ],
    faqs: [
      {
        q: "What's the difference between performance management software and an HRIS?",
        a: 'An HRIS is the system of record for employment data: contracts, pay, absence and the org chart. Performance management software runs the work that happens on top of that record — setting and tracking goals, collecting feedback, assessing competencies, running review cycles and planning development. CLEAR Talent runs alongside your HRIS rather than replacing it, and holds performance and development data, not payroll.',
      },
      {
        q: 'What does CLEAR Talent include?',
        a: 'It covers goals and OKRs that cascade from company objectives (Strategic Linked Goals), evidence-based competency assessment (the Competency Assessment Framework), anonymised 360° feedback, review cycles with calibration, development plans and succession planning. Reminders, roll-up scoring and PDF or Excel reports are automated, so goal scores, competency ratings and 360° input reach the review without anyone re-keying them.',
      },
      {
        q: 'How long does implementation take?',
        a: 'It depends on the size of the organisation, how much existing data is migrated, the integrations involved and how complex your review workflow is, so the rollout is scoped with each customer before work begins. Starting with one review cycle or one department and widening from there keeps the first launch manageable while the framework settles.',
      },
      {
        q: 'Can we migrate existing review data and connect our HRIS?',
        a: 'There is no prebuilt connector to a named HRIS. Organisation structure, goals and ratings come in through CSV and Excel bulk import or the API, and records can be exported to CSV, Excel or PDF on every plan. How your current data maps onto CLEAR Talent is agreed during discovery, before anything moves, so historic reviews arrive in a shape the new cycles can use.',
      },
      {
        q: 'Is CLEAR Talent GDPR-compliant?',
        a: 'CLEAR Talent provides the controls a GDPR programme needs for performance data: subject access request exports, right-to-be-forgotten workflows, a data processing agreement, role-based access and audit logs of sign-ins, exports and rating changes. Compliance also depends on how you configure and use the platform, so your DPO should review the DPA and the settings as part of the evaluation.',
      },
      {
        q: "What's the pricing model?",
        a: `Plans are published and billed monthly or annually, with ${ANNUAL_DISCOUNT_PERCENT}% off annual billing. The ${BASIC_TIER.name} plan starts at USD ${BASIC_TIER.monthlyUsd} per month and the ${PRO_TIER.name} plan at USD ${PRO_TIER.monthlyUsd}, each covering a set number of admin and staff users with published rates above that. Larger organisations move to a quoted Enterprise plan, and there is a ${TRIAL_POLICY.toLowerCase()}.`,
      },
    ],
    internalLinks: [
      { label: 'See all features', href: '/features' },
      { label: '360° Feedback Software', href: '/solutions/360-degree-feedback-software' },
      { label: 'OKR & Goal Setting Software', href: '/solutions/okr-goal-setting-software' },
      { label: 'Continuous Performance Management', href: '/solutions/continuous-performance-management' },
      { label: 'Employee Development Software', href: '/solutions/employee-development-software' },
      {
        label: 'Talent Management Software: performance, competencies, development and succession together',
        href: '/solutions/talent-management-software',
      },
      { label: 'Product showcase', href: '/showcase' },
      { label: 'Guide: what is performance management?', href: '/blog/what-is-performance-management' },
      {
        label: 'Guide: the performance review process, step by step, with a 12-month calendar',
        href: '/blog/performance-review-process',
      },
      {
        label: 'Software vs spreadsheets: a decision guide and review tracker',
        href: '/resources/performance-management-software-vs-spreadsheets',
      },
      {
        label: 'Buyer’s checklist: 25 questions to ask vendors before the demo',
        href: '/resources/performance-management-software-buyers-checklist',
      },
      {
        label: 'RFP template and vendor evaluation scorecard',
        href: '/resources/performance-management-software-rfp',
      },
      {
        label: 'Business case template for your budget request',
        href: '/resources/performance-management-business-case',
      },
      {
        label: 'ROI calculator: what your reviews cost and the payback',
        href: '/resources/performance-management-roi-calculator',
      },
    ],
    // Dated 2026-10-11 for the rewritten FAQs, 2026-10-13 for the link to the
    // talent management page, 2026-10-15 for the buyer's checklist link, and
    // 2026-10-19 for the links to the performance review process guide and the
    // ROI calculator.
    dateModified: '2026-10-19',
  },

  // ─────────────────────────────────────────────
  // 2. 360 Degree Feedback Software
  // ─────────────────────────────────────────────
  {
    slug: '360-degree-feedback-software',
    category: 'solutions',
    primaryKeyword: '360 Degree Feedback Software',
    secondaryKeywords: [
      '360 review software',
      'multi-rater feedback tool',
      '360 feedback platform',
      'anonymous peer feedback software',
      '360 assessment tool',
      'upward feedback tool',
    ],
    seoTitle: '360 Degree Feedback Software | CLEAR Talent',
    metaDescription:
      'Run structured 360° reviews with anonymised peer, manager and direct-report feedback — tied to competencies. Book a demo with CLEAR Talent.',
    eyebrow: '360° Feedback',
    h1: "360° Feedback Software That People Actually Trust",
    heroIntro:
      "Most 360° feedback tools produce a pile of anonymous quotes no one acts on. CLEAR Talent structures every rater's input against the competencies that matter for the role — so employees leave their 360° with a coachable, prioritised development plan, not a word cloud.",
    heroImage: '/screenshots/12-feedback-main.webp',
    heroImageAlt:
      'CLEAR Talent Continuous Feedback screen listing development notes and kudos, each tagged to competencies and marked private or public, beside feedback stats and quick templates',
    heroStats: [
      { value: 'Structured', label: 'Competency-linked input' },
      { value: 'Flexible', label: 'Configurable rater groups' },
      { value: 'Built-in', label: 'Anonymity controls' },
    ],
    primaryCta: { text: 'See a sample 360° report', href: '/contact?reason=sample-360&source=360-feedback' },
    secondaryCta: { text: 'Launch a pilot 360°', href: '/contact?reason=pilot&source=360-feedback' },
    sections: [
      {
        id: 'structured',
        eyebrow: 'Structured, not survey-monkey',
        heading: 'Structured feedback, not a survey-monkey exercise',
        paragraphs: [
          "Open-text surveys create noise. CLEAR Talent's 360° runs against your Competency Assessment Framework (CAF), so every piece of feedback maps to a specific behavioural competency with a rating scale you control.",
          'The output is a structured, comparable, longitudinal report — not a one-off.',
        ],
      },
      {
        id: 'how-it-works',
        eyebrow: 'How it works',
        heading: "How CLEAR Talent's 360° works",
        steps: [
          {
            title: 'Choose raters intelligently',
            description:
              'Managers, peers, direct reports, cross-functional stakeholders. Employees nominate; managers approve.',
          },
          {
            title: 'Collect evidence against competencies',
            description:
              'Each rater scores a targeted set of competencies with optional written evidence. Review length depends on the framework and questions selected.',
          },
          {
            title: 'Anonymise and consolidate automatically',
            description:
              'Individual raters are anonymised by group, outliers flagged, minimum-rater thresholds enforced for statistical integrity.',
          },
          {
            title: 'Deliver a coachable report',
            description:
              'Strengths, blind spots, development priorities, and a suggested IDP — all in one PDF or shareable link.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'why-anonymity',
        eyebrow: 'Why it works',
        heading: 'Why anonymity + structure beats open-text surveys',
        paragraphs: [
          'Honest feedback requires psychological safety. Actionable feedback requires structure. CLEAR Talent delivers both: raters are anonymised within their group, and the rating structure makes comparison across raters defensible and non-biased.',
        ],
      },
      {
        id: 'tie-to-development',
        eyebrow: 'From feedback to growth',
        heading: 'Tie 360° feedback to development plans',
        paragraphs: [
          'Every 360° automatically generates a suggested Individual Development Plan (IDP) based on the lowest-scoring, highest-importance competencies. Managers turn feedback into coaching conversations — not a filed PDF.',
        ],
        cta: { text: 'See how IDPs work', href: '/solutions/employee-development-software' },
      },
    ],
    faqs: [
      {
        q: 'What is 360 degree feedback software?',
        a: '360 degree feedback software collects structured input on one person from the people around them — their manager, peers, direct reports and other stakeholders — alongside their own self-assessment, then consolidates it into a report. In CLEAR Talent every rater scores competencies from the same framework, so the report compares like with like and leads straight into a development plan instead of a list of anonymous quotes.',
      },
      {
        q: 'Is 360° feedback truly anonymous?',
        a: 'Yes. Raters are anonymised within their group — peers, direct reports, stakeholders — and minimum-rater thresholds are enforced, so results only appear for a group once enough people have responded that no single comment can be traced back. Raw rater identities are never displayed, and managers see aggregated, anonymised group data rather than who rated what.',
      },
      {
        q: 'How many raters should each employee have?',
        a: 'Enough in each group to clear its anonymity threshold, and no more than can give informed feedback. The right mix depends on the role: a people manager needs input from direct reports, while an individual contributor may be better served by peers and cross-functional stakeholders. Employees nominate their raters and managers approve the list, so the selection is checked before anyone is asked.',
      },
      {
        q: 'Can we run 360° feedback separately from the annual review?',
        a: 'Yes. A 360° can run on any cadence — quarterly, at the end of a project, ahead of a promotion decision or as a stand-alone development exercise. When it runs inside a review cycle, the structured results feed the review alongside goal scores and competency ratings, weighted by your policy. When it runs on its own, the report goes straight to the development conversation.',
      },
      {
        q: 'What does a 360° feedback report include?',
        a: 'Strengths, blind spots and development priorities, consolidated from every rater group against the competencies in your framework, with outliers flagged. The report also suggests an individual development plan built from the lowest-scoring, highest-importance competencies, so the manager and employee start the conversation from clear priorities rather than raw scores. It is available as a PDF or a shareable link.',
      },
      {
        q: 'How long does a 360° take a rater to complete?',
        a: 'It depends on how many competencies are in scope, the rating scale and whether written evidence is required. Each rater scores a targeted set of competencies rather than the whole framework, which keeps the form focused. Fewer, well-chosen competencies with a sentence of evidence each usually give a more useful report than a long questionnaire answered in a hurry.',
      },
    ],
    internalLinks: [
      { label: 'Competency Management Software', href: '/solutions/competency-management-software' },
      { label: 'Continuous Performance Management', href: '/solutions/continuous-performance-management' },
      { label: 'Employee Development Software', href: '/solutions/employee-development-software' },
      { label: 'Performance Management Software', href: '/solutions/performance-management-software' },
      { label: 'Glossary: what is a 360 review?', href: '/glossary/360-degree-review' },
      { label: 'Guide: 100 example 360 feedback questions', href: '/blog/360-feedback-questions' },
      { label: 'Template: 360 feedback form, with completed examples', href: '/resources/templates/360-feedback-form-template' },
      { label: 'Best 360 feedback tools, compared', href: '/best-360-feedback-tools' },
      { label: 'Guide: what is performance management?', href: '/blog/what-is-performance-management' },
    ],
    // Dated 2026-10-11 for the rewritten FAQs, and re-dated 2026-10-17 for the
    // link to the 360 feedback form template.
    dateModified: '2026-10-17',
  },

  // ─────────────────────────────────────────────
  // 3. OKR & Goal Setting Software
  // ─────────────────────────────────────────────
  {
    slug: 'okr-goal-setting-software',
    category: 'solutions',
    primaryKeyword: 'OKR & Goal Setting Software',
    secondaryKeywords: [
      'OKR software',
      'goal setting software',
      'goal tracking software',
      'SMART goals software',
      'strategic goal alignment',
      'cascading goals software',
    ],
    /**
     * The page drew real sessions but had no index footprint at all. Discovery
     * was not the gap: it is linked from the header, the footer, the homepage,
     * the /solutions hub and several solution, blog and comparison pages, and it
     * is in the generated sitemap. The page itself was thin — short sections and
     * five one-line FAQs that said less than the cascading guide and the
     * technology industry page already said about the same product. The rewrite
     * answers what a buyer evaluating OKR software actually needs to know, and
     * the title now names the category instead of a benefit line that never
     * said "OKR software" at all.
     *
     * The same gap showed on the employee-development and continuous-performance
     * pages, which share this template. Much of what the template renders is the
     * same on every page, so each of the three now carries sections only it
     * could publish: here, the annotated goal alignment screen and a mid-quarter
     * re-prioritisation walkthrough.
     */
    seoTitle: 'OKR Software: Link Every Goal to Strategy | CLEAR Talent',
    metaDescription:
      'OKR and goal-setting software that connects every goal to company strategy, with a KPI library, at-risk flags and scores that feed each review. Book a demo.',
    dateModified: '2026-09-19',
    eyebrow: 'Goals & OKRs',
    h1: 'OKR Software That Connects Strategy to Every Role',
    heroIntro:
      "Strategy dies in the gap between the leadership offsite and Monday morning. CLEAR Talent's Strategic Linked Goals (SLG) framework cascades every company objective down to the individuals who deliver it — visibly, measurably, and in real time.",
    heroImage: '/screenshots/04-organizational-goals-main.webp',
    heroImageAlt:
      'CLEAR Talent Organizational Goals screen showing a goal cycle, portfolio progress, an attention queue of goals with no owner or parent alignment, and the goal hierarchy with weighted KPIs',
    heroStats: [
      { value: 'Enforced', label: 'Parent-goal linkage' },
      { value: 'Weighted', label: 'Goals & KPI portfolios' },
      { value: 'AI-drafted', label: 'Goals, KPIs & weightings' },
    ],
    primaryCta: { text: 'Book an OKR rollout demo', href: '/demo?source=okr' },
    secondaryCta: { text: 'See SLG in action', href: '/showcase' },
    sections: [
      {
        id: 'problem',
        eyebrow: 'The problem',
        heading: 'Why goal programmes lose the thread by the second quarter',
        paragraphs: [
          'Most organisations are not short of objectives. They are agreed at the start of the year and translated through a round of planning meetings, and by the second quarter the connection between what leadership asked for and what teams are actually working on has become a matter of opinion.',
          'The framework is rarely the problem. The link between an individual goal and the objective it serves usually lives in a slide deck or a spreadsheet column nobody is obliged to fill in. Goals get written without a parent, weights stay equal because nobody chose otherwise, and progress is reconstructed from memory when the review arrives.',
          'OKR software earns its place by making those links structural: a goal cannot float free of strategy, a priority is stated as a weight rather than implied, and progress is visible while there is still time to act on it.',
        ],
      },
      {
        id: 'strategy-to-execution',
        eyebrow: 'How the cascade works',
        heading: 'From boardroom objective to individual goal',
        intro:
          'CLEAR Talent holds the cascade as one connected structure, not a set of documents that someone has to keep in step by hand.',
        steps: [
          {
            title: 'Set the organisation objectives',
            description:
              'Leadership records the outcomes the organisation is working towards. Most organisations set these annually, because that is the horizon on which company-level objectives genuinely change.',
          },
          {
            title: 'Translate them for departments and teams',
            description:
              'Each department and team sets goals describing its own contribution to a parent objective — a distinct contribution, not the company objective copied down with a smaller number attached.',
          },
          {
            title: 'Link every individual goal to a parent',
            description:
              'Individual goals are written against a team or organisation goal. Strategic Linked Goals will not accept an individual goal without one, so any piece of work can be traced upwards to the objective it serves.',
          },
          {
            title: 'Approve, track and score',
            description:
              'Goals an employee drafts go to their manager, who can approve them, reject them or send them back with requested changes. Approved goals are tracked through check-ins and scored at the end of the cycle.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'slg-difference',
        eyebrow: 'The SLG difference',
        heading: 'Linkage that is enforced, not requested',
        paragraphs: [
          'Asking people to link each goal to a parent objective works for as long as someone keeps checking. Strategic Linked Goals makes it a rule instead: an individual goal that is not connected to a parent cannot be created.',
          'That single design decision removes the strategic drift you see in spreadsheet OKRs. Orphaned goals — work nobody at the top knows is happening — cannot quietly accumulate, and a team cannot spend a quarter optimising for something the organisation stopped asking for.',
          'A visual cascade map shows every goal and the objective it rolls up to, so leaders can see which objectives are well supported and which have nothing beneath them yet.',
        ],
        cta: { text: 'Read the goal cascading guide', href: '/blog/how-to-cascade-company-goals' },
      },
      {
        id: 'alignment-view',
        eyebrow: 'Inside the product',
        heading: 'Where the cascade breaks, on one screen',
        intro:
          'A cascade is only as strong as its weakest links, so the goal alignment view opens on the problems rather than on the full tree. It is where an HR or strategy lead checks, mid-cycle, whether the objectives set at the start of the year are still the ones people are working towards.',
        figure: {
          src: '/images/features/goal-alignment-1280.webp',
          srcSet: '/images/features/goal-alignment-640.webp 640w, /images/features/goal-alignment-1280.webp 1280w',
          alt: 'CLEAR Talent goal alignment view showing overall alignment, weak alignment, cascade gaps, unlinked KPIs and a warning about active goals without an owner',
          width: 1280,
          height: 600,
          caption:
            'Goal alignment in CLEAR Talent: a decision snapshot for the current goal cycle, above a cascade that can be explored as a network, a tree or a list.',
          annotations: [
            {
              title: 'Overall alignment',
              description:
                'One alignment score for the goals in view, with how many of them are strongly or moderately aligned, so a leadership team can tell at a glance whether the cycle is holding together.',
            },
            {
              title: 'Weak alignment and cascade gaps',
              description:
                'Goals with weak or missing alignment signals, and goals below organisation level with no parent connection. These are where the cascade breaks — a short list to work through rather than a tree to search.',
            },
            {
              title: 'Unlinked KPIs',
              description:
                'KPIs in the cycle that are not connected to any goal: measures someone is tracking that do not yet count towards an objective.',
            },
            {
              title: 'Goals without an owner',
              description:
                'Active goals with nobody accountable are called out on their own, because direct KPI inputs on those goals are left out of score roll-ups until an owner or assignee is assigned.',
            },
            {
              title: 'Network, tree or list',
              description:
                'The cascade itself can be explored three ways: a network to see relationships, a tree to follow it down from the top, and a list for a focused review.',
            },
          ],
        },
        variant: 'tinted',
      },
      {
        id: 'methodology-agnostic',
        eyebrow: 'Your framework',
        heading: 'OKRs, SMART goals and weighted KPIs on one platform',
        intro:
          'CLEAR Talent is methodology-agnostic. What holds a goal programme together is the linkage between goals, not the vocabulary used to describe them.',
        bullets: [
          {
            title: 'OKRs with key results',
            description:
              'An objective and the measurable key results that show it was achieved, typically on a quarterly rhythm for teams and individuals.',
          },
          {
            title: 'SMART goals',
            description:
              'Specific, measurable goals with success criteria and a target date. Ava can suggest SMART goals for a role as a starting point for the employee and manager to edit.',
          },
          {
            title: 'Weighted KPIs',
            description:
              "Ongoing measures held together in a KPI portfolio and weighted to reflect what the role is accountable for. Portfolio weights count only active KPIs, so a retired metric no longer holds a share of anyone's score.",
          },
          {
            title: 'A mix of all three',
            description:
              'Departments and levels can use different frameworks and cadences on the same platform, and every goal still rolls up to the same organisation objectives.',
          },
        ],
      },
      {
        id: 'kpi-library',
        eyebrow: 'KPI library',
        heading: 'KPIs that mean the same thing in every team',
        intro:
          '"Exceeded target" is only a useful phrase if it means the same thing in sales, in support and in finance. A central KPI library is how that consistency survives a dozen different managers.',
        bullets: [
          {
            title: 'Reusable, role-aware templates',
            description:
              'Define a KPI once and reuse it for every role that carries it, instead of each team rebuilding its own version with slightly different wording.',
          },
          {
            title: 'Performance bands and guardrails',
            description:
              'Bands and guardrails are agreed centrally, which keeps scoring consistent between teams rather than letting each one recalibrate its own definition of success.',
          },
          {
            title: 'Cascaded with configurable weightings',
            description:
              'KPIs cascade from organisation goals down to teams and individuals with the weightings you set, so a score reflects the measures a role is actually accountable for.',
          },
          {
            title: 'Evidence linked to every score',
            description:
              'Evidence can be linked to each KPI result, so when someone asks how a score was reached, the answer is in the record rather than in a mailbox.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'weighting',
        eyebrow: 'Priorities',
        heading: 'Weighting is the priority signal people actually read',
        paragraphs: [
          'Once someone holds more than two goals, the weights on them are the clearest statement of priority they will get all year. Equal weights say that nothing matters more than anything else — which is almost never what leadership means.',
          "Goal weighting in CLEAR Talent is configurable per level: equal, manager-weighted or custom percentages. A person's goal weights for a period cannot total more than 100%, so a new priority has to take its share from an existing one rather than quietly landing on top of it.",
          'One level up, the balance between goals and KPIs on one side and competency assessment on the other is configurable per level as well — so a revenue-generating role and a control function can sit on the same platform without being measured the same way.',
        ],
      },
      {
        id: 'real-time',
        eyebrow: 'Live dashboards',
        heading: 'Spot a drifting goal in month two, not at review time',
        intro: 'Dashboards keep progress in front of the people who can still change the outcome.',
        bullets: [
          {
            title: 'Completion by department',
            description: 'Goal completion rates by department, team, manager and individual.',
          },
          {
            title: 'At-risk flags',
            description:
              'Goals falling behind are flagged automatically from pace and check-in signals, so the conversation happens while the quarter is still running.',
          },
          {
            title: 'Cascade coverage',
            description:
              'Objectives that nothing has been linked to yet are surfaced for leaders — usually the first sign that a priority has not landed.',
          },
          {
            title: 'Score projections',
            description:
              'End-of-cycle score projections based on current pace, so an outcome nobody wanted is visible while there is still time to change it.',
          },
        ],
      },
      {
        id: 'mid-quarter-walkthrough',
        eyebrow: 'Walkthrough',
        heading: 'A new priority lands halfway through the quarter',
        intro:
          'Goal programmes rarely fail at the planning offsite. They fail when leadership changes its mind six weeks later and nobody updates anything. Here is that moment followed through the platform, as an illustrative example.',
        steps: [
          {
            title: 'Leadership adds the objective to the live cycle',
            description:
              'The new organisation goal goes into the current cycle with its success criteria, a weight, a target date and the departments it applies to. Assignees are limited to admins, managers and people managers in those departments, so it lands with someone who can act on it.',
          },
          {
            title: 'The roll-up rule is decided up front',
            description:
              'Every goal carries a progress roll-up setting that controls how its progress is calculated from child goals, linked KPIs and key results, with an average as the default. Choosing it now avoids an argument at quarter end about why the parent reads the way it does.',
          },
          {
            title: 'Weights make room instead of piling up',
            description:
              "Department and team goals are written against the new objective. Because a person's goal weights for a period cannot total more than 100%, the new priority takes its share from an existing goal. At organisation level the portfolio can be rebalanced equally or weighted by hand.",
          },
          {
            title: 'The alignment view confirms it landed',
            description:
              'A week later, the goal alignment view shows whether the change reached the people doing the work: cascade gaps where a goal has no parent, KPIs not yet linked to any goal, and active goals still without an owner.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'manager-coaching',
        eyebrow: 'Manager workflows',
        heading: 'Check-ins that keep goals live between cycles',
        paragraphs: [
          'A cascade that is never revisited between the setting meeting and the review meeting turns into documentation. Check-in prompts tied to each goal and structured progress updates replace the ad-hoc "how\'s it going?" catch-up, so managers coach against the goal and its evidence rather than against what they happen to remember.',
          'Priorities move, and changing a goal mid-cycle is normal. What damages trust is a goal quietly rewritten at year end, so goal status changes are written to the audit log and an adjusted goal stays a visible decision.',
          'Check-ins can be completed on the web or in the iOS and Android apps, in 16 languages, so keeping a goal current is not something only desk-based staff find easy.',
        ],
        cta: { text: 'See continuous performance management', href: '/solutions/continuous-performance-management' },
      },
      {
        id: 'into-the-review',
        eyebrow: 'Into the review',
        heading: 'Goal scores that carry straight into the performance review',
        paragraphs: [
          'Goal scores roll up into the performance review automatically, so the cycle closes on the same data it opened with. Nobody retypes a quarter of results at year end, and nobody argues about which version of a spreadsheet is current.',
          'Quarterly and annual cycles run side by side. Teams can work to quarterly OKRs while compensation and promotion decisions stay on the annual cycle, with each quarter feeding the annual review. Decide early how much of the annual rating goal scores should carry, because the roll-up applies exactly the weighting you configure.',
          'In the review, goal achievement sits alongside evidence-based competency ratings and structured 360° feedback, combining into one composite rating whose parts stay visible — so a calibration session can question a goal score without disturbing the rest of the rating.',
        ],
        cta: { text: 'See performance review software', href: '/solutions/employee-performance-review-software' },
        variant: 'tinted',
      },
      {
        id: 'ava',
        eyebrow: 'AI assistance',
        heading: 'Ava drafts the goals. People decide what matters.',
        paragraphs: [
          'The blank page is the slowest part of goal setting. Ava can draft goals, KPIs and weightings tailored to a role and hand them back for the employee and manager to edit and approve.',
          'What no tool can do is decide what your organisation should prioritise this year. Every Ava output is labelled and editable, and Ava can be switched off per cycle, team or module.',
        ],
        cta: { text: 'See how Ava assists', href: '/solutions/ai-performance-management' },
        variant: 'dark',
      },
    ],
    faqs: [
      {
        q: 'What is OKR software?',
        a: 'OKR software is where objectives and key results are set, linked and tracked: the organisation objectives for the year, the team and individual goals that contribute to them, the measures that show progress, and the check-ins that keep them current. In CLEAR Talent the same goals then feed the performance review, so what people worked towards all year is what they are assessed against.',
      },
      {
        q: 'How is OKR software different from tracking goals in a spreadsheet?',
        a: "A spreadsheet records goals but cannot enforce anything about them. CLEAR Talent will not accept an individual goal without a parent objective, stops a person's goal weights for a period from totalling more than 100%, flags goals that are falling behind and rolls scores into the review automatically — none of which depends on someone remembering to update a column.",
      },
      {
        q: 'Do you support quarterly OKRs and annual goals?',
        a: "Yes. Cadences can be mixed per department or level, so teams can run quarterly OKRs while organisation objectives and the compensation cycle stay annual. Each quarter's goal scores roll up into the annual review automatically.",
      },
      {
        q: 'Can employees write their own goals?',
        a: 'Yes. Employees can draft goals against a parent objective and submit them for manager approval. The manager can approve a goal, reject it with comments or send it back with requested changes, and the link to a parent objective is required whoever writes the goal.',
      },
      {
        q: 'How does goal weighting work?',
        a: "Goal weighting is configurable per level — equal, manager-weighted or custom percentages — and a person's goal weights for a period cannot total more than 100%. Separately, the balance between goals and KPIs and competency assessment in the overall rating is also configurable per level.",
      },
      {
        q: 'Can I see goal alignment across the whole company?',
        a: 'Yes. A visual cascade map shows every goal and the objective it rolls up to, and dashboards report completion by department, team, manager and individual, flag at-risk goals and surface objectives that nothing has been linked to yet.',
      },
      {
        q: 'How do we find goals that have drifted away from strategy?',
        a: 'Start in the goal alignment view, which opens on the problems rather than on the full cascade: an overall alignment score, goals with weak or missing alignment signals, cascade gaps where a goal below organisation level has no parent connection, and KPIs in the cycle that are not linked to any goal. Active goals without an owner are flagged separately, because their direct KPI inputs stay out of score roll-ups until someone is assigned.',
      },
      {
        q: 'How is progress on a parent objective calculated?',
        a: 'Each goal has a progress roll-up setting that controls how its progress is calculated from its child goals, linked KPIs and key results, with an average as the default. Decide it when the goal is created: a parent that rolls up differently from what its owners expect is a quick way to lose trust in a goal dashboard.',
      },
      {
        q: 'What happens when a new priority is added in the middle of a quarter?',
        a: "It goes into the current cycle like any other goal, and teams and individuals link their own goals to it. A person's goal weights for a period cannot total more than 100%, so the new goal has to take its share from an existing one rather than landing on top, and goal status changes are written to the audit log so the change is still visible when the quarter is reviewed.",
      },
      {
        q: 'Can OKRs feed into performance reviews?',
        a: 'Yes. Goal scores roll up into the performance review automatically, where they sit alongside competency ratings and 360° feedback in a single composite rating. You configure how much weight each stream carries.',
      },
      {
        q: 'Is CLEAR Talent a standalone OKR tool?',
        a: 'No. It manages OKRs, goals and KPIs as part of a performance management platform, so the same goals connect to reviews, 360° feedback, competency assessment, development plans and succession planning. If you only need to track objectives and never assess anyone against them, a dedicated OKR tool may be all you need.',
      },
    ],
    internalLinks: [
      { label: 'Performance Management Software', href: '/solutions/performance-management-software' },
      { label: 'Continuous Performance Management', href: '/solutions/continuous-performance-management' },
      { label: 'Employee Performance Review Software', href: '/solutions/employee-performance-review-software' },
      { label: 'Competency Management Software', href: '/solutions/competency-management-software' },
      { label: 'Guide: cascading company goals', href: '/blog/how-to-cascade-company-goals' },
      { label: 'Guide: 45 SMART goals examples by role', href: '/blog/smart-goals-examples-for-employees' },
      {
        label: 'Walkthrough: quarterly OKRs alongside an engineering ladder',
        href: '/in-practice/engineering-performance-management-rollout',
      },
      { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
      { label: 'Best OKR software for HR teams, compared', href: '/best-okr-software' },
      { label: 'Glossary: OKR vs KPI', href: '/glossary/okr-vs-kpi' },
      { label: 'Product showcase', href: '/showcase' },
      { label: 'Guide: what is performance management?', href: '/blog/what-is-performance-management' },
    ],
    // Each entry is something the sections and FAQs above describe, so the
    // SoftwareApplication node says what the software does rather than listing
    // the keyword variants a buyer might type to reach the page.
    schemaFeatures: [
      'Goal cascading from organisation objectives to department, team and individual goals',
      'Enforced parent-goal linkage through Strategic Linked Goals',
      'Goal alignment view showing cascade gaps, unlinked KPIs and goals without an owner',
      'Configurable progress roll-up from child goals, linked KPIs and key results',
      'OKRs, SMART goals and weighted KPIs on one platform',
      'Central KPI library with reusable, role-aware templates, performance bands and guardrails',
      'Configurable goal weighting per level',
      'Manager approval workflow for employee-drafted goals',
      'Goal completion dashboards, at-risk flags and end-of-cycle score projections',
      'Quarterly and annual goal cycles that roll up into performance reviews',
      'AI-drafted goals, KPIs and weightings with Ava',
    ],
  },

  // ─────────────────────────────────────────────
  // 4. Employee Performance Review Software
  // ─────────────────────────────────────────────
  {
    slug: 'employee-performance-review-software',
    category: 'solutions',
    primaryKeyword: 'Employee Performance Review Software',
    secondaryKeywords: [
      'performance appraisal software',
      'annual review software',
      'performance evaluation software',
      'review management system',
      'performance review tool',
      'employee appraisal system',
    ],
    seoTitle: 'Performance Review & Appraisal Software | CLEAR Talent',
    metaDescription:
      'Run performance reviews and appraisals from self-review through calibration to one defensible rating built on goals, 360° input and competencies. See a demo.',
    eyebrow: 'Performance Reviews & Appraisals',
    h1: 'Employee Performance Review & Appraisal Software',
    heroIntro:
      'Whether your organisation calls it a performance review or a performance appraisal, it should be the most important conversation of the year — not a forms-filling exercise. CLEAR Talent automates the admin (reminders, roll-ups, reports) so managers spend their time coaching, not chasing, and every rating is fair, fast and defensible.',
    heroImage: '/screenshots/13-reviews-main.webp',
    heroImageAlt:
      'CLEAR Talent Review cycles screen listing active, completed and draft cycles with their dates, self-assessment deadlines, participant counts and completion progress',
    heroStats: [
      { value: 'Automated', label: 'Reminders & roll-ups' },
      { value: 'Traceable', label: 'Review history & approvals' },
      { value: '1-click', label: 'PDF & Excel exports' },
    ],
    primaryCta: { text: 'Book a review-cycle demo', href: '/demo?source=reviews' },
    secondaryCta: { text: 'See a sample review report', href: '/contact?reason=sample-review&source=reviews' },
    sections: [
      {
        id: 'reimagined',
        eyebrow: 'Reimagined cycle',
        heading: 'The review cycle, reimagined',
        paragraphs: [
          'Most review tools are glorified form builders. CLEAR Talent orchestrates the entire cycle: self-review → 360° feedback → manager review → calibration → final rating → signed-off report.',
          'Automated reminders. Configurable workflows. Zero chase emails.',
        ],
      },
      {
        id: 'three-streams',
        eyebrow: 'One review',
        heading: 'Three data streams, one review',
        bullets: [
          { title: 'Goal achievement', description: 'Scores roll up from SLG automatically; no re-entry.' },
          { title: 'Competency ratings', description: 'Evidence-based CAF ratings, with optional behavioural evidence required.' },
          {
            title: '360° feedback',
            description: 'Structured input from peers, reports and stakeholders, weighted by your policy.',
          },
        ],
        paragraphs: ['All three combine into a single, defensible composite rating.'],
        cta: {
          text: 'Turn competency ratings into development plans',
          href: '/solutions/employee-development-software',
        },
        variant: 'tinted',
      },
      {
        id: 'calibration',
        eyebrow: 'Bias-killing tools',
        heading: 'Calibration tools that kill rating bias',
        paragraphs: [
          'Calibration meetings use live heatmaps, forced-distribution simulations, and outlier flags to surface inconsistent ratings across managers before they go final.',
          'Your HRBPs get the evidence they need to challenge a rating — or defend it.',
        ],
        // The calibration page owns "performance calibration software"; this
        // section stays a summary of the stage and hands the detail over.
        cta: { text: 'See performance calibration software', href: '/solutions/performance-calibration-software' },
      },
      {
        id: 'reports',
        eyebrow: 'Outputs',
        heading: 'Auto-generated review reports — PDF and Excel',
        paragraphs: [
          'One click produces a board-ready PDF for each employee or a roll-up Excel export for HR analytics. Formatting is consistent, branded, and audit-ready.',
        ],
      },
      {
        id: 'audit-trail',
        eyebrow: 'Defensibility',
        heading: 'Audit trail for every rating, every comment',
        paragraphs: [
          'Every change — who, what, when — is logged immutably. Defensible under internal audit, works-council review, or legal discovery.',
        ],
        variant: 'dark',
      },
    ],
    faqs: [
      {
        q: 'Is performance review software the same as performance appraisal software?',
        a: 'Yes. Many UK, Irish and European organisations call the formal review an appraisal, while others say performance review or evaluation; the cycle underneath is the same. CLEAR Talent runs it end to end — self-review, 360° feedback, manager review, calibration, final rating and a signed-off report — whichever name your organisation uses in its policy and its review forms.',
      },
      {
        q: 'Can we run mid-year and end-of-year reviews?',
        a: 'Yes. You can run as many cycles as you need — mid-year, year-end or quarterly — each with its own configurable template. Every stage, from goal setting and self-assessment to manager review, calibration and final rating, opens and closes on the dates you set, and automated reminders keep each stage moving, so HR is not chasing forms by email.',
      },
      {
        q: 'How is the overall performance rating calculated?',
        a: "It combines three streams: goal achievement rolled up from the goals and KPIs set during the year, evidence-based competency ratings, and structured 360° feedback. You configure how much each stream counts, and the balance between goals and competencies can differ by level, so a senior leader's rating can lean more on goals and a new starter's more on competencies. The result is one composite rating with its evidence attached.",
      },
      {
        q: 'How does calibration work, and is forced distribution required?',
        a: 'Before ratings go final, calibration sessions show live heatmaps of ratings across managers and teams, flag outliers and let you simulate a forced distribution to see where ratings cluster. That surfaces a manager who rates everyone highly, or a team rated far below its peers, while there is still time to discuss it. Forced distribution is an optional guardrail and never mandatory.',
      },
      {
        q: 'Can employees see their full review?',
        a: 'Yes. Once the manager releases the review, the employee sees all of their competency ratings and every narrative section, so the person it describes can see what the rating rests on. Every change made during the cycle — who changed what, and when — is recorded in the audit log, and the finished review can be exported as a PDF.',
      },
      {
        q: 'What will a works council want to know about the review process?',
        a: 'In some EU countries a performance evaluation system can require works council consultation or consent before it goes live, so take advice from your employment counsel. CLEAR Talent makes the answers concrete: role-based access with managers working within their own reporting line, anonymity thresholds on 360° feedback, stages that close on fixed dates, and an audit log of every rating change.',
      },
    ],
    internalLinks: [
      { label: 'Performance Management Software', href: '/solutions/performance-management-software' },
      { label: '360° Feedback Software', href: '/solutions/360-degree-feedback-software' },
      { label: 'Competency Management Software', href: '/solutions/competency-management-software' },
      { label: 'OKR & Goal Setting Software', href: '/solutions/okr-goal-setting-software' },
      { label: 'Employee Development Software', href: '/solutions/employee-development-software' },
      { label: 'Guide: running a calibration session', href: '/blog/performance-calibration-session-guide' },
      {
        label: 'Guide: performance appraisal vs performance review vs performance management',
        href: '/blog/performance-appraisal-vs-performance-review',
      },
      { label: 'Glossary: what is a calibration session?', href: '/glossary/calibration-session' },
      { label: 'Glossary: what is a performance appraisal?', href: '/glossary/performance-appraisal' },
      {
        label: 'Templates: performance review forms for every stage of the cycle',
        href: '/resources/templates',
      },
      { label: 'Template: self-assessment form', href: '/resources/templates/self-assessment-form-template' },
      { label: 'Template: 360 feedback form', href: '/resources/templates/360-feedback-form-template' },
      { label: 'Template: manager review form', href: '/resources/templates/manager-review-form-template' },
      {
        label: 'Template: annual performance review form',
        href: '/resources/templates/annual-performance-review-template',
      },
    ],
    // Re-dated 2026-10-11 for the rewritten FAQs, 2026-10-16 for the links to
    // the performance calibration page, and 2026-10-17 for the links to the
    // performance review form templates.
    dateModified: '2026-10-17',
  },

  // ─────────────────────────────────────────────
  // 4a. Performance Calibration Software
  // ─────────────────────────────────────────────
  // Owns "performance calibration software", the commercial counterpart to the
  // calibration-session, 9-box and bias guides, which stay informational. The
  // review page keeps calibration as one stage of its cycle and links here for
  // the detail. Every capability restates something the site already says: the
  // stage order, heatmaps, outlier flags and optional forced distribution from
  // the review page and the calibration-session glossary term; Ava's bias
  // prompts from the AI page; manager trends and completion from the competency
  // report update; the 9-box grid from the succession page and guide, on the
  // Enterprise plan as /pricing lists it; the audit log, GDPR tooling, works
  // councils, the EU AI Act and pay transparency from the Europe page and the
  // bias guide. It claims no field for the reason behind a rating change, because
  // the repository describes none — only the audit log's before-and-after values.
  {
    slug: 'performance-calibration-software',
    category: 'solutions',
    primaryKeyword: 'Performance Calibration Software',
    secondaryKeywords: [
      'calibration meeting software',
      'rating calibration tool',
      'talent calibration software',
      'performance rating calibration',
    ],
    // The head term plus what calibration is for, rather than the feature name
    // alone: feature-name-only titles are the ones that underperform on this site.
    seoTitle: 'Calibration Software for Consistent Ratings | CLEAR Talent',
    metaDescription:
      'Performance calibration software with rating heatmaps, outlier and bias flags, manager comparisons and an audit trail on every rating change. Book a demo.',
    eyebrow: 'Performance Calibration',
    h1: 'Performance Calibration Software for Fair, Consistent Ratings',
    heroIntro:
      'Two managers can read the same rating scale and picture very different levels of performance. Calibration is where an organisation catches that before ratings reach pay, promotion or an employee’s record. CLEAR Talent runs it as a stage of the review cycle, from the evidence already in the platform — with bias prompts before ratings are submitted, heatmaps and outlier flags across managers, and an audit trail of every change agreed in the room.',
    heroImage: '/screenshots/13-reviews-main.webp',
    heroImageAlt:
      'CLEAR Talent Review cycles screen with an active mid-year appraisal, a completed quarterly review and a draft cycle, showing dates, participants, completion and cycle actions such as Remind and Sync managers',
    heroStats: [
      { value: 'Heatmaps', label: 'Ratings across managers' },
      { value: 'Optional', label: 'Forced-distribution guardrail' },
      { value: 'Logged', label: 'Every rating change' },
    ],
    primaryCta: { text: 'Book a calibration demo', href: '/demo?source=calibration' },
    secondaryCta: { text: 'See plans and pricing', href: '/pricing' },
    sections: [
      {
        id: 'problem',
        eyebrow: 'The problem',
        heading: 'Why the same performance earns different ratings',
        paragraphs: [
          'A rating scale describes a standard; managers apply it. One reserves the top band for an ideal nobody meets, another rates the whole team above expectations to protect them, and a third puts nearly everyone in the middle because there is no evidence to defend anything else. Each rating looks reasonable on its own. Place the teams side by side and comparable work has earned three different scores.',
          'That difference does not stay in the review. It flows into pay, promotion and succession decisions, and into a record that internal audit, a works council or a later dispute may ask you to explain.',
          'Software will not decide what “exceeds expectations” should mean in your organisation; that is a leadership decision. What performance calibration software removes are the practical reasons a session runs badly: evidence that arrives too late to read, distributions nobody has seen and changes nobody wrote down.',
        ],
      },
      {
        id: 'what-it-does',
        eyebrow: 'What it does',
        heading: 'Calibration as a stage of the review, not a separate exercise',
        intro:
          'In CLEAR Talent, calibration sits in the review cycle after manager review and before the final rating, so it happens while the decision is still live rather than after ratings have been communicated.',
        bullets: [
          {
            title: 'One cycle, fixed dates',
            description:
              'Self-review, 360° feedback, manager review, calibration, final rating and a signed-off report run as one cycle. Each stage opens and closes on the dates you set, and automated reminders keep it moving.',
          },
          {
            title: 'The evidence travels with the rating',
            description:
              'Goal scores roll up from Strategic Linked Goals, competency ratings carry the behavioural evidence behind them and structured 360° input sits alongside, so the room reads the same data the rating was made from.',
          },
          {
            title: 'Inconsistency surfaced before the meeting',
            description:
              'Live heatmaps, outlier flags and forced-distribution simulations show where ratings are inconsistent across managers and teams before anything is final.',
          },
          {
            title: 'A record that outlasts the session',
            description:
              'Every change is logged with who, what and when, and one-click PDF and Excel exports produce both the pack for the meeting and the record afterwards.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'distribution-views',
        eyebrow: 'Rating distributions',
        heading: 'Read the shape of the ratings, not the average',
        intro:
          'Two teams can share the same average rating while one is solidly competent and the other is split between strong and struggling performers. Calibration starts with how ratings are spread, by manager and by team.',
        bullets: [
          {
            title: 'Heatmaps across managers and teams',
            description:
              'Ratings by manager and team sit side by side, so a manager who rates everyone highly, or a team rated far below its peers, stands out while there is still time to discuss it.',
          },
          {
            title: 'Forced distribution as a check, not a quota',
            description:
              'Simulate a forced distribution to see where ratings cluster. It is an optional guardrail and never mandatory, because a curve enforced as a quota turns calibration into an allocation exercise.',
          },
          {
            title: 'Competency distribution with trends',
            description:
              'The team report shows how members score across each competency, with trend analysis, so a cluster at the top of the scale for a capability that is new to most of the team gets questioned.',
          },
          {
            title: 'Completion before conclusions',
            description:
              'Assessment coverage sits beside the results, and the competency assessment report shows completion alongside distribution and manager trends, so a report run mid-cycle is not mistaken for the whole population.',
          },
        ],
      },
      {
        id: 'team-report',
        eyebrow: 'Inside the product',
        heading: 'Where the calibration pack starts',
        intro:
          'Before the session, HR checks who has been assessed and how their results are spread. The Team tab of Reports & Analytics answers both for one team and one period, so the picture can be circulated before anyone has to defend a rating.',
        figure: {
          src: '/screenshots/07-reports-tab-team.webp',
          alt: 'CLEAR Talent Reports & Analytics, Team tab, with report filters for team, start date and end date, an option to include in-progress assessments, and cards for team statistics, competency distribution and actionable insights',
          width: 1920,
          height: 1283,
          caption:
            'The Team tab of Reports & Analytics in CLEAR Talent: choose a team and a period, then read assessment coverage and the competency distribution before the session.',
          annotations: [
            {
              title: 'Team, Start Date and End Date',
              description:
                'Pick the team and the period, so the report covers the same people and the same window as the cycle being calibrated.',
            },
            {
              title: 'Include in-progress assessments',
              description:
                'Leave it clear to report on finished assessments only, or tick it to see a cycle part-way through — useful for chasing completion, not for drawing conclusions.',
            },
            {
              title: 'Team Statistics',
              description:
                'Aggregate performance metrics, assessment coverage and high-performer counts: the first check of who has actually been assessed before any result is quoted.',
            },
            {
              title: 'Competency Distribution',
              description:
                'How team members score across each competency, with trend analysis — the shape of the ratings rather than an average that hides it.',
            },
            {
              title: 'Actionable Insights',
              description:
                'Recommendations for team development based on performance patterns, so a gap the session agrees on leads somewhere after calibration.',
            },
          ],
        },
        variant: 'tinted',
      },
      {
        id: 'bias-flags',
        eyebrow: 'Bias flags',
        heading: 'Recency and leniency flagged before ratings are submitted',
        paragraphs: [
          'Most rating bias is an evidence problem. The last eight weeks are vivid and the first eight months are not, so a strong final quarter can turn an uneven year into “exceeds expectations”. Some managers rate high to protect their team; others rate low to look rigorous.',
          'Before a manager submits, Ava, the AI assistant built into CLEAR Talent, looks for rating patterns that may indicate gender, recency or leniency bias and prompts the manager to look again. It is a prompt, not a verdict: the manager decides whether the pattern reflects bias and whether to change anything, and calibration remains the place where ratings are compared across managers. Every Ava output is labelled and editable, and Ava can be switched off per cycle, team or module.',
          'The best counter to recency is a record kept through the year. Managers log 1-on-1s, development notes and performance-journal observations tagged to competencies as they happen, so the review opens with the period’s evidence already there rather than six weeks of memory.',
        ],
        cta: {
          text: 'Guide: how to reduce bias in performance reviews',
          href: '/blog/reduce-bias-in-performance-reviews',
        },
      },
      {
        id: 'manager-comparison',
        eyebrow: 'Manager comparison',
        heading: 'A manager-comparison workflow that ends in a recorded decision',
        intro:
          'A manager whose ratings run above their peers may lead a stronger team, or may be applying a different standard; the evidence decides which. An illustrative sequence for working through it in the session:',
        steps: [
          {
            title: 'Compare like with like',
            description:
              'Set a manager’s ratings against peers rating the same roles against the same competencies. The competency assessment report shows manager trends alongside completion and distribution, so leniency, severity and central tendency show up as patterns to discuss.',
          },
          {
            title: 'Start with the outliers',
            description:
              'Work the highest ratings, the lowest and the cases the outlier flags mark, rather than every name in alphabetical order. That is where inconsistency lives, and those are the discussions that deserve the time.',
          },
          {
            title: 'Ask for evidence, not adjectives',
            description:
              'Goal scores, competency ratings with their evidence and anonymised 360° input are already in the platform. The composite rating keeps each part visible, so the room can question a goal score without disturbing the competency ratings beside it.',
          },
          {
            title: 'Record what changed',
            description:
              'Every rating change is written to the audit log against the user who made it, with the before-and-after values. Agree the reason before moving on, so the manager can explain the outcome to the employee in their own words.',
          },
        ],
        cta: {
          text: 'Guide: how to run a performance calibration session',
          href: '/blog/performance-calibration-session-guide',
        },
        variant: 'tinted',
      },
      {
        id: 'nine-box',
        eyebrow: 'Talent reviews',
        heading: 'From calibrated ratings to the 9-box grid',
        paragraphs: [
          'A talent review should start from performance that has already been calibrated. Re-rating performance around a 9-box grid quietly undoes the session you already ran and introduces a second, undocumented standard.',
          `In CLEAR Talent the 9-box grid draws on the same competency assessments, 360° feedback and performance scores the review cycle produces. Its axes are configurable, it filters live by role family, region or diversity dimension, and every drag-and-drop placement is written to the audit log. It is part of the ${SUCCESSION_MODULE} module on the Enterprise plan.`,
        ],
        cta: { text: 'Guide: the 9-box grid and talent reviews', href: '/blog/nine-box-grid-talent-review' },
      },
      {
        id: 'audit-trail',
        eyebrow: 'Audit trail',
        heading: 'An audit trail behind every calibrated rating',
        paragraphs: [
          'A rating that moved in calibration is the one most likely to be questioned later. The audit log records rating changes, approvals, sign-ins and exports against the user responsible, with the before-and-after values on every change, so you can show what moved, who moved it and when.',
          'Each stage closes on the date you set, so once the cycle ends a calibrated rating cannot be quietly amended. One-click PDF and Excel exports give HR the session pack beforehand and the record afterwards, and a roll-up Excel export supports your own analysis of which ratings moved, in which direction and for whom.',
        ],
        variant: 'dark',
      },
      {
        id: 'eu-governance',
        eyebrow: 'EU-friendly governance',
        heading: 'Calibration that holds up to GDPR, works councils and pay transparency',
        intro:
          'In Ireland and across the EU, a calibrated rating is personal data about an identifiable employee, and where it feeds pay it increasingly has to be explained. These are the controls that make those conversations concrete.',
        paragraphs: [
          'CLEAR Talent runs on Google Cloud Platform. If employee data has to stay in a particular region, such as the EU, raise it at the start: regional requirements are agreed for each deployment and confirmed alongside the DPA.',
        ],
        bullets: [
          {
            title: 'GDPR tooling for performance data',
            description:
              'Data subject access request exports, right-to-be-forgotten workflows and a data processing agreement, so a request about a calibrated rating is answered from the system rather than from meeting notes and inboxes.',
          },
          {
            title: 'Role-based access',
            description:
              'Access is role-based across administrator, manager and employee roles, with ownership checks on individual records, and managers work within their own reporting line.',
          },
          {
            title: 'Concrete answers for a works council',
            description:
              'In some EU countries, Germany and the Netherlands among them, a performance evaluation system can require works council consultation or consent. Fixed stage dates, role-based access and an audit log of every rating change give representatives specific answers; take advice from your employment counsel.',
          },
          {
            title: 'Pay transparency and the EU AI Act',
            description:
              'The EU Pay Transparency Directive expects pay progression to rest on objective, gender-neutral criteria, and the EU AI Act treats AI used to evaluate workers’ performance as high-risk. Ratings carry their evidence, Ava’s prompts are labelled and editable, and people make every decision.',
          },
        ],
        cta: { text: 'Read the trust centre: GDPR, the DPA and AI governance', href: '/trust' },
        variant: 'tinted',
      },
      {
        id: 'pricing',
        eyebrow: 'Pricing',
        heading: 'A published price list, before you talk to sales',
        paragraphs: [
          `Performance review cycles, where the calibration stage sits, are included on every plan. The ${BASIC_TIER.name} plan is USD ${BASIC_TIER.monthlyUsd} per month for ${BASIC_TIER.includedAdmins} admin user and ${BASIC_TIER.includedStaff} staff users, and the ${PRO_TIER.name} plan is USD ${PRO_TIER.monthlyUsd} per month for ${PRO_TIER.includedAdmins} admin users and ${PRO_TIER.includedStaff} staff users, with published rates for each additional user. Annual billing saves ${ANNUAL_DISCOUNT_PERCENT}%, and there is a ${TRIAL_POLICY.toLowerCase()}.`,
          `Not every capability sits on every plan: the AI capabilities differ between plans, and the ${SUCCESSION_MODULE} module, where the 9-box grid lives, is on the quoted Enterprise plan. Check the plan comparison before you scope a rollout, and raise anything unclear in the demo.`,
        ],
        cta: { text: 'Compare the plans', href: '/pricing' },
      },
    ],
    faqs: [
      {
        q: 'What is performance calibration software?',
        a: 'Performance calibration software helps managers apply one rating standard across teams before ratings are final. It brings each rating together with the evidence behind it, shows how ratings are distributed by manager and team, flags outliers and records what changes. In CLEAR Talent, calibration is a stage of the review cycle, between manager review and the final rating, working from goal scores, competency ratings and 360° feedback already in the platform.',
      },
      {
        q: 'Does calibration in CLEAR Talent mean forced distribution?',
        a: 'No. Calibration aims for a consistent standard, not a fixed share of people in each rating band. CLEAR Talent can simulate a forced distribution so you can see where ratings cluster, but it is an optional guardrail and never mandatory. A team of genuinely strong performers keeps strong ratings when the evidence supports them.',
      },
      {
        q: 'How does CLEAR Talent flag rating bias?',
        a: 'Before a manager submits, Ava looks for rating patterns that may indicate gender, recency or leniency bias and prompts the manager to look again. In the calibration stage, live heatmaps and outlier flags show where ratings are inconsistent across managers and teams before anything is final. Both are prompts, not verdicts: people decide whether a pattern reflects bias and what, if anything, changes.',
      },
      {
        q: 'Can we show later why a rating changed in calibration?',
        a: 'Yes. Every rating change is written to the audit log against the user who made it, with the before-and-after values and the time. Stages close on the dates you set, so a rating cannot be quietly amended after the cycle ends, and PDF and Excel exports keep the session pack and the outcome. Agree the reason in the room so the manager can explain it.',
      },
      {
        q: 'How does CLEAR Talent handle calibration data under GDPR?',
        a: 'CLEAR Talent provides the controls a GDPR programme needs for performance data: subject access request exports, right-to-be-forgotten workflows, role-based access, audit logs and a data processing agreement. Hosting is on Google Cloud Platform, with any regional requirement agreed for your deployment. Compliance also depends on how you configure and use the platform, so your DPO should review the DPA and the settings.',
      },
    ],
    internalLinks: [
      { label: 'Guide: how to run a performance calibration session', href: '/blog/performance-calibration-session-guide' },
      { label: 'Guide: the 9-box grid and talent reviews', href: '/blog/nine-box-grid-talent-review' },
      { label: 'Guide: how to reduce bias in performance reviews', href: '/blog/reduce-bias-in-performance-reviews' },
      {
        label: 'Guide: manager rating trends in competency reports',
        href: '/blog/manager-rating-trends-competency-reports',
      },
      { label: 'Guide: performance rating scales explained', href: '/blog/performance-rating-scales' },
      { label: 'Glossary: what is a calibration session?', href: '/glossary/calibration-session' },
      { label: 'Glossary: what is a 9-box grid?', href: '/glossary/nine-box-grid' },
      {
        label: 'Guide: the EU Pay Transparency Directive and performance reviews',
        href: '/blog/pay-transparency-directive-performance-reviews',
      },
      { label: 'Trust and security: GDPR, the DPA, hosting and AI governance', href: '/trust' },
      { label: 'Plans and pricing', href: '/pricing' },
    ],
    // Every entry is something the sections and FAQs above describe, so the
    // SoftwareApplication node says what the software does rather than listing
    // the keyword variants a buyer might type to reach the page.
    schemaFeatures: [
      'Calibration stage in the review cycle, between manager review and the final rating',
      'Live heatmaps of ratings across managers and teams',
      'Outlier flags on inconsistent ratings before they are final',
      'Optional forced-distribution simulation',
      'Competency distribution, completion and manager trends in assessment reports',
      'AI prompts on rating patterns that may indicate gender, recency or leniency bias',
      'Composite ratings that keep goal, competency and 360° parts visible',
      'Audit log of rating changes with before-and-after values',
      'PDF and Excel exports for calibration packs and records',
      'Subject access request exports, erasure workflows and a data processing agreement',
    ],
    dateModified: '2026-10-16',
  },

  // ─────────────────────────────────────────────
  // 5. Competency Management Software
  // ─────────────────────────────────────────────
  {
    slug: 'competency-management-software',
    category: 'solutions',
    primaryKeyword: 'Competency Management Software',
    // "skills management software" and "skills matrix software" belong to
    // /solutions/skills-management-software, which this page links to; listing
    // them here as well would set the two pages up as a cannibalisation pair.
    secondaryKeywords: [
      'competency framework software',
      'capability management tool',
      'competency assessment tool',
      'skills gap analysis software',
    ],
    seoTitle: 'Competency Management Software | CLEAR Talent',
    /**
     * This page earns one of the highest impression counts on the site and
     * almost no clicks, so the snippet is what fails, not the ranking. The old
     * description leaned on "CAF" and "role-based capability at scale" — an
     * unexplained acronym and a phrase that says nothing a buyer can picture.
     * This one spends the same space on what the software actually gives them:
     * the CAF in plain words (behavioural anchors, proficiency levels,
     * evidence-based ratings) plus the gap dashboards and a CTA.
     */
    metaDescription:
      'Competency management software with a reusable library: behavioural anchors, proficiency levels, evidence-based ratings and skills-gap dashboards. Book a demo.',
    eyebrow: 'Competency Management',
    h1: 'Competency Management Software for Skills-First Organisations',
    heroIntro:
      "The companies that win the next decade are the ones that know exactly what their people can do — and where the gaps are. CLEAR Talent's Competency Assessment Framework (CAF) replaces vague job descriptions with precise, assessable capabilities.",
    heroImage: '/screenshots/06-competencies-main.webp',
    heroImageAlt:
      'CLEAR Talent Competency Library showing core, functional and leadership competencies as cards, each with a definition, a category and its assessment questions, with tabs for behavioural indicators and the framework wheel',
    heroStats: [
      { value: '1', label: 'Library, infinite reuse' },
      { value: 'Role-specific', label: 'Scales by job family' },
      { value: 'Evidence-based', label: 'Defensible ratings' },
    ],
    primaryCta: { text: 'Book a CAF workshop demo', href: '/demo?source=competency' },
    secondaryCta: {
      text: 'Download a sample competency library',
      href: '/contact?reason=caf-library&source=competency',
    },
    sections: [
      {
        id: 'capabilities',
        eyebrow: 'Skills-first thinking',
        heading: 'Move from job titles to capabilities',
        paragraphs: [
          'Traditional HR thinks in titles. Modern talent organisations think in capabilities.',
          'CLEAR Talent lets you define every competency once — with behavioural anchors, proficiency levels, and evidence requirements — then apply it across roles, reviews, 360°, and development plans.',
        ],
      },
      {
        id: 'caf',
        eyebrow: 'The framework',
        heading: 'The Competency Assessment Framework (CAF)',
        bullets: [
          {
            title: 'Build once, reuse everywhere',
            description: 'A central competency library flows into every review, 360°, and IDP.',
          },
          {
            title: 'Behavioural anchors, not vague adjectives',
            description: 'Each level is described with observable behaviours, not "good" and "excellent".',
          },
          {
            title: 'Role-specific scales',
            description: 'Different scales for technical, leadership, and individual-contributor roles.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'evidence',
        eyebrow: 'Evidence-based',
        heading: 'Assess capabilities with evidence',
        paragraphs: [
          'Every rating requires (or optionally requires) evidence — a project, a deliverable, a customer win. Ratings become defensible, coachable, and longitudinal.',
        ],
        cta: {
          text: 'Generate behaviour-based review and 360 questions free',
          href: '/tools/performance-review-generator',
        },
      },
      {
        id: 'gaps',
        eyebrow: 'Gap analysis',
        heading: 'Identify and close skills gaps',
        paragraphs: [
          'Organisation-wide dashboards show capability coverage by team, department and competency. Gaps get flagged; training gets targeted; promotions become objective.',
        ],
        cta: {
          text: 'Get the free skills gap analysis template and worked example',
          href: '/resources/skills-gap-analysis-template',
        },
      },
      {
        id: 'idp-link',
        eyebrow: 'Development link',
        heading: 'Link competencies to development plans',
        paragraphs: [
          'Every CAF assessment automatically drafts an IDP: the three competencies most important for the next role, ranked by current gap. Development becomes a forward action, not a file.',
        ],
        cta: { text: 'See development planning', href: '/solutions/employee-development-software' },
      },
    ],
    faqs: [
      {
        q: 'What is competency management software?',
        a: 'Competency management software defines the capabilities each role needs, assesses people against them and shows where the gaps are. In CLEAR Talent that means one competency library with behavioural anchors and proficiency levels, assessments backed by evidence, gap dashboards by team and department, and development plans drafted from the results — so the framework is used in every review rather than filed in a document.',
      },
      {
        q: 'Do you provide a pre-built competency library?',
        a: "Yes. CLEAR Talent comes with industry-standard starter libraries that you can edit, extend or replace, so you are not starting from a blank page. A common approach is to keep core and leadership competencies close to the starter set and write functional ones in your organisation's own language. For a reference point, the free core competencies list gives definitions and behavioural indicators at four levels.",
      },
      {
        q: 'How many proficiency levels can a competency scale have?',
        a: 'Three to seven levels per scale, configured by you. Each level is described through observable behaviour rather than labels such as "good" or "excellent", so two managers reading the same evidence have the same description to rate against. You can run different scales for technical, leadership and individual-contributor roles, and each role sets the target level it needs for every competency.',
      },
      {
        q: 'How does a competency assessment work in CLEAR Talent?',
        a: "Each person is rated against the competencies their role requires, on that role's scale. A rating can require evidence — a project, a deliverable or an observed behaviour — so it can be explained later and compared with the next assessment. The same competencies run through reviews, 360° feedback and development plans, which means a rating given in one place is read the same way everywhere else.",
      },
      {
        q: 'Can we measure skills-gap progress over time?',
        a: 'Yes. Organisation-wide dashboards show capability coverage by team, department and competency, and longitudinal charts track each competency for an individual or a team across assessments. Because every assessment re-scores the same competencies on the same scale, you can see whether a gap actually closed after training or a stretch assignment, rather than relying on course completions as a proxy.',
      },
      {
        q: 'How do competency gaps connect to learning and development?',
        a: "Every assessment drafts an individual development plan around the three competencies most important for the person's next role, ranked by the size of the current gap. Development goals are then tracked at check-ins, and learning impact dashboards show whether that activity is closing the gaps. CLEAR Talent is not a learning management system, so courses stay in the tools you already use.",
      },
    ],
    internalLinks: [
      { label: 'Employee Development Software', href: '/solutions/employee-development-software' },
      { label: 'Succession Planning Software', href: '/solutions/succession-planning-software' },
      {
        label: 'Skills Management Software: skills matrix, coverage and gaps across the workforce',
        href: '/solutions/skills-management-software',
      },
      { label: '360° Feedback Software', href: '/solutions/360-degree-feedback-software' },
      { label: 'Performance Management Software', href: '/solutions/performance-management-software' },
      {
        label: 'Talent Management Software: how competencies feed reviews, development and succession',
        href: '/solutions/talent-management-software',
      },
      {
        label: 'Guide: building a competency framework, with a matrix template',
        href: '/blog/competency-framework-guide',
      },
      {
        label: 'Guide: leadership competencies for managers, a 12-competency model',
        href: '/blog/leadership-competencies-for-managers',
      },
      {
        label: 'Guide: nurse and clinical competency assessment',
        href: '/blog/nurse-clinical-competency-assessment',
      },
      { label: 'Glossary: what is a skills matrix?', href: '/glossary/skills-matrix' },
      { label: 'Template: how to conduct a skills gap analysis', href: '/resources/skills-gap-analysis-template' },
      {
        label: 'Reference: core competencies list with behavioural examples',
        href: '/resources/core-competencies-list',
      },
      {
        label: 'Examples: performance reviews by job role at three rating levels',
        href: '/resources/performance-review-examples',
      },
      { label: 'Sales performance review examples', href: '/resources/sales-performance-review-examples' },
      {
        label: 'Software engineer performance review examples',
        href: '/resources/software-engineer-performance-review-examples',
      },
      {
        label: 'Customer service performance review examples',
        href: '/resources/customer-service-performance-review-examples',
      },
      { label: 'Manager performance review examples', href: '/resources/manager-performance-review-examples' },
      { label: 'HR performance review examples', href: '/resources/hr-performance-review-examples' },
      { label: 'Free tool: performance review and 360 question generator', href: '/tools/performance-review-generator' },
      { label: 'Guide: what is performance management?', href: '/blog/what-is-performance-management' },
    ],
    // Re-dated 2026-10-03 for the links to the free review question generator,
    // 2026-10-07 for the link to the core competencies list, 2026-10-08 for
    // the links to the skills management page, 2026-10-11 for the rewritten
    // FAQs, 2026-10-13 for the links to the leadership competencies guide
    // and the talent management page, and 2026-10-14 for the links to the
    // role-based performance review examples.
    dateModified: '2026-10-14',
    // Every entry is something the sections and FAQs above describe, so the
    // SoftwareApplication node says what the software does instead of listing
    // the six keyword variants a searcher might have typed to reach it.
    schemaFeatures: [
      'Central competency library with behavioural anchors and proficiency levels',
      'Role-specific competency scales for technical, leadership and individual-contributor roles',
      'Evidence-based competency assessment with defensible ratings',
      'Skills-gap analysis by individual, team, department and competency',
      'Longitudinal tracking of competency progress over time',
      'Competency gaps drafted into individual development plans',
    ],
  },

  // ─────────────────────────────────────────────
  // 6. Continuous Performance Management
  // ─────────────────────────────────────────────
  {
    slug: 'continuous-performance-management',
    category: 'solutions',
    primaryKeyword: 'Continuous Performance Management',
    secondaryKeywords: [
      'continuous feedback platform',
      'ongoing performance management',
      'real-time performance management',
      'agile performance management',
      'check-in software',
      'one-on-one meeting software',
    ],
    seoTitle: 'Continuous Performance Management Platform | CLEAR Talent',
    /**
     * The third page in the same indexation-depth gap as the OKR and
     * employee-development pages, and the thinnest of the three: five short
     * sections and four one-line FAQs, most of which any continuous-PM vendor
     * could have written. It now shows how the continuous part actually works in
     * the product — 1-on-1s, development notes and kudos, the performance
     * journal — through a walkthrough and two annotated screens. The old leader
     * section claimed "team sentiment" dashboards; CLEAR Talent has no survey
     * product (see the 15Five head-to-head), so that claim is gone and the page
     * says so plainly instead.
     */
    metaDescription:
      'Continuous performance management with 1-on-1s, goal check-ins, feedback and a performance journal that feed a calibrated year-end rating. Book a demo.',
    dateModified: '2026-09-19',
    eyebrow: 'Continuous PM',
    h1: 'Continuous Performance Management, Done Properly',
    heroIntro:
      'The annual review was designed for the industrial age. Continuous performance management is designed for now — where projects change quarterly, teams reshape monthly, and feedback needs to be in the moment to matter. CLEAR Talent gives you the best of both: always-on coaching with the audit trail and calibration rigour you still need.',
    heroImage: STITCH_IMAGES.featuresTeam,
    heroImageAlt:
      'Illustration of five colleagues around a meeting table with laptops, discussing team performance charts shown on a wall-mounted screen',
    heroStats: [
      { value: 'Mobile-first', label: 'Check-ins on iOS & Android' },
      { value: 'Automated', label: 'Year-end consolidation' },
      { value: 'Always-on', label: 'Feedback flow' },
    ],
    primaryCta: { text: 'Book a continuous PM consultation', href: '/demo?source=continuous' },
    secondaryCta: {
      text: 'Download the Continuous PM playbook',
      href: '/contact?reason=playbook&source=continuous',
    },
    sections: [
      {
        id: 'why-fail',
        eyebrow: 'The problem',
        heading: 'Why annual reviews fail',
        paragraphs: [
          'Annual reviews collapse 12 months of performance into a single conversation, usually remembered through recency bias. Feedback is stale. Goals are obsolete.',
          'And managers avoid hard conversations because they happen once a year under high stakes.',
        ],
      },
      {
        id: 'four-pillars',
        eyebrow: 'The model',
        heading: 'The four pillars of continuous PM',
        bullets: [
          {
            title: 'Real-time goals',
            description: 'Goals update quarterly (or monthly) with progress check-ins baked into one-on-ones.',
          },
          {
            title: 'Weekly/monthly check-ins',
            description: 'Structured templates keep conversations focused on outcomes, blockers and development.',
          },
          {
            title: 'Always-on feedback',
            description:
              'Any employee can give structured feedback to any other at any time, mapped to competencies.',
          },
          {
            title: 'Coaching over rating',
            description: 'Feedback drives behaviour change, not a score.',
          },
        ],
        cta: {
          text: 'See how check-ins feed development plans',
          href: '/solutions/employee-development-software',
        },
        variant: 'tinted',
      },
      {
        id: 'quarter-walkthrough',
        eyebrow: 'Walkthrough',
        heading: 'One quarter between two reviews, step by step',
        intro:
          'Continuous performance management is easy to describe and hard to picture. This illustrative example follows one manager and a small team through a quarter, and shows what is already on record when the formal review opens.',
        steps: [
          {
            title: 'Book the one-on-ones',
            description:
              'The manager schedules 1-on-1s from their own schedule, which tracks upcoming, completed and cancelled sessions for the period and flags anyone on the team who still needs a next one booked.',
          },
          {
            title: 'Prepare from the record, not from memory',
            description:
              'Before each meeting, the team overview gives a per-person snapshot: a combined summary of their sessions so far and suggested next steps, so the conversation picks up where the last one ended.',
          },
          {
            title: 'Capture feedback where it happens',
            description:
              'Coaching agreed in the meeting is recorded as a development note linked to that 1-on-1, and can be kept private. Good work seen elsewhere gets kudos, tagged to the competency it shows, which can be made public.',
          },
          {
            title: 'Log what you notice between meetings',
            description:
              'Observations, achievements and incidents go into the performance journal as they happen, each tagged to the competencies it evidences, while the detail is still accurate.',
          },
          {
            title: 'Keep goals current',
            description:
              'Check-ins against each goal keep progress up to date, and goals falling behind are flagged from pace and check-in signals while the quarter is still running.',
          },
          {
            title: 'Open the review with the evidence already there',
            description:
              'When the formal review opens, competencies are assessed with the journal to hand, and check-ins, feedback and goal scores consolidate into the rating instead of being rebuilt from memory.',
          },
        ],
      },
      {
        id: 'one-on-ones',
        eyebrow: 'Inside the product',
        heading: 'One-on-ones that remember the last one',
        intro:
          "Most one-on-ones are held and then forgotten, which is why so many feel like the first. In CLEAR Talent they are scheduled, summarised and followed up in the same place as the rest of a person's performance record.",
        figure: {
          src: '/images/features/one-on-one.webp',
          alt: 'CLEAR Talent 1-on-1 schedule showing upcoming, completed and cancelled sessions, with a per-person team overview listing a combined summary and suggested next steps',
          width: 1920,
          height: 900,
          caption: "A manager's 1-on-1 schedule in CLEAR Talent, with the team 1-on-1 overview beneath it.",
          annotations: [
            {
              title: 'Sessions this period',
              description:
                'Upcoming, completed and cancelled or no-show sessions, with a running total — so a manager can see whether the rhythm they intended is the one actually happening.',
            },
            {
              title: 'Who needs a next 1-on-1',
              description:
                'The team overview calls out anyone without a next session scheduled. That is how the quiet member of a team stops going a quarter without a conversation.',
            },
            {
              title: 'Search and filter the team',
              description:
                'Search by name, email, role or department, and filter by role, department and activity — useful as soon as a manager looks after more than one team.',
            },
            {
              title: 'Combined summary and suggested next steps',
              description:
                "Each person's card carries a summary of their sessions and the next steps suggested for them, so preparation starts from what was agreed last time rather than from a search through old notes.",
            },
          ],
        },
        variant: 'tinted',
      },
      {
        id: 'between-meetings',
        eyebrow: 'Evidence between meetings',
        heading: 'Feedback and observations that are still there at review time',
        paragraphs: [
          'Continuous feedback covers both coaching and recognition. Development notes carry coaching, including feedback captured from a 1-on-1, and can be kept private. Kudos recognise good work, can be tagged to the competency or value they reflect, and can be made public. Quick templates — kudos, team player, helpful suggestion, above and beyond — give people a starting point instead of a blank box, and received, given, recognition-wall and timeline views keep the history in one place.',
          'Managers also keep a performance journal: a running log of what they observe about each team member, referenced when that person is next assessed. The feedback history and the journal are what make the year-end conversation a review of twelve months rather than of the last fortnight.',
        ],
        figure: {
          src: '/images/features/manager-journal.webp',
          alt: 'CLEAR Talent performance journal listing observation and achievement notes for team members, each tagged to competencies',
          width: 1920,
          height: 900,
          caption: 'The performance journal in CLEAR Talent, where managers log notes to reference during competency assessments.',
          annotations: [
            {
              title: 'Add a note as it happens',
              description:
                'Record an observation, an achievement or an incident against a team member on the day, not reconstructed at the end of the year.',
            },
            {
              title: 'Tagged to competencies',
              description:
                'Each note can carry the competencies it evidences, so it lines up with the framework the person will actually be assessed against.',
            },
            {
              title: 'Filter by person, type or text',
              description:
                'Narrow the journal to one employee or one type of note, or search it, when it is time to prepare an assessment.',
            },
          ],
        },
      },
      {
        id: 'keep-best',
        eyebrow: 'Keep the best',
        heading: 'Keep the best of annual — defensibility and calibration',
        paragraphs: [
          "Continuous doesn't mean unstructured. At year-end, all the check-ins, feedback and goal scores consolidate automatically into a defensible, calibrated annual rating for compensation, promotion, and audit.",
        ],
        variant: 'tinted',
      },
      {
        id: 'leadership-view',
        eyebrow: 'Leadership view',
        heading: 'What leaders see between cycles',
        paragraphs: [
          'Dashboards report goal completion by department, team and manager, flag goals falling behind from pace and check-in signals, and project end-of-cycle scores from current pace — so a drifting quarter is visible while it can still be changed. Ava surfaces attrition and performance risk signals, so leaders know where to look first.',
          'CLEAR Talent does not run engagement or pulse surveys. If measuring sentiment is part of your continuous model, that stays in a dedicated survey tool alongside it.',
        ],
      },
      {
        id: 'manager-first',
        eyebrow: 'Manager-first design',
        heading: 'Built around what managers actually do',
        paragraphs: [
          'Every feature is built around what managers actually do: one-on-ones, check-ins, unblocking. No 90-minute forms. No chase emails.',
        ],
        variant: 'dark',
      },
    ],
    faqs: [
      {
        q: 'Do we have to abandon annual reviews?',
        a: 'No — CLEAR Talent supports fully continuous, fully annual, or hybrid models.',
      },
      {
        q: 'How often should check-ins happen?',
        a: 'Monthly is the sweet spot; weekly for fast-changing teams.',
      },
      {
        q: 'Will managers actually adopt this?',
        a: 'Structured templates, automated reminders, and native iOS/Android apps keep check-ins short and easy to complete — no logins hunted, no forms dreaded.',
      },
      {
        q: 'Is this just "more admin"?',
        a: 'No — continuous PM replaces the annual review scramble. Check-ins, feedback, and goal scores consolidate automatically, so year-end becomes a review, not a rebuild.',
      },
      {
        q: 'Does CLEAR Talent schedule and track one-on-ones?',
        a: 'Yes. Managers schedule 1-on-1s from their own schedule, which tracks upcoming, completed and cancelled sessions for the period. A team overview gives each person a combined summary of their sessions and suggested next steps, and flags anyone who still needs a next 1-on-1 booked.',
      },
      {
        q: 'Can feedback from a one-on-one stay private?',
        a: 'Yes. Feedback captured in a 1-on-1 is recorded as a development note linked to that meeting and can be kept private rather than shared. Kudos for good work can be made public instead. Both can be tagged to the competencies they relate to, which is what lets them count as evidence when the formal review arrives.',
      },
      {
        q: 'Does CLEAR Talent include pulse or engagement surveys?',
        a: 'No. CLEAR Talent has no survey product. Continuous performance management here means one-on-ones, check-ins against goals, structured feedback and a performance journal, all feeding the formal review. If you measure engagement or sentiment, keep that in a dedicated survey tool alongside it.',
      },
    ],
    internalLinks: [
      { label: 'Performance Management Software', href: '/solutions/performance-management-software' },
      { label: 'OKR & Goal Setting Software', href: '/solutions/okr-goal-setting-software' },
      { label: '360° Feedback Software', href: '/solutions/360-degree-feedback-software' },
      { label: 'Employee Performance Review Software', href: '/solutions/employee-performance-review-software' },
      { label: 'Employee Development Software', href: '/solutions/employee-development-software' },
      { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
      { label: 'Glossary: what is continuous feedback?', href: '/glossary/continuous-feedback' },
      { label: 'Guide: what is performance management?', href: '/blog/what-is-performance-management' },
    ],
    // Each entry is something the sections and FAQs above describe, so the
    // SoftwareApplication node says what the software does rather than listing
    // the keyword variants a buyer might type to reach the page.
    schemaFeatures: [
      '1-on-1 scheduling with session tracking, combined summaries and suggested next steps',
      'Development notes captured from 1-on-1s that can be kept private',
      'Kudos and recognition tagged to competencies and values',
      'Performance journal for observations, achievements and incidents, referenced during competency assessments',
      'Goal check-ins with at-risk flags from pace and check-in signals',
      'Check-ins, feedback and goal scores consolidated into a calibrated annual rating',
      'Check-ins and feedback on web, iOS and Android',
    ],
  },

  // ─────────────────────────────────────────────
  // 7. AI Performance Management
  // ─────────────────────────────────────────────
  {
    slug: 'ai-performance-management',
    category: 'solutions',
    primaryKeyword: 'AI Performance Management Software',
    secondaryKeywords: [
      'AI-powered performance reviews',
      'AI HR software',
      'AI employee feedback',
      'performance management AI assistant',
      'AI review drafting',
      'AI coaching tool',
    ],
    seoTitle: 'AI Performance Management Software with Ava | CLEAR Talent',
    metaDescription:
      'AI performance management software with Ava: draft reviews from evidence, summarise 360° feedback and flag potential rating bias. Managers stay in charge.',
    eyebrow: 'Meet Ava',
    h1: 'AI Performance Management That Keeps Humans in Charge',
    heroIntro:
      'Generative AI in HR can be lazy or transformative. Ava is transformative — she reads the evidence, drafts the narrative, summarises 360° feedback, and flags rating bias — but every decision stays with the human. Ava writes the first draft; your managers write the review.',
    heroImage: '/images/ava-avatar.png',
    heroImageAlt: 'Ava, the CLEAR Talent AI assistant',
    heroStats: [
      { value: 'AI-drafted', label: 'Reviews & summaries' },
      { value: 'Human-reviewed', label: 'People approve decisions' },
      { value: 'Per-tenant', label: 'AI isolation' },
    ],
    primaryCta: { text: 'Try Ava in a live demo', href: '/demo?source=ai' },
    secondaryCta: { text: 'See Ava in the platform tour', href: '/showcase' },
    sections: [
      {
        id: 'where-ai-helps',
        eyebrow: 'Where AI belongs',
        heading: "Where AI genuinely helps (and where it shouldn't)",
        paragraphs: [
          "AI shouldn't replace judgement. It should erase the 3 hours a manager spends staring at a blank review form at 11pm.",
          'Ava takes on the drafting, summarising, and pattern-spotting — the work managers already hate — and leaves the coaching conversation firmly human.',
        ],
      },
      {
        id: 'what-ava-does',
        eyebrow: 'Capabilities',
        heading: 'What Ava does',
        bullets: [
          {
            title: 'Drafts reviews from evidence',
            description:
              'Pulls goal scores, competency ratings, 360° feedback and check-in notes into a first-draft review narrative.',
          },
          {
            title: 'Summarises 360° feedback',
            description:
              "Consolidates 8 raters' input into three strengths, three development areas, and a suggested focus.",
          },
          {
            title: 'Surfaces coaching moments',
            description: "Flags when an employee's trajectory diverges from expectation.",
          },
          {
            title: 'Flags potential bias in ratings',
            description:
              'Spots rating patterns that may indicate gender, recency, or leniency bias — and prompts the manager before submission.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'human-loop',
        eyebrow: 'Trust',
        heading: 'Human-in-the-loop, by design',
        paragraphs: [
          'Every Ava output is labelled, editable, and auditable. Managers accept, amend or reject — and every edit is logged.',
        ],
      },
      {
        id: 'data-privacy',
        eyebrow: 'Data privacy',
        heading: 'Your data never trains a shared model',
        paragraphs: [
          "Ava runs on isolated tenants. Your employees' data is never used to train models for other customers. You can turn Ava off for any workflow at any time.",
        ],
        variant: 'dark',
      },
      {
        id: 'time-savings',
        eyebrow: 'The outcome',
        heading: 'Give managers their review time back',
        paragraphs: [
          'The slowest part of any review is the blank page. Ava assembles the goal scores, competency ratings, and feedback that already exist into a structured first draft, giving managers more time for the coaching conversation itself.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Does Ava make the final rating?',
        a: 'No. Ava drafts and summarises; people decide. She writes a first-draft review narrative from the evidence already in the platform, but the manager accepts, amends or rejects the draft, and the rating is theirs. Every Ava output is labelled, editable and auditable, and every edit a manager makes is logged, so the record shows what the manager changed.',
      },
      {
        q: 'What does Ava use to draft a performance review?',
        a: "Ava pulls together what is already recorded for the period: goal scores, competency ratings, 360° feedback and check-in notes. From that she writes a structured first draft, and for 360° feedback she consolidates the raters' input into three strengths, three development areas and a suggested focus. The manager then edits the draft into their own review, or discards it.",
      },
      {
        q: 'How does Ava flag potential rating bias?',
        a: 'Before a manager submits, Ava looks for rating patterns that may indicate gender, recency or leniency bias and prompts the manager to look again. It is a prompt, not a verdict: the manager decides whether the pattern reflects bias and whether to change anything, and calibration remains the place where ratings are compared across managers before they go final.',
      },
      {
        q: 'Is my employee data used to train AI?',
        a: "Your employees' data is never used to train models for other customers. Ava runs on isolated tenants, so one organisation's reviews, ratings and feedback are kept apart from every other's. If your security review needs more detail about how the AI processes data, raise it at the start of the evaluation and confirm the answer in writing alongside the data processing agreement.",
      },
      {
        q: 'Can we switch AI off?',
        a: 'Yes. Ava can be turned off per review cycle, per team or per module, at any time, so you can pilot AI drafting with one group, keep it out of a sensitive process, or run without it entirely. Everything Ava does — drafting, summarising and bias prompts — is assistance on top of a review process that still runs without her.',
      },
      {
        q: 'How does Ava align with the EU AI Act?',
        a: "The EU AI Act treats AI used to evaluate workers' performance as a high-risk use, which puts human oversight, transparency and record-keeping at the centre. Ava was built around those principles: labelled and editable output, human approval of every decision and a full audit log. CLEAR Talent does not certify your compliance; the team will work through specifics with your compliance function.",
      },
    ],
    internalLinks: [
      { label: 'Employee Performance Review Software', href: '/solutions/employee-performance-review-software' },
      { label: '360° Feedback Software', href: '/solutions/360-degree-feedback-software' },
      { label: 'Performance Management Software', href: '/solutions/performance-management-software' },
      { label: 'See all features', href: '/features' },
      { label: 'Guide: what is performance management?', href: '/blog/what-is-performance-management' },
      {
        label: 'Guide: how to use AI to write performance reviews',
        href: '/blog/ai-performance-review-writing',
      },
      {
        label: 'Performance management for Ireland and Europe: GDPR, works councils and the EU AI Act',
        href: '/solutions/performance-management-software-europe',
      },
    ],
    // Dated 2026-10-11 for the rewritten FAQs, and 2026-10-16 for the
    // performance calibration page in its "Related solutions" block.
    dateModified: '2026-10-16',
  },

  // ─────────────────────────────────────────────
  // 8. Succession Planning Software
  // ─────────────────────────────────────────────
  {
    slug: 'succession-planning-software',
    category: 'solutions',
    primaryKeyword: 'Succession Planning Software',
    secondaryKeywords: [
      'succession management tool',
      'leadership pipeline software',
      'talent pipeline software',
      '9-box grid tool',
      'high-potential identification software',
      'succession planning system',
    ],
    seoTitle: 'Succession Planning Software | CLEAR Talent',
    metaDescription:
      'Identify, develop and track successors for every critical role. 9-box grids, readiness ratings, and IDPs — all backed by evidence. Book a demo.',
    eyebrow: 'Succession Planning',
    h1: 'Succession Planning Software Your Board Will Actually Trust',
    heroIntro:
      "When a critical leader leaves, the succession plan on the HR drive is usually 18 months out of date. CLEAR Talent keeps it live: every successor identified, assessed, developed, and re-ranked continuously — so when the call comes, you already know who's ready.",
    heroImage: '/screenshots/07-reports-tab-individual.webp',
    heroImageAlt:
      'CLEAR Talent Reports & Analytics, Individual tab: pick a staff member and date range to generate a report with competency analysis, a high-potential score and AI-generated insights',
    heroStats: [
      { value: 'Live', label: 'Always-current pipeline' },
      { value: '9-box', label: 'Interactive talent grid' },
      { value: 'Board-ready', label: 'PDF & Excel exports' },
    ],
    primaryCta: { text: 'Book a succession planning demo', href: '/demo?source=succession' },
    secondaryCta: {
      text: 'See a sample succession pack',
      href: '/contact?reason=succession-pack&source=succession',
    },
    sections: [
      {
        id: 'no-spreadsheets',
        eyebrow: 'Live, not stale',
        heading: 'Succession planning without the spreadsheet gymnastics',
        paragraphs: [
          'Most organisations manage succession in Excel — and most Excels are wrong.',
          'CLEAR Talent links succession to the same competency data, 360° feedback, and performance scores you already use for reviews. No parallel data; no quarterly data-gathering scramble.',
        ],
      },
      {
        id: 'identify',
        eyebrow: 'Evidence-based',
        heading: 'Identify successors with evidence, not gut feel',
        bullets: [
          { title: 'CAF readiness score', description: 'Competency-based readiness mapped to the target role.' },
          { title: 'Performance trajectory', description: 'Historical performance trend — not just last cycle.' },
          { title: '360° leadership scores', description: 'Multi-rater leadership competency input.' },
          { title: 'Mobility & aspiration', description: 'Self-declared willingness to relocate, lead, or step up.' },
        ],
        variant: 'tinted',
      },
      {
        id: 'nine-box',
        eyebrow: '9-box grid',
        heading: 'The 9-box grid, but better',
        paragraphs: [
          'Interactive 9-box with live filtering by role family, region, or diversity dimension. Drag-and-drop placement with a full audit log of every move.',
        ],
        cta: { text: 'Read the 9-box grid guide', href: '/blog/nine-box-grid-talent-review' },
      },
      {
        id: 'readiness',
        eyebrow: 'Readiness ratings',
        heading: 'Readiness ratings with a defined development plan',
        paragraphs: [
          '"Ready now" / "ready 1–2 yrs" / "ready 3+ yrs" / "not a successor" — each with the specific competency gaps that need to close to move up a category.',
        ],
      },
      {
        id: 'gap-alerts',
        eyebrow: 'Risk visibility',
        heading: 'Succession gaps, surfaced before they become crises',
        paragraphs: [
          'Dashboards flag critical roles with fewer than 2 successors, single-point-of-failure positions, and diversity gaps in the pipeline.',
        ],
      },
      {
        id: 'board-ready',
        eyebrow: 'For the board',
        heading: 'Board-ready exports',
        paragraphs: ['One-click PDF and Excel exports for board, nom-co and audit committee.'],
        variant: 'dark',
      },
    ],
    faqs: [
      { q: 'Do you support the 9-box grid?', a: 'Yes — with full configurability of axes.' },
      { q: 'Can I run diversity analytics on my pipeline?', a: 'Yes — with full privacy controls.' },
      {
        q: "How do successors find out they're on a plan?",
        a: "That's your policy — some customers keep it confidential, some openly communicate; CLEAR Talent supports both.",
      },
      {
        q: 'Can we integrate succession with compensation planning?',
        a: 'Via export and API.',
      },
    ],
    internalLinks: [
      { label: 'Competency Management Software', href: '/solutions/competency-management-software' },
      { label: 'Employee Development Software', href: '/solutions/employee-development-software' },
      { label: 'Performance Management Software', href: '/solutions/performance-management-software' },
      { label: 'Guide: the 9-box grid and talent reviews', href: '/blog/nine-box-grid-talent-review' },
      { label: 'Glossary: what is a 9-box grid?', href: '/glossary/nine-box-grid' },
      { label: 'Glossary: what is a talent review?', href: '/glossary/talent-review' },
    ],
  },

  // ─────────────────────────────────────────────
  // 9. Employee Development & IDP Software
  // ─────────────────────────────────────────────
  {
    slug: 'employee-development-software',
    category: 'solutions',
    primaryKeyword: 'Employee Development & IDP Software',
    secondaryKeywords: [
      'IDP software',
      'individual development plan software',
      'career development software',
      'learning and development platform',
      'employee growth platform',
      'L&D management tool',
    ],
    seoTitle: 'Employee Development & IDP Software | CLEAR Talent',
    metaDescription:
      'Turn competency gaps into individual development plans managers actually track. IDP software with career pathing, check-ins and learning impact. Book a demo.',
    // Part of the indexation-depth fix shared with the OKR and continuous-PM
    // pages: the Team IDPs screen, the development/growth goal distinction and
    // the manager's-quarter walkthrough exist on no other page of the site.
    // Re-dated 2026-09-22 for the link to the free IDP template resource,
    // 2026-10-08 for the link to the training needs analysis template, and
    // 2026-10-13 for the link to the talent management page.
    dateModified: '2026-10-13',
    eyebrow: 'Development & IDPs',
    h1: 'Employee Development Software That Actually Drives Growth',
    heroIntro:
      'Most development plans die in a PDF. CLEAR Talent turns IDPs into living, tracked, coached plans — fed directly from competency gaps, 360° feedback, and career aspirations, and refreshed every check-in.',
    heroImage: '/screenshots/09-me-competencies-main.webp',
    heroImageAlt:
      "CLEAR Talent My competencies screen showing an employee's assigned competencies, self-assessment scores, top strength, priority development area and their completed self-assessments",
    heroStats: [
      { value: 'Auto-drafted', label: 'IDPs from CAF gaps' },
      { value: 'In-line', label: 'Check-in tracking' },
      { value: 'Tracked', label: 'Learning impact' },
    ],
    primaryCta: { text: 'Book a development demo', href: '/demo?source=development' },
    secondaryCta: { text: 'See a sample IDP', href: '/contact?reason=sample-idp&source=development' },
    sections: [
      {
        id: 'problem',
        eyebrow: 'The problem',
        heading: 'Why development plans stall',
        paragraphs: [
          'Most organisations already have development plans. They get written once a year, saved somewhere, and looked at again only when the next cycle forces it. Nobody is being negligent — the plan simply has no place in the working week.',
          'The failure is structural rather than motivational. The plan is drafted in a different document from the assessment that identified the gap, tracked nowhere in particular, and revisited so long afterwards that the context has gone. By then the role has usually moved on too.',
          'CLEAR Talent keeps the plan in the same platform as the assessment that created it, the check-ins that move it along, and the review that re-tests it. Development stops being a separate exercise and becomes the follow-through on work you are already doing.',
        ],
      },
      {
        id: 'plan-sources',
        eyebrow: 'Where plans start',
        heading: 'Every plan starts from something you already measured',
        intro:
          'A development plan is only as good as the diagnosis behind it. CLEAR Talent drafts from evidence you already generate, so nobody starts from a blank page.',
        bullets: [
          {
            title: 'Competency assessment',
            description:
              'An assessment against the Competency Assessment Framework (CAF) automatically drafts an IDP from the competencies that matter most for the role, ranked by the size of the current gap. The plan arrives already prioritised.',
          },
          {
            title: '360° feedback',
            description:
              'Every 360° generates a suggested plan built from the lowest-scoring, highest-importance competencies — so multi-rater input becomes a short list of things to work on rather than a word cloud.',
          },
          {
            title: 'Career aspiration',
            description:
              'Where someone has named the role they want next, the gap is calculated against what that role requires, not only against the job they hold today.',
          },
        ],
        cta: { text: 'See competency management', href: '/solutions/competency-management-software' },
        variant: 'tinted',
      },
      {
        id: 'inside-an-idp',
        eyebrow: 'Inside an IDP',
        heading: 'What an individual development plan actually holds',
        intro:
          'An IDP here is a structured record, not a free-text box. Each plan is anchored to named competencies, which is what lets progress be measured on the same scale the assessment used.',
        bullets: [
          {
            title: 'The gap it exists to close',
            description:
              'Each action is attached to a specific competency, its current level and its target level. "Improve stakeholder management" becomes a measurable move between two defined points on a scale.',
          },
          {
            title: 'SMART actions with owners and dates',
            description:
              'Every development action carries an owner, a due date and the resources it needs, so the plan reads as work rather than as intention.',
          },
          {
            title: 'Evidence logged as it happens',
            description:
              'Progress is recorded in line against each action instead of being recalled at year end — which is what makes the next assessment a review of the year rather than of the last fortnight.',
          },
          {
            title: 'Cost and approval where budget is involved',
            description:
              'Actions that need training spend carry a cost field and an approval workflow, so the budget decision sits with the plan it funds.',
          },
        ],
        cta: {
          text: 'Get the free IDP template and three filled-in examples',
          href: '/resources/individual-development-plan-template',
        },
      },
      {
        id: 'three-steps',
        eyebrow: 'The model',
        heading: 'From gap to growth in three steps',
        steps: [
          {
            title: 'Identify the right gap',
            description:
              'The three competencies with the biggest gap between current and target level, weighted by how important each one is to the role. Two or three real gaps beat a list of ten aspirations nobody will reach.',
          },
          {
            title: 'Draft a plan that matters',
            description:
              'SMART development actions tied to specific competencies, with due dates, owners and resources. Employees can draft their own and send it for manager approval — usually the difference between a plan someone owns and a plan someone was handed.',
          },
          {
            title: 'Track progress that sticks',
            description:
              'Monthly check-in prompts, evidence logged in line as the work happens, and re-assessment against the same competency scale at the next review, so you can see whether the gap actually closed.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'manager-coaching',
        eyebrow: 'Manager coaching',
        heading: 'Coaching conversations with something specific in them',
        intro:
          'The hard part of development was never writing the plan. It is every conversation afterwards.',
        paragraphs: [
          'IDP items surface automatically in one-on-ones, so a manager opens the meeting with the two things this person is working on rather than with "how are things?". The coaching is aimed at a named competency at a named level — not at "keep growing".',
          "Because the actions and the evidence live in the record rather than in a manager's notebook, the context is still there when a team changes hands. A development plan survives a reorganisation.",
        ],
      },
      {
        id: 'team-idps',
        eyebrow: 'Inside the product',
        heading: "Every development plan in a manager's team, on one screen",
        intro:
          'Development plans rarely fail loudly. They stop moving, and nobody notices until the next review. The Team IDPs view is where a manager notices first.',
        figure: {
          src: '/images/features/idp-manager.webp',
          alt: 'CLEAR Talent Team IDPs view listing each team member with their individual development plans, development goals, growth goals and progress',
          width: 1920,
          height: 900,
          caption:
            "Team IDPs in CLEAR Talent: a manager's view of the individual development plans, development goals and growth goals across their team.",
          annotations: [
            {
              title: 'Team totals',
              description:
                'Team members, total IDPs and how many are active, development goals and growth goals — the size of the development commitment across a team, visible before anyone opens a single plan.',
            },
            {
              title: 'Department and team filter',
              description:
                'Filter by department or team, with people grouped under the team they belong to, so a manager with more than one group sees each one separately.',
            },
            {
              title: 'One row per person',
              description:
                "Each team member's row shows their role, how many IDPs, development goals and growth goals they hold, and a progress bar for how far through them they are. The row that has not moved is the first agenda item for the next one-on-one.",
            },
            {
              title: 'Manage the plans from here',
              description:
                "Rows expand, and the view is where a manager reviews and manages each person's plans and goals — not a report exported to be read somewhere else.",
            },
          ],
        },
        variant: 'tinted',
      },
      {
        id: 'quarter-walkthrough',
        eyebrow: 'Walkthrough',
        heading: "A manager's development quarter, step by step",
        intro:
          "An illustrative example of how a team lead keeps development moving between two review cycles, using the employee's own goals page and the manager's team view rather than a spreadsheet of who promised what.",
        steps: [
          {
            title: 'Start from the gaps, not from a course catalogue',
            description:
              "Each employee's goals page opens with the capability gaps they have to address, written as a move on the competency scale — collaboration from level 1 to level 3, for example — drawn from their competency assessments.",
          },
          {
            title: 'Separate what the role needs from the stretch',
            description:
              'Development goals close the gaps the assessment found, and the manager assigns them from the results. Growth goals are for stretching beyond the comfort zone, and employees can suggest their own for the manager to consider.',
          },
          {
            title: 'Generate a first draft, then edit it together',
            description:
              "Development goal recommendations can be generated from the employee's competency assessments, KPIs, organisation goals and feedback. They are a starting point for the conversation, not a plan handed down.",
          },
          {
            title: 'Check the whole team, then re-test',
            description:
              'A monthly look at the Team IDPs view shows whose plan is moving and whose is not. At the next assessment the same competencies are scored on the same scale, and the gap has either narrowed or it has not.',
          },
        ],
      },
      {
        id: 'career-pathing',
        eyebrow: 'Career pathing',
        heading: 'A career development platform, not just a gap tracker',
        intro:
          'People rarely leave because they were short of a training course. They leave because they cannot see what comes next.',
        paragraphs: [
          'CLEAR Talent shows an employee the role they could grow into and exactly which capabilities that role calls for, measured against where they are now. The conversation moves from "what training would you like?" to "here is the distance, and here is the plan for closing it".',
          'Because readiness is calculated from the same competency data the organisation uses for succession, an employee working through a development plan and a critical role waiting on a successor are looking at one set of evidence rather than two.',
        ],
        cta: { text: 'See succession planning', href: '/solutions/succession-planning-software' },
      },
      {
        id: 'learning',
        eyebrow: 'Learning & L&D',
        heading: 'How development connects to learning',
        intro:
          'To be clear about the boundary: CLEAR Talent is not a learning management system and does not host course content. It is where the plan, the reason for it and the evidence of progress live, and it runs alongside whatever you use to deliver training.',
        paragraphs: [
          'Within a plan, CLEAR Talent recommends development actions, mentors and stretch assignments based on the gaps that plan is meant to close — so a suggestion is tied to a competency rather than pulled from a catalogue.',
          'Learning impact dashboards then answer the question L&D is always asked and rarely able to evidence: did the activity close the gap it was bought to close? Development activity can be imported and exported via API and CSV or Excel, and enterprise integrations are scoped per rollout.',
        ],
        cta: {
          text: 'Get the free training needs analysis template and scoring sheet',
          href: '/resources/training-needs-analysis-template',
        },
      },
      {
        id: 'at-scale',
        eyebrow: 'For HR and L&D',
        heading: 'Running development across the whole organisation',
        bullets: [
          {
            title: 'Reporting at every level',
            description:
              'Individual, team and organisation dashboards cover development alongside goals and competencies, with one-click PDF, Excel and CSV exports for HR reporting and audit.',
          },
          {
            title: 'Owned by the employee, approved by the manager',
            description:
              'Employees draft and own their plan and submit it for approval, with visibility governed by the same role-based access as the rest of the platform.',
          },
          {
            title: 'Reachable from wherever people work',
            description:
              'Web plus native iOS and Android apps in 16 languages, so a plan is as available on a shop floor, a ward or a site visit as it is at a desk.',
          },
          {
            title: 'A trail you can defend',
            description:
              'Changes are logged with who made them and when — the same audit trail the platform keeps for ratings, approvals and exports.',
          },
        ],
        variant: 'dark',
      },
    ],
    faqs: [
      {
        q: 'What is IDP software?',
        a: 'IDP software is where an individual development plan is created, owned and tracked: the competency gap it exists to close, the actions agreed to close it, who owns each action and by when, and the evidence that they happened. In CLEAR Talent the plan sits in the same platform as the assessment that identified the gap and the review that re-tests it, rather than in a separate document.',
      },
      {
        q: 'How is a development plan different from a performance review?',
        a: 'A review looks backwards and produces a rating; a development plan looks forwards and produces actions. CLEAR Talent connects the two — competency ratings from the review identify the gaps, the plan sets out what will be done about them, and the next assessment measures against the same scale so you can see whether anything moved.',
      },
      {
        q: 'Can employees own their own IDP?',
        a: 'Yes. Employees can draft their own plan and submit it for manager approval, so it is something they own rather than something they were handed. Managers and HR keep visibility through the same role-based access that governs the rest of the platform.',
      },
      {
        q: 'Do you integrate with our LMS?',
        a: 'CLEAR Talent is not a learning management system and does not host course content. Development activity can be imported and exported via API and CSV or Excel so plans and completions stay in step with your learning stack. Talk to us about the systems involved — enterprise integrations are scoped per rollout.',
      },
      {
        q: 'Can we tie IDPs to budget or training spend?',
        a: 'Yes. Development actions carry a cost field and an approval workflow, so training spend is approved against the named gap it is meant to close rather than as a line item detached from the plan.',
      },
      {
        q: 'How often should an IDP be reviewed?',
        a: 'Monthly check-in prompts keep the plan in front of both the employee and the manager between cycles, with a full re-assessment against the competency scale at the next review. The cadence follows the assessment periods you configure rather than a rhythm the platform imposes.',
      },
      {
        q: 'Does CLEAR Talent support career pathing as well as development planning?',
        a: 'Yes. An employee can see the role they could move into next and the capabilities it requires, measured against their current competency levels. The same readiness data feeds succession planning, so the organisation and the individual work from one view rather than two.',
      },
      {
        q: 'What is the difference between a development goal and a growth goal?',
        a: "A development goal closes a gap a competency assessment has found, and is assigned by the manager from the assessment results. A growth goal is for stretching beyond the comfort zone: employees can suggest their own for their manager to consider, or work on one their manager assigns. Both sit on the same page as the employee's performance goals, so development is not something they have to go and find in a separate system.",
      },
      {
        q: 'Can a manager see every development plan in their team at once?',
        a: 'Yes. The Team IDPs view lists each team member with their role, the number of IDPs, development goals and growth goals they hold, and a progress bar for how far through them they are. Totals for the team sit at the top, and the list can be filtered by department or team. What a manager can see follows the same role-based access as the rest of the platform.',
      },
      {
        q: 'Where does an employee start if they have no development goals yet?',
        a: 'With a self-assessment against the competencies assigned to their role. Their manager reviews the results and assigns development goals to the gaps that matter. In the meantime the employee can suggest a growth goal of their own, or generate recommendations drawn from their assessments, KPIs, organisation goals and feedback.',
      },
    ],
    internalLinks: [
      { label: 'Competency Management Software', href: '/solutions/competency-management-software' },
      { label: 'Succession Planning Software', href: '/solutions/succession-planning-software' },
      { label: '360° Feedback Software', href: '/solutions/360-degree-feedback-software' },
      { label: 'Employee Performance Review Software', href: '/solutions/employee-performance-review-software' },
      { label: 'Continuous Performance Management', href: '/solutions/continuous-performance-management' },
      {
        label: 'Template: skills gap analysis, from required levels to development actions',
        href: '/resources/skills-gap-analysis-template',
      },
      {
        label: 'Talent Management Software: development alongside reviews and succession',
        href: '/solutions/talent-management-software',
      },
    ],
    // Each entry is something the sections and FAQs above describe, so the
    // SoftwareApplication node says what the software does rather than listing
    // the keyword variants a buyer might type to reach the page.
    schemaFeatures: [
      'Individual development plans drafted from competency assessment and 360° feedback gaps',
      'SMART development actions with owners, due dates, resources and evidence logged in line',
      'Cost fields and an approval workflow for development actions that need training spend',
      'Development goals and growth goals alongside performance goals',
      'Development goal recommendations from assessments, KPIs, organisation goals and feedback',
      "Team IDPs view of every plan, development goal and growth goal in a manager's team",
      'Career pathing against the competency requirements of a target role',
      'Learning impact dashboards, with API, CSV and Excel import and export of development activity',
    ],
  },

  // ─────────────────────────────────────────────
  // 9a. Talent Management Software (umbrella)
  // ─────────────────────────────────────────────
  // Owns "talent management software" and "talent management platform". It is
  // the umbrella over the performance, competency, development and succession
  // pages, so it explains how one set of evidence moves between them and where
  // the platform stops, and leaves each workflow's detail — and its head term —
  // to the page that owns it. Every capability restates something those pages,
  // the AI page, /pricing or /integrations already say. The scope boundary (no
  // recruitment, applicant tracking, payroll or core HR) restates
  // constants/integrations.ts and the ClearCompany comparison.
  {
    slug: 'talent-management-software',
    category: 'solutions',
    primaryKeyword: 'Talent Management Software',
    secondaryKeywords: [
      'talent management platform',
      'talent management system',
      'integrated talent management',
      'talent management suite',
    ],
    seoTitle: 'Talent Management Software & Platform | CLEAR Talent',
    metaDescription:
      "Talent management software that connects performance reviews, competencies, 9-box succession, development plans and Ava's AI on one record. Book a demo.",
    eyebrow: 'Talent Management',
    h1: 'Talent Management Software That Connects Performance, Skills and Succession',
    heroIntro:
      'Most organisations already run reviews, keep a competency framework, write development plans and hold a succession list. The trouble is that each one lives in its own document, built from its own evidence. CLEAR Talent puts them on one platform and one competency framework, so the rating from a review is the same data that drafts a development plan and informs who is ready for a critical role.',
    heroImage: '/screenshots/07-reports-main.webp',
    heroImageAlt:
      'CLEAR Talent Reports & Analytics screen with individual, team, department and executive summary reports, filtered by staff member and date range',
    heroStats: [
      { value: 'Connected', label: 'Reviews, skills, IDPs & succession' },
      { value: '16', label: 'Languages supported' },
      { value: 'Human-led', label: 'Ava drafts, people decide' },
    ],
    primaryCta: { text: 'Book a talent management demo', href: '/demo?source=talent-management' },
    secondaryCta: { text: 'Start free trial', href: 'https://login.clear-talent.com/register' },
    sections: [
      {
        id: 'after-the-hire',
        eyebrow: 'What it covers',
        heading: 'Talent management, from the first goal to the next role',
        paragraphs: [
          'Talent management is usually drawn as a lifecycle that starts with a job advert and ends with an exit interview. CLEAR Talent covers the part of that lifecycle where most of the judgement happens: what someone is asked to deliver, how well they deliver it, which capabilities they still need, and whether they could step into a bigger role.',
          'It does not cover recruitment or applicant tracking, and it does not run payroll, time off or the core employee record. Those stay in the systems you already have. If buying hiring and performance from one supplier matters more to you than depth in assessment and development, a broader HR suite may be the better fit, and it is worth knowing that before a demo rather than after one.',
        ],
      },
      {
        id: 'four-parts',
        eyebrow: 'One platform',
        heading: 'Four talent processes, one competency framework underneath',
        intro:
          'Each of these has its own page with the detail. What matters here is that they read from, and write to, the same record.',
        bullets: [
          {
            title: 'Performance',
            description:
              'Goals that cascade from organisation objectives, KPIs, continuous check-ins, anonymised 360° feedback and review cycles with calibration, combined into one rating a manager can explain.',
          },
          {
            title: 'Competencies',
            description:
              'One library of competencies with behavioural anchors and three to seven proficiency levels, role-specific target levels, and ratings that can be set to require evidence.',
          },
          {
            title: 'Development',
            description:
              'Individual development plans drafted from the competencies that matter most for the role, ranked by the size of the gap, with owners, due dates and monthly check-in prompts.',
          },
          {
            title: 'Succession and the 9-box',
            description:
              'Readiness ratings for critical roles, a 9-box grid with configurable axes and an audit log of every move, and dashboards that flag roles with fewer than two successors.',
          },
        ],
        cta: { text: 'See competency management', href: '/solutions/competency-management-software' },
        variant: 'tinted',
      },
      {
        id: 'one-thread',
        eyebrow: 'How it connects',
        heading: 'How one set of evidence moves through the talent cycle',
        intro:
          'The value of a connected platform is not that four screens share a login. It is that nobody has to re-key, re-interpret or re-argue the same evidence at each step.',
        steps: [
          {
            title: 'Define what each role needs',
            description:
              'Each role gets the competencies it requires and the target level for each, described as observable behaviour. That definition is reused by every step that follows.',
          },
          {
            title: 'Assess and review against it',
            description:
              'Self-review, manager review and 360° feedback rate the same competencies on the same scale, alongside goal and KPI scores. Calibration compares ratings across managers before they go final.',
          },
          {
            title: 'Turn the gaps into a plan',
            description:
              'The assessment drafts a development plan around the largest gaps that matter most. The employee and manager edit it together, and the next assessment re-scores the same competencies, so you can see whether a gap closed.',
          },
          {
            title: 'Decide who is ready for what',
            description:
              'Readiness for a critical role is read from the same competency ratings, performance trend and 360° input — "ready now", "ready in 1–2 years", "ready in 3+ years" — each with the specific gaps that stand in the way.',
          },
        ],
        cta: { text: 'See how development plans work', href: '/solutions/employee-development-software' },
      },
      {
        id: 'why-connected',
        eyebrow: 'Why it matters',
        heading: 'What changes when the talent processes share a record',
        paragraphs: [
          'When reviews, development and succession run in separate places, every talent conversation starts with an argument about whose numbers are right. The succession list was compiled from last year’s ratings, the development plan was written before the latest 360°, and the competency framework in the policy document is not quite the one managers rate against.',
          'Running them on one framework removes that argument. A talent review opens with the same ratings, evidence and development history the manager saw in the review, a high-potential conversation can point at the competencies behind it, and a successor’s development plan is aimed at the gaps that stand between them and the role, not at a generic leadership course.',
        ],
        cta: { text: 'See succession planning', href: '/solutions/succession-planning-software' },
        variant: 'tinted',
      },
      {
        id: 'ava',
        eyebrow: 'AI with people in charge',
        heading: 'Ava does the drafting across the cycle; people make the calls',
        paragraphs: [
          'Ava, the AI assistant inside CLEAR Talent, takes on the work that slows every talent process down: she drafts review narratives from goal scores, competency ratings, 360° feedback and check-in notes, consolidates 360° input into strengths, development areas and a suggested focus, and prompts a manager before submission when a rating pattern may indicate gender, recency or leniency bias.',
          'None of that is a decision. Every Ava output is labelled, editable and logged, the manager accepts, amends or rejects it, and Ava can be switched off per review cycle, team or module. Your employees’ data is never used to train models for other customers.',
        ],
        cta: { text: 'See AI performance management with Ava', href: '/solutions/ai-performance-management' },
      },
      {
        id: 'starting-point',
        eyebrow: 'Rolling it out',
        heading: 'Where to start, and what each plan includes',
        paragraphs: [
          'A talent platform rarely goes live in one step, and it does not need to. Starting with the competency framework and one review cycle gives every later process something to read from; development plans follow from the first assessments, and succession builds on the ratings and plans that already exist. The rollout itself is scoped with each organisation before work begins.',
          `Not every module sits on every plan. ${SUCCESSION_MODULE} is one of the modules the quoted Enterprise plan adds, so check which plan covers the talent processes you intend to run before you scope the rollout, and raise anything unclear in the demo.`,
        ],
        cta: { text: 'Compare plans and pricing', href: '/pricing' },
      },
      {
        id: 'alongside-hris',
        eyebrow: 'Data and trust',
        heading: 'Built to sit beside your HRIS, with the controls people data needs',
        paragraphs: [
          'There is no prebuilt connector to a named HRIS. Organisation structure, goals and ratings come in through CSV and Excel bulk import or the API, and records export to CSV, Excel or PDF on every plan.',
          'Role-based access decides who sees which reviews, plans and succession data; sign-ins, exports and rating changes are audit-logged; and subject access request exports and right-to-be-forgotten workflows support your GDPR programme. The platform is hosted on Google Cloud Platform.',
        ],
        cta: { text: 'Visit the trust centre', href: '/trust' },
        variant: 'dark',
      },
    ],
    faqs: [
      {
        q: 'What is talent management software?',
        a: 'Talent management software is where an organisation manages its people after they are hired: setting goals, reviewing performance, assessing competencies, planning development and deciding who could step into critical roles. In CLEAR Talent those workflows share one competency framework and one set of evidence, so a rating given in a review is the same data that drafts a development plan and informs a succession decision.',
      },
      {
        q: 'Does CLEAR Talent include recruitment or payroll?',
        a: 'No. CLEAR Talent has no recruitment or applicant tracking product, and it does not run payroll, time off or the core employee record. It covers performance and development: goals and KPIs, review cycles, 360° feedback, competency assessment, calibration, development plans and succession. It is built to run alongside the HRIS and hiring tools you already have, so if one supplier for recruiting and performance matters most, a broader suite may suit you better.',
      },
      {
        q: 'How is talent management software different from performance management software?',
        a: 'Performance management is one part of talent management: the goals, feedback, reviews and calibration that judge how someone is doing in the role they hold. Talent management adds the forward-looking decisions built on that evidence — which capabilities to develop, who could move into a critical role and how ready they are. CLEAR Talent runs both on the same competency data rather than as separate modules with separate records.',
      },
      {
        q: 'Is every talent management capability on every plan?',
        a: `No. Plans are published on the pricing page, and some modules sit only on the quoted Enterprise plan — ${SUCCESSION_MODULE} among them. Check which plan covers the capabilities you need before you scope a rollout, and raise anything unclear in the demo so the quote covers the talent processes you actually intend to run, not only the review cycle.`,
      },
      {
        q: 'How does Ava fit into talent management?',
        a: 'Ava drafts and summarises; people decide. She writes first-draft review narratives from goal scores, competency ratings, 360° feedback and check-in notes, consolidates 360° input into strengths and development areas, and prompts managers about rating patterns that may indicate bias. Every output is labelled, editable and logged, and Ava can be switched off per review cycle, team or module. Your data is never used to train models for other customers.',
      },
      {
        q: 'How does our existing people data get into CLEAR Talent?',
        a: 'There is no prebuilt connector to a named HRIS. Organisation structure — departments, locations, roles and reporting lines — along with goals and ratings comes in through CSV and Excel bulk import or the API, and records export to CSV, Excel or PDF on every plan. How your current data maps across is agreed during discovery, before anything moves.',
      },
    ],
    internalLinks: [
      { label: 'Performance Management Software', href: '/solutions/performance-management-software' },
      { label: 'Competency Management Software', href: '/solutions/competency-management-software' },
      { label: 'Employee Development & IDP Software', href: '/solutions/employee-development-software' },
      { label: 'Succession Planning Software', href: '/solutions/succession-planning-software' },
      { label: 'Skills Management Software', href: '/solutions/skills-management-software' },
      { label: '360° Feedback Software', href: '/solutions/360-degree-feedback-software' },
      { label: 'Integrations: import, API access and export', href: '/integrations' },
      { label: 'Guide: the 9-box grid and talent reviews', href: '/blog/nine-box-grid-talent-review' },
      { label: 'Glossary: what is a talent review?', href: '/glossary/talent-review' },
      { label: 'Buyer’s guide: best performance management software', href: '/best-performance-management-software' },
    ],
    schemaFeatures: [
      'Goals cascaded from organisation objectives, KPIs, check-ins and review cycles with calibration',
      'Competency library with behavioural anchors, proficiency levels and role-specific target levels',
      'Anonymised 360° feedback rated against the same competencies as reviews',
      'Individual development plans drafted from competency gaps, with check-in tracking',
      'Succession readiness ratings and a 9-box grid with configurable axes and an audit log',
      'Ava AI drafting and summaries with human approval, switchable per cycle, team or module',
      'CSV and Excel bulk import, API access and CSV, Excel and PDF export',
    ],
    dateModified: '2026-10-13',
  },

  // ─────────────────────────────────────────────
  // 9b. Skills Management & Skills Matrix Software
  // ─────────────────────────────────────────────
  // Owns "skills management software" and "skills matrix software", which the
  // competency page no longer lists. The split: the competency page is about
  // defining the framework and assessing against it; this one is about using the
  // results as a workforce-wide skills inventory — the taxonomy, required levels
  // per role, coverage and gaps, and what they mean for planning. Every capability
  // restates something the competency, development, succession or integrations
  // pages, or the product tour, already say. There are no skills heatmaps (the
  // heatmaps are calibration's) and no headcount forecasting, so it claims neither.
  {
    slug: 'skills-management-software',
    category: 'solutions',
    primaryKeyword: 'Skills Management Software',
    secondaryKeywords: [
      'skills matrix software',
      'skills-based talent management',
      'skills inventory software',
      'skills taxonomy',
    ],
    seoTitle: 'Skills Management & Skills Matrix Software | CLEAR Talent',
    metaDescription:
      'Skills management software with a live skills matrix: role-based proficiency levels, gap reporting by team and department, and links to IDPs. Book a demo.',
    eyebrow: 'Skills Management',
    h1: 'Skills Management Software: Know What Your Workforce Can Do',
    heroIntro:
      'Ask most HR teams how many people can do a given job to the standard it needs, and the answer is in a spreadsheet someone last updated before the reorganisation. CLEAR Talent keeps a skills inventory that fills itself from the assessments you already run — set against the levels each role requires, and readable by person, team, department and skill.',
    heroImage: '/screenshots/07-reports-tab-team.webp',
    heroImageAlt:
      'CLEAR Talent Reports & Analytics, Team tab: pick a team to generate a report with team statistics, the competency distribution across its members and actionable insights',
    heroStats: [
      { value: 'Per role', label: 'Required skills and target levels' },
      { value: '3–7', label: 'Proficiency levels per scale' },
      { value: 'Team & department', label: 'Coverage and gap reporting' },
    ],
    primaryCta: { text: 'Book a skills management demo', href: '/demo?source=skills' },
    secondaryCta: {
      text: 'Get the free skills gap analysis template',
      href: '/resources/skills-gap-analysis-template',
    },
    sections: [
      {
        id: 'problem',
        eyebrow: 'The problem',
        heading: 'Why most skills matrices stop being trusted',
        paragraphs: [
          'A skills matrix usually starts life as a spreadsheet: people down the side, skills across the top, a number in each cell. It is useful for about one quarter. Then people move, roles change, the ratings were mostly self-assessed in the first place, and the sheet quietly stops matching the organisation it describes.',
          'The cost shows up when someone needs a straight answer. Which teams are thin on a capability the next project depends on? Which skills sit with one person who has just handed in their notice? Should this gap be closed by developing people or by hiring? A matrix nobody trusts cannot answer any of those, so the decision gets made on instinct.',
          'Skills management software fixes the maintenance problem rather than the format. The grid stays the same; what changes is that it is built from current assessments against defined levels, so it is as up to date as the last review rather than the last time someone opened the file.',
        ],
      },
      {
        id: 'taxonomy',
        eyebrow: 'The skills taxonomy',
        heading: 'One library behind every skill you track',
        intro:
          'A skills inventory is only comparable across teams if everyone is rating the same thing on the same scale. In CLEAR Talent that structure is a single competency library, organised into categories and reused everywhere.',
        bullets: [
          {
            title: 'Categories you can filter by',
            description:
              'Competencies are grouped into categories, so a skills taxonomy can separate technical, leadership and behavioural capabilities and still be read as one inventory.',
          },
          {
            title: 'Proficiency levels with behavioural indicators',
            description:
              'Each entry has defined levels — three to seven per scale, configured by you — described through behaviour someone could observe, so a level 3 in one department means a level 3 in another.',
          },
          {
            title: 'Practical skills alongside broader competencies',
            description:
              'The library is fully editable. A system, a procedure or a piece of equipment can sit beside communication or clinical judgement as an entry with its own levels, assessed in the same way.',
          },
          {
            title: 'A starting point rather than a blank page',
            description:
              'Industry starter libraries are fully editable, and AI assistance helps draft new competencies and their assessment questions for your own roles. Your team decides what goes in.',
          },
        ],
        cta: {
          text: 'Browse the core competencies list with behavioural examples',
          href: '/resources/core-competencies-list',
        },
        variant: 'tinted',
      },
      {
        id: 'role-profiles',
        eyebrow: 'Inside the product',
        heading: 'Required skills per role, set once',
        intro:
          'The other half of a skills matrix is the target: what each role actually needs. Role profiles hold that requirement, so every gap in the inventory is measured against something the organisation agreed rather than against an individual manager’s expectations.',
        figure: {
          src: '/screenshots/05-roles-main.webp',
          alt: 'CLEAR Talent Roles & Departments screen showing role profiles with their department, role level and number of required competencies, and tabs for the role-competency matrix and departments',
          width: 1920,
          height: 1283,
          caption:
            'Roles & Departments in CLEAR Talent: each role profile carries its department, role level and the competencies it requires.',
          annotations: [
            {
              title: 'Role Profiles, Role-Competency Matrix and Departments',
              description:
                'Three tabs over one structure: the roles themselves, the matrix setting roles against the competencies they require, and the departments they belong to.',
            },
            {
              title: 'Department, role level and competency filters',
              description:
                'Narrow the list by department, by role level or by a single competency — the quickest way to see every role that depends on one skill.',
            },
            {
              title: 'Role level on every profile',
              description:
                'Entry-level / Graduate, Mid-level / Experienced, Senior / Specialist and Manager / People Leader, so the same skill can carry a different target level as roles become more senior.',
            },
            {
              title: 'Competencies per role',
              description:
                'Each card shows how many competencies the role requires. Edit opens the profile, where the requirement is maintained once and then applies to everyone in the role.',
            },
          ],
        },
      },
      {
        id: 'inventory',
        eyebrow: 'Skills inventory',
        heading: 'An inventory filled from assessment, not guesswork',
        paragraphs: [
          'Each cell of the matrix comes from a competency assessment against the defined levels. Self-assessment and manager assessment are compared side by side, and ratings can require supporting evidence, so a level is something that was observed rather than something someone felt confident claiming.',
          'Because role profiles hold the target, the comparison of required against actual level is there for every person without anyone building it. When the next assessment cycle runs, the inventory moves with it — there is no separate matrix to update.',
        ],
        cta: {
          text: 'See how competency assessment works',
          href: '/solutions/competency-management-software',
        },
      },
      {
        id: 'gaps',
        eyebrow: 'Coverage and gaps',
        heading: 'Read the matrix by person, team, department or skill',
        intro:
          'A skills matrix earns its keep when you can read it in both directions: along a row for one person, down a column for how much of a capability the organisation really has.',
        bullets: [
          {
            title: 'Individual',
            description:
              'Required against actual level for each competency in the role, with longitudinal charts showing whether a gap is narrowing between assessments.',
          },
          {
            title: 'Team',
            description:
              'The team report shows how members score across each competency, with trend analysis, alongside assessment coverage — how many people have actually been assessed.',
          },
          {
            title: 'Department and organisation',
            description:
              'Dashboards show capability coverage by team, department and competency, and flag the gaps, so the thin spots are visible before a project or a resignation exposes them.',
          },
          {
            title: 'Exports for your own analysis',
            description:
              'PDF, Excel and CSV exports on every plan, plus API access, so skills data can be taken into whatever model finance or workforce planning already uses.',
          },
        ],
        cta: { text: 'What is a skills matrix? Read the definition', href: '/glossary/skills-matrix' },
        variant: 'tinted',
      },
      {
        id: 'workforce-planning',
        eyebrow: 'Workforce planning',
        heading: 'Skills data for the decisions workforce planning has to make',
        intro:
          'To be clear about the boundary: CLEAR Talent does not forecast headcount or model budgets. What it supplies is the half of workforce planning most organisations do not have — an accurate picture of the skills they already employ.',
        paragraphs: [
          'Read down a column and you can see where a capability depends on one or two people, which is the single-point-of-failure question every workforce plan should ask. Filter role profiles by a competency and you can see every role that depends on it, which tells you how far a shortage would reach.',
          'The same data separates "develop" from "hire". A gap of one level across a team that is otherwise strong is a development problem with a plan attached. A capability nobody holds at any level is usually a hiring decision. And because succession readiness is calculated from the same competency data, the bench for a critical role and the skills inventory for the department are one set of evidence rather than two.',
        ],
        cta: { text: 'See succession planning', href: '/solutions/succession-planning-software' },
      },
      {
        id: 'development',
        eyebrow: 'From gap to growth',
        heading: 'Every gap leads somewhere',
        intro:
          'A matrix that records a gap and stops there is a report. Skills-based talent management means the gap changes what happens next for the person, the team and the review.',
        steps: [
          {
            title: 'The assessment shows the gap',
            description:
              'Current level against the level the role requires, for each competency in the profile, confirmed by the manager rather than self-reported alone.',
          },
          {
            title: 'A development plan is drafted from it',
            description:
              'Each assessment drafts an individual development plan around the competencies that matter most for the role, ranked by the size of the gap.',
          },
          {
            title: 'Progress is tracked between cycles',
            description:
              'Development goals sit on the employee’s goals page and come up at check-ins, so the plan is worked on during the year rather than rediscovered at the end of it.',
          },
          {
            title: 'The next review re-scores the same skills',
            description:
              'The same library flows into reviews and 360° feedback, so the next assessment measures on the same scale and the matrix shows whether the gap actually closed.',
          },
        ],
        cta: { text: 'See employee development and IDPs', href: '/solutions/employee-development-software' },
        variant: 'tinted',
      },
      {
        id: 'rollout',
        eyebrow: 'Getting started',
        heading: 'Building a skills matrix you will still trust next year',
        intro:
          'An illustrative sequence for a first rollout. It works best when it starts narrow and is driven by the decisions the matrix has to support.',
        steps: [
          {
            title: 'Name the decisions first',
            description:
              'Training budget, project staffing, succession cover or hiring — two or three of them. They decide which skills belong in the matrix and how precise the levels need to be.',
          },
          {
            title: 'Start with a handful of roles',
            description:
              'Choose roles where a gap would hurt, set the competencies each one requires and the target level for each, and leave the rest of the organisation until the first set works.',
          },
          {
            title: 'Assess, then confirm',
            description:
              'Self-assessment as the starting point, confirmed by the manager against the behavioural indicators. Unconfirmed self-ratings say as much about confidence as about skill.',
          },
          {
            title: 'Read the gaps and act on a few',
            description:
              'Look down the columns for thin coverage and along the rows for individual gaps, then turn the most important ones into development plans. Extend to more roles once the first set is being used.',
          },
        ],
      },
    ],
    faqs: [
      {
        q: 'What is skills management software?',
        a: 'Skills management software records which skills an organisation needs, which its people have and at what level, and where the two do not meet. In CLEAR Talent that inventory is built from competency assessments against defined proficiency levels and the target levels set in each role profile, so it stays current as people are assessed rather than being maintained by hand.',
      },
      {
        q: 'What is the difference between skills management and competency management?',
        a: 'Competency management is about defining the standard and assessing people against it: the framework, the behavioural indicators and the evidence behind each rating. Skills management uses those results as an inventory across the workforce — coverage, gaps, single points of failure and what to develop or hire for. In CLEAR Talent both work from the same library, so there is no second set of data to keep in step.',
      },
      {
        q: 'Is a skills matrix the same as a competency matrix?',
        a: 'The terms are often used interchangeably. Where a distinction is drawn, a skills matrix tracks practical skills such as a system or procedure, and a competency matrix tracks broader capabilities such as communication or leadership. CLEAR Talent can hold both in one library, each with its own defined levels, so they can be read in one grid.',
      },
      {
        q: 'How many proficiency levels can a skill have?',
        a: 'Three to seven levels per scale, configured by you, each described through observable behaviour. Different scales can be used for technical, leadership and individual-contributor roles, and a role profile sets the target level each role needs.',
      },
      {
        q: 'Can CLEAR Talent support workforce planning?',
        a: 'It supplies the skills side of it. Coverage and gap reporting by team, department and competency shows where a capability is thin or depends on one person, and succession readiness draws on the same data. CLEAR Talent does not forecast headcount or model budgets; skills data can be exported to Excel, CSV or PDF, or reached through the API, for use in the planning model you already have.',
      },
      {
        q: 'How do skills gaps turn into development?',
        a: 'Each competency assessment drafts an individual development plan around the competencies that matter most for the role, ranked by the size of the gap. Development goals are then tracked at check-ins, and the next assessment re-scores the same competencies on the same scale, so you can see whether the gap closed.',
      },
      {
        q: 'Can we bring in our existing skills matrix?',
        a: 'Organisation structure, goals and ratings can be brought in through CSV and Excel bulk import or the API. How an existing spreadsheet matrix maps onto your competency library and role profiles is agreed during discovery, before any data moves.',
      },
    ],
    internalLinks: [
      { label: 'Competency Management Software', href: '/solutions/competency-management-software' },
      { label: 'Employee Development Software', href: '/solutions/employee-development-software' },
      { label: 'Succession Planning Software', href: '/solutions/succession-planning-software' },
      { label: 'Glossary: what is a skills matrix?', href: '/glossary/skills-matrix' },
      { label: 'Template: how to conduct a skills gap analysis', href: '/resources/skills-gap-analysis-template' },
      {
        label: 'Reference: core competencies list with behavioural examples',
        href: '/resources/core-competencies-list',
      },
      {
        label: 'Guide: building a competency framework, with a matrix template',
        href: '/blog/competency-framework-guide',
      },
      { label: 'Integrations: import, API access and export', href: '/integrations' },
    ],
    dateModified: '2026-10-08',
    schemaFeatures: [
      'Competency library organised into categories, with configurable proficiency levels and behavioural indicators',
      'Role profiles with required competencies and target levels by department and role level',
      'Skills inventory built from self and manager competency assessments with supporting evidence',
      'Required versus actual level comparison for every employee',
      'Coverage and gap reporting by individual, team, department and competency, with trends over time',
      'Skills gaps drafted into individual development plans',
      'PDF, Excel and CSV export and API access to skills data',
    ],
  },

  // ─────────────────────────────────────────────
  // 9b. Employee Engagement & Pulse Survey Software
  // ─────────────────────────────────────────────
  {
    /**
     * Pulse- and engagement-survey queries sit next to performance management,
     * but CLEAR Talent has no survey product — the head-to-heads, the 360
     * roundup and llms-full.txt all say so. This page therefore answers the
     * query honestly: it states the boundary first, then covers what a buyer
     * still needs from the performance side — how survey findings become
     * 1-on-1 actions, goals and evidence, anonymity and GDPR, and sample
     * questions — using only the feedback and check-in features the product
     * ships. Survey results are not imported: bulk import covers people,
     * structure, goals and ratings (constants/integrations.ts). If a survey
     * module ever ships, rewrite the page around it rather than adding claims.
     */
    slug: 'employee-engagement-survey-software',
    category: 'solutions',
    primaryKeyword: 'Employee Engagement & Pulse Survey Software',
    secondaryKeywords: [
      'pulse survey software',
      'employee engagement survey tool',
      'employee pulse survey questions',
      'anonymous employee feedback',
      'engagement survey anonymity threshold',
    ],
    seoTitle: 'Employee Engagement & Pulse Survey Software | CLEAR Talent',
    metaDescription:
      'Pulse and engagement surveys measure the organisation; CLEAR Talent holds the performance record. How they connect, with anonymity, GDPR and sample questions.',
    dateModified: '2026-10-10',
    eyebrow: 'Engagement & pulse surveys',
    h1: 'Employee engagement & pulse survey software: where CLEAR Talent fits',
    heroIntro:
      'A straight answer first: CLEAR Talent does not run engagement or pulse surveys. It is the performance and development platform that sits beside your survey tool — 1-on-1s, goal check-ins, structured feedback and a performance journal — so that what a survey tells you about a team turns into conversations, goals and evidence for individual people. This page covers how the two connect, how to keep survey anonymity and GDPR in order, and question sets you can use on both sides.',
    heroImage: '/screenshots/12-feedback-main.webp',
    heroImageAlt:
      'CLEAR Talent Continuous Feedback screen with development notes and kudos tagged to competencies and marked private or public, alongside feedback statistics and quick templates',
    heroStats: [
      { value: '1-on-1s', label: 'Scheduled, summarised, followed up' },
      { value: 'Thresholds', label: 'Minimum-rater rules on 360° feedback' },
      { value: 'DSAR', label: 'Access-request exports and erasure' },
    ],
    primaryCta: { text: 'Talk to us about feedback and check-ins', href: '/demo?source=engagement' },
    secondaryCta: {
      text: 'See continuous performance management',
      href: '/solutions/continuous-performance-management',
    },
    sections: [
      {
        id: 'straight-answer',
        eyebrow: 'The boundary',
        heading: 'CLEAR Talent is not a survey tool — and what it is instead',
        paragraphs: [
          'If an engagement or pulse survey is a required part of this purchase, CLEAR Talent does not cover it and you will need a dedicated survey tool. We would rather you knew that on the first page you read than in the third demo. Several platforms combine surveys with performance management, and our comparison pages name them and say where each one fits.',
          'What CLEAR Talent does cover is the part a survey cannot: the record for each person. One-on-ones, check-ins against goals, development notes, kudos, a performance journal and anonymised 360° feedback build that record through the year, and it is what the formal review is assessed from. Most organisations that run surveys still need that record; the two tools answer different questions.',
        ],
        cta: {
          text: 'Compare 360 feedback tools, including ones that bundle surveys',
          href: '/best-360-feedback-tools',
        },
      },
      {
        id: 'different-questions',
        eyebrow: 'Two kinds of data',
        heading: 'What a pulse survey measures, and what it cannot',
        intro:
          'Engagement and pulse surveys ask how people feel about the organisation. Feedback and check-ins ask how one person is doing and what happens next. Mixing them up is how surveys end up being read as performance data, which they were never designed to be.',
        bullets: [
          {
            title: 'A survey reports on groups',
            description:
              'Results are read for a team, a department or the whole organisation, and only once enough people have answered to keep individuals anonymous. A good survey tells you where to look; it does not tell you about any one person.',
          },
          {
            title: 'A check-in records an individual',
            description:
              'A 1-on-1, a goal check-in or a development note is about one named person, agreed with them and kept on their record. It is evidence for their review, which is exactly why it can never be anonymous.',
          },
          {
            title: 'Feedback sits in between',
            description:
              '360° feedback is about one person but collected from several raters, so it is anonymised within each rater group. That is the closest CLEAR Talent comes to survey-style input, and it is structured against the competencies in the role profile rather than gathered as open text.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'feed-performance',
        eyebrow: 'Connecting the two',
        heading: 'How pulse survey results feed performance and goal data',
        intro:
          'There is no connector between a survey tool and CLEAR Talent, and survey responses are not imported into it. The link is what managers do with the results. This is the sequence that keeps it useful without breaking anyone’s anonymity.',
        steps: [
          {
            title: 'Read the results at the level the survey reports',
            description:
              'Take the team or department result as it comes out of your survey tool. Do not try to work out who said what, and do not carry individual comments into anyone’s performance record.',
          },
          {
            title: 'Take the theme into the next 1-on-1s',
            description:
              'If workload or clarity of priorities scored low, the manager raises it as a team finding in the next round of 1-on-1s. Each person’s 1-on-1 in CLEAR Talent carries a summary of previous sessions and suggested next steps, so the follow-up is still there next month.',
          },
          {
            title: 'Record what was agreed, privately if it needs to be',
            description:
              'Actions agreed with an individual are captured as a development note linked to that 1-on-1, which can be kept private. Recognition for someone who helped fix the problem can go on record as kudos, tagged to the competency it shows.',
          },
          {
            title: 'Turn team actions into goals with check-ins',
            description:
              'Where the fix is a piece of work — clearer handovers, a reworked rota, a delivery process — it becomes a goal linked to the organisation objective it serves. Check-ins keep it current, and goals falling behind are flagged from pace and check-in signals while the quarter is still running.',
          },
          {
            title: 'Compare at team level, never person by person',
            description:
              'Before the next survey, export goal and review data as CSV or Excel and set it beside the new results for the same teams or departments. Join the two only at a group size your survey tool would itself report, or the comparison can identify people.',
          },
        ],
      },
      {
        id: 'what-ships',
        eyebrow: 'Inside the product',
        heading: 'Feedback and check-ins: what CLEAR Talent actually ships',
        bullets: [
          {
            title: '1-on-1s that remember the last one',
            description:
              'Managers schedule 1-on-1s from their own schedule, which tracks upcoming, completed and cancelled sessions, and the team overview flags anyone who still needs a next one booked.',
          },
          {
            title: 'Goal check-ins',
            description:
              'Check-ins against each goal keep progress current, with at-risk flags from pace and check-in signals and goals linked to the organisation objectives above them.',
          },
          {
            title: 'Development notes and kudos',
            description:
              'Coaching is recorded as development notes that can be kept private; kudos recognise good work, can be tagged to a competency or value and can be made public.',
          },
          {
            title: 'A performance journal',
            description:
              'Managers log observations, achievements and incidents as they happen, each tagged to the competencies it evidences, and refer to it when the person is next assessed.',
          },
          {
            title: 'Anonymised 360° feedback',
            description:
              'Rater groups across managers, peers, direct reports and stakeholders, anonymised within each group, with minimum-rater thresholds and input structured against role competencies.',
          },
          {
            title: 'On the web and on mobile',
            description:
              'Check-ins and feedback work in native iOS and Android apps as well as the browser, with push notifications and stage reminders so the rhythm does not depend on someone chasing.',
          },
        ],
        cta: {
          text: 'See how check-ins carry into the year-end review',
          href: '/solutions/continuous-performance-management',
        },
        variant: 'tinted',
      },
      {
        id: 'anonymity',
        eyebrow: 'Anonymity thresholds',
        heading: 'Anonymity people actually believe',
        intro:
          'People answer a pulse survey honestly only if they are sure they cannot be identified. Whichever survey tool you use, settle these rules before the first survey goes out rather than after a small team’s results land on a manager’s desk.',
        bullets: [
          {
            title: 'Set a minimum group size before results are shown',
            description:
              'Agree the smallest group the tool will report on, and hold back any team, department or filter below it. Put the number in the launch communication so employees know it exists.',
          },
          {
            title: 'Watch combinations of filters',
            description:
              'A department that clears the threshold can fall below it once it is sliced by location, tenure or grade. Apply the minimum to every cut of the data, not only to the top-level teams.',
          },
          {
            title: 'Treat free-text answers with care',
            description:
              'Writing style, a named project or a specific incident can identify someone even in a large group. Decide who reads raw comments, and whether managers see themes rather than verbatim text.',
          },
          {
            title: 'Keep survey results out of individual records',
            description:
              'Survey answers should never be used as evidence about one person. In CLEAR Talent the performance record is built from 1-on-1s, check-ins, the journal and 360° feedback — and 360° raters are anonymised within their group, minimum-rater thresholds apply and managers see only aggregated group data.',
          },
        ],
      },
      {
        id: 'gdpr',
        eyebrow: 'GDPR handling',
        heading: 'GDPR for survey data and for the performance record',
        paragraphs: [
          'Survey responses that can be linked back to a person — directly, through an employee identifier the vendor holds, or by combining filters — are personal data under the GDPR, and pseudonymised data still counts. Only genuinely anonymous, aggregated results fall outside it. Before launch, ask your survey vendor for its data processing agreement, where responses are held, how long they are kept, and who on their side and yours can see identifiable data. This is general information, not legal advice; your data protection officer or counsel should confirm what applies to you.',
          'On the performance side, CLEAR Talent supports data subject access request exports and right-to-be-forgotten workflows, so a DSAR or an erasure request can be handled from the platform. Access is role-based across administrator, manager and employee roles, and sign-ins, exports, rating changes and consent records are audit-logged against the user responsible. CLEAR Talent runs on Google Cloud Platform; if you need data held in a particular region, regional requirements are agreed for each deployment, and a DPA is available on request.',
        ],
        cta: { text: 'Read the trust centre: GDPR, the DPA and hosting', href: '/trust' },
        variant: 'dark',
      },
      {
        id: 'sample-questions',
        eyebrow: 'Sample question sets',
        heading: 'Questions to use in your survey and in the follow-up',
        intro:
          'Illustrative starting points to adapt, not a validated survey instrument. The first set belongs in your survey tool; the other three are for the conversations and check-ins that follow, which is where CLEAR Talent comes in.',
        bullets: [
          {
            title: 'A five-question pulse survey (in your survey tool)',
            description:
              'Rate 1–5: “I understand how my work this month connects to my team’s goals.” “My workload over the last two weeks has been manageable.” “I have had a useful conversation about my development in the last month.” “I would feel comfortable raising a concern with my manager.” Open: “What one change would make next month easier?”',
          },
          {
            title: 'Following up a team result in a 1-on-1',
            description:
              '“The team said priorities feel unclear — does that match your experience?” “Which piece of your work is most affected?” “What would you change first?” “What do you need from me to make that happen?” “How will we know in a month whether it has improved?”',
          },
          {
            title: 'A monthly check-in',
            description:
              '“What went well since we last spoke?” “Where are you on each of your goals, and is any of them at risk?” “What is blocking you?” “What feedback have you had, or would you like?” “What is one thing to focus on before our next 1-on-1?”',
          },
          {
            title: 'Checking an action goal at quarter end',
            description:
              '“Did the change we agreed get made?” “What evidence do we have that it helped?” “Is the goal still the right one, or should it be revised or closed?” “Who should be recognised for the work?”',
          },
        ],
        variant: 'tinted',
        cta: { text: 'Get 100 example 360 feedback questions', href: '/blog/360-feedback-questions' },
      },
    ],
    faqs: [
      {
        q: 'Does CLEAR Talent include employee engagement or pulse surveys?',
        a: 'No. CLEAR Talent has no survey product. It covers the performance side — 1-on-1s, goal check-ins, development notes, kudos, a performance journal, anonymised 360° feedback and the formal review. If engagement or pulse surveys are a required part of your purchase, you will need a dedicated survey tool alongside it, or a platform that bundles both.',
      },
      {
        q: 'Can survey results be imported into CLEAR Talent?',
        a: 'Not through a connector, and survey responses are not something CLEAR Talent stores. Bulk import and API access cover people, organisation structure, goals and ratings. The usual route is the reverse: export goal and review data from CLEAR Talent as CSV or Excel and compare it with survey results for the same teams or departments, at a group size that keeps people anonymous.',
      },
      {
        q: 'What anonymity threshold should a pulse survey use?',
        a: 'Agree a minimum group size before launch, hold back any team or filter combination below it, and tell employees what the number is. Apply it to every cut of the data, not just top-level teams, and decide who can read free-text comments. CLEAR Talent applies the same principle to its own 360° feedback: raters are anonymised within their group and minimum-rater thresholds apply.',
      },
      {
        q: 'Are pulse survey responses personal data under the GDPR?',
        a: 'If a response can be linked to a person — directly, through an identifier the vendor holds, or by combining filters — it is personal data, and pseudonymised data still is. Only genuinely anonymous, aggregated results fall outside the GDPR. Ask your survey vendor for its DPA and retention terms. This is general information rather than legal advice.',
      },
      {
        q: 'What is the difference between a pulse survey and a check-in?',
        a: 'A pulse survey is a short, usually anonymous questionnaire about how a group feels about the organisation, read in aggregate. A check-in is a named conversation between a manager and one employee about their goals, blockers and development, kept on their record. Survey results tell a manager where to look; check-ins are where the follow-up happens.',
      },
    ],
    internalLinks: [
      { label: 'Continuous Performance Management', href: '/solutions/continuous-performance-management' },
      { label: '360° Feedback Software', href: '/solutions/360-degree-feedback-software' },
      { label: 'OKR & Goal Setting Software', href: '/solutions/okr-goal-setting-software' },
      { label: 'Employee Development Software', href: '/solutions/employee-development-software' },
      { label: 'Best 360 feedback tools compared', href: '/best-360-feedback-tools' },
      { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
      { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
      { label: 'Glossary: what is continuous feedback?', href: '/glossary/continuous-feedback' },
      { label: 'Guide: keeping a performance journal', href: '/blog/performance-journal-for-managers' },
      {
        label: 'Guide: GDPR subject access requests and performance reviews',
        href: '/blog/gdpr-subject-access-request-performance-reviews',
      },
      { label: 'Trust and security: GDPR, the DPA, hosting and AI governance', href: '/trust' },
    ],
    // What the sections and FAQs describe the software doing — no survey
    // capability, because there is none.
    schemaFeatures: [
      '1-on-1 scheduling with session tracking, combined summaries and suggested next steps',
      'Goal check-ins with at-risk flags from pace and check-in signals',
      'Development notes that can be kept private, and kudos tagged to competencies and values',
      'Performance journal for observations, achievements and incidents',
      'Anonymised 360° feedback with minimum-rater thresholds',
      'Role-based access, audit logging, data subject access request exports and right-to-be-forgotten workflows',
      'CSV and Excel export of goal and review data',
      'Check-ins and feedback on web, iOS and Android',
    ],
  },

  // ─────────────────────────────────────────────
  // 10. Performance Management for Healthcare
  // ─────────────────────────────────────────────
  {
    slug: 'performance-management-healthcare',
    category: 'industries',
    primaryKeyword: 'Performance Management Software for Healthcare',
    secondaryKeywords: [
      'healthcare HR software',
      'nurse performance management',
      'hospital performance review software',
      'clinical competency management',
      'healthcare talent management',
      'Joint Commission competency compliance',
    ],
    // This title dropped "Performance Management Software for Healthcare", the
    // page's primaryKeyword and H1. Its impressions have also fallen by about
    // half between two audit snapshots; the cause is not yet confirmed. Run the checks under "Diagnosing an impression drop on one URL" in
    // DEPLOYMENT.md before changing it again.
    seoTitle: 'Audit-Ready Healthcare Performance Software | CLEAR Talent',
    metaDescription:
      'Clinical competency sign-off, scheduled reassessment and a full audit trail on every rating. Shift-friendly reviews for nurses and allied health. Book a demo.',
    eyebrow: 'For Healthcare',
    h1: 'Performance Management Software Built for Healthcare',
    heroIntro:
      "Healthcare HR can't run on generic performance tools. Shift patterns, clinical competency validation, scheduled reassessment, and regulator-ready audit trails all need to be native to the platform — not workarounds. CLEAR Talent is purpose-configured for hospitals, health systems and care providers.",
    heroImage: '/screenshots/06-competencies-tab-library.webp',
    heroImageAlt:
      'CLEAR Talent Competency Library, Behavioral Indicators tab: observable behaviours for each competency at basic, intermediate and advanced levels, each with worked examples',
    heroStats: [
      { value: 'Audit-ready', label: 'Every rating logged' },
      { value: 'GCP', label: 'Enterprise cloud hosting' },
      { value: '24h', label: 'Signed file-URL TTL' },
    ],
    primaryCta: { text: 'Book a healthcare demo', href: '/demo?source=healthcare' },
    secondaryCta: {
      text: 'Download the healthcare competency library',
      href: '/contact?reason=healthcare-library&source=healthcare',
    },
    sections: [
      {
        id: 'different',
        eyebrow: 'Healthcare is different',
        heading: 'Healthcare performance management is different',
        paragraphs: [
          'Clinical staff rotate shifts. Competencies must be reassessed on a schedule set by accreditation bodies. Every rating must be signed off with an auditable trail.',
          'Generic HR software treats these as edge cases; CLEAR Talent treats them as the default.',
        ],
      },
      {
        id: 'clinical',
        eyebrow: 'Clinical competency',
        heading: 'Clinical competency validation, evidenced and signed off',
        intro:
          'Surveyors rarely ask whether your clinical staff are competent. They ask to see the evidence: dated, attributable, and reviewed by someone qualified to judge it. CLEAR Talent is where that evidence is created and kept, so an accreditation request becomes a search rather than a scramble.',
        paragraphs: [
          'To be clear about the boundary: CLEAR Talent is not a credentialling or primary source verification system. It covers what happens after someone is credentialled — ongoing competency assessment, the evidence behind each rating, and the record of who signed it off — and runs alongside whatever you use to verify licences and privileges.',
        ],
        bullets: [
          {
            title: 'Pre-built clinical libraries',
            description: 'Nursing, allied health, physician — fully editable starter sets.',
          },
          {
            title: 'Required by role, scoped by unit',
            description:
              'Each competency carries the roles that must hold it and the department it belongs to, so skills matrices read by ward, unit or department without a separate spreadsheet.',
          },
          {
            title: 'Peer and skip-level assessment',
            description:
              'Rotational staff, bank and agency cover, and cross-site working can be assessed by the people who actually observed the work — not only the line manager on the org chart.',
          },
        ],
        steps: [
          {
            title: 'Define the competency once',
            description:
              'Set its proficiency levels, the behaviours expected at each level, the departments it applies to and the roles that require it. A ward-based nurse and a theatre practitioner are held to different standards drawn from the same library.',
          },
          {
            title: 'Staff evidence it as they go',
            description:
              'Every entry records the situation, the action taken and the result, dated by month and tagged, with supporting files attached. The annual reassessment then reviews a year of evidence rather than the last fortnight.',
          },
          {
            title: 'The assessor rates and signs off',
            description:
              'Self-rating, assessor rating and final rating are held as separate values, each with its own comments. A preceptor or charge nurse can moderate a self-assessment without overwriting what the clinician originally recorded, and the sign-off is attributed and time-stamped.',
          },
          {
            title: 'Reassess on your accreditation calendar',
            description:
              'Each assessment period carries its own deadlines for self-assessment, assessor review, calibration and final rating, with automated reminders on whatever is outstanding. Set those dates to what your accreditation programme requires — annually, on role change, or after extended absence.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'shift-friendly',
        eyebrow: 'Mobile-first',
        heading: 'Shift-friendly review workflows',
        paragraphs: [
          'Reviews that can be completed in 10-minute windows between shifts. Mobile-first. Offline drafting. Single-tap feedback.',
        ],
      },
      {
        id: 'audit',
        eyebrow: 'Compliance',
        heading: 'An audit trail a surveyor can follow',
        intro:
          'Every rating submission, approval, status change and export is written to the audit log with the person responsible, the record they touched, the values before and after, the timestamp, and the address and device the request came from.',
        bullets: [
          {
            title: 'Before-and-after values, not just events',
            description:
              'A moderated rating keeps the original self-rating, the assessor rating and the final value side by side with their comments. When someone asks why a rating changed, the answer is in the record rather than in a mailbox.',
          },
          {
            title: 'Closed periods stay closed',
            description:
              'Goal setting, self-assessment, assessor review, calibration and final rating each open and close on the period dates you set. When a cycle closes, so does the window to quietly amend it.',
          },
          {
            title: 'Access is logged, not just edits',
            description:
              'Sign-ins, sign-outs and exports are recorded against the user who made them. Documents are served through signed links that expire after 24 hours, so a forwarded assessment link stops working on its own.',
          },
          {
            title: 'Evidence packs on demand',
            description:
              'One clinician for one period as a PDF, a whole department as a PDF, or competency history, calibration and team analytics as spreadsheets — ready for an internal auditor or an accreditation visit.',
          },
        ],
      },
      {
        id: 'security',
        eyebrow: 'Security & residency',
        heading: 'Security & data residency built for regulated industries',
        bullets: [
          {
            title: 'No patient data required',
            description: 'CLEAR Talent manages employee performance data — not PHI — reducing your compliance surface.',
          },
          { title: 'GDPR-ready', description: 'DSAR exports, right-to-be-forgotten workflows, and consent audit logs built in.' },
          { title: 'A+ security rating', description: 'Mozilla Observatory top score across all infrastructure.' },
          {
            title: 'Continuous monitoring',
            description: 'GCP Security Command Center Premium watches the perimeter 24/7.',
          },
        ],
        variant: 'dark',
      },
    ],
    faqs: [
      {
        q: 'Does CLEAR Talent handle patient data?',
        a: 'No — CLEAR Talent stores employee performance data, not PHI, which keeps it outside most HIPAA scopes. Our team will work through specifics with your compliance function.',
      },
      {
        q: 'Does it support Joint Commission competency reassessment schedules?',
        a: 'You set the reassessment cadence, the deadlines for each stage and the sign-off required, so the platform can be configured to whatever your accreditation programme calls for — including annual clinical competency reassessment. CLEAR Talent runs the process and holds the evidence; it does not certify your compliance.',
      },
      {
        q: 'Does CLEAR Talent replace our credentialling system?',
        a: 'No. Credentialling systems verify licences, qualifications and privileges. CLEAR Talent covers what happens afterwards — ongoing competency assessment, the evidence behind each rating, development plans and the audit trail — and runs alongside your credentialling platform rather than duplicating it.',
      },
      {
        q: 'Does it work for multi-site health systems?',
        a: 'Yes — organisation-wide view with unit/department scoping.',
      },
      {
        q: 'Can clinicians complete reviews on mobile between shifts?',
        a: 'Yes — mobile-first UX with offline draft.',
      },
      {
        q: 'Where is our data stored?',
        a: 'On Google Cloud Platform enterprise infrastructure with global data centres. Talk to us about regional requirements for enterprise deployments.',
      },
    ],
    internalLinks: [
      { label: 'Competency Management Software', href: '/solutions/competency-management-software' },
      { label: 'Performance Management Software', href: '/solutions/performance-management-software' },
      {
        label: 'Guide: nurse and clinical competency assessment',
        href: '/blog/nurse-clinical-competency-assessment',
      },
      {
        label: 'Walkthrough: rolling this out in a hospital group',
        href: '/in-practice/healthcare-performance-management-rollout',
      },
      { label: 'Talk to a healthcare specialist', href: '/contact?reason=healthcare' },
    ],
    /**
     * This page, the homepage and competency management between them carry most
     * of the site's search impressions, and this one earns more than any other
     * page with no clicks at all. Its snippet was rewritten, but its
     * SoftwareApplication node still fell back to the keyword list — which put
     * "Joint Commission competency compliance" into structured data as a product
     * feature, on a page whose own FAQ says CLEAR Talent does not certify
     * compliance.
     *
     * Every entry is something the sections and FAQs above describe.
     */
    schemaFeatures: [
      'Editable starter competency libraries for nursing, allied health and physician roles',
      'Clinical competencies required by role and scoped by ward, unit or department',
      'Peer and skip-level assessment for rotational, bank and agency staff',
      'Competency evidence recorded as it happens, with supporting files attached',
      'Separate self, assessor and final ratings with attributed, time-stamped sign-off',
      'Scheduled reassessment periods with stage deadlines and automated reminders',
      'Audit log of rating submissions, approvals and status changes, with before-and-after values',
      'Sign-in and export logging, with signed document links that expire after 24 hours',
      'PDF and spreadsheet evidence packs for internal audit and accreditation visits',
      'Mobile-first review workflows with offline drafting for shift-based staff',
      'Organisation-wide view across sites with unit and department scoping',
    ],
  },

  // ─────────────────────────────────────────────
  // 11. Performance Management for Financial Services
  // ─────────────────────────────────────────────
  {
    slug: 'performance-management-financial-services',
    category: 'industries',
    primaryKeyword: 'Performance Management Software for Financial Services',
    secondaryKeywords: [
      'banking HR software',
      'financial services performance reviews',
      'conduct and competence software',
      'regulated performance management',
      'insurance performance management software',
      'evidence-based competency assessment',
    ],
    seoTitle: 'Financial Services Performance Management | CLEAR Talent',
    metaDescription:
      'Performance management for banks, insurers and asset managers: evidence-based competency records, audit-logged reviews and role-based access. Book a demo.',
    eyebrow: 'For Financial Services',
    h1: 'Performance Management Software for Financial Services',
    heroIntro:
      "In financial services a performance rating is not just a conversation — it is a record. It has to hold up under a compliance review, a works-council challenge, or a question about how you concluded someone was competent, sometimes years later. CLEAR Talent keeps the evidence attached to the rating from the moment it is made.",
    heroImage: '/screenshots/07-reports-tab-organization.webp',
    heroImageAlt:
      'CLEAR Talent Reports & Analytics, Department tab: pick a department to generate a report with a department overview, team comparisons and strategic insights',
    heroStats: [
      { value: 'Audit-logged', label: 'Every rating & sign-off' },
      { value: 'Evidence-based', label: 'Competency ratings' },
      { value: 'Role-based', label: 'Access & permissions' },
    ],
    primaryCta: {
      text: 'Book a financial services demo',
      href: '/demo?source=financial-services',
    },
    secondaryCta: {
      text: 'Talk through your control requirements',
      href: '/contact?reason=financial-services&source=financial-services',
    },
    sections: [
      {
        id: 'rating-as-record',
        eyebrow: 'Why this is different',
        heading: 'In a regulated firm, a rating is a record',
        paragraphs: [
          'Most performance tools are designed around the conversation. In banking, insurance and asset management the conversation is only half the job — the other half is being able to show, later and to someone sceptical, what the judgement was based on.',
          'CLEAR Talent captures that evidence at the point of assessment instead of reconstructing it during an audit. Competency ratings can require observable behavioural evidence, goal scores trace back to the KPI that produced them, and every change is logged with the user and timestamp behind it.',
        ],
      },
      {
        id: 'competence-evidence',
        eyebrow: 'Competence, evidenced',
        heading: 'Assess competence against a published standard',
        bullets: [
          {
            title: 'Role profiles per function',
            description:
              'Define the competencies each role genuinely requires, with behavioural indicators at every proficiency level, then reuse that profile across assessments, reviews and promotion decisions.',
          },
          {
            title: 'Scheduled reassessment',
            description:
              'Set the cadence your compliance function requires and let the platform issue the reminders, rather than tracking renewal dates in a side spreadsheet.',
          },
          {
            title: 'Sign-off workflows',
            description:
              'Configurable approval chains route each assessment to the reviewer and the approver who has to stand behind it.',
          },
          {
            title: 'Immutable audit log',
            description:
              'Every rating, comment, approval and file access is recorded — the trail exists whether or not anyone ever asks for it.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'balanced-scorecard',
        eyebrow: 'Balance',
        heading: 'Weigh the numbers against the behaviours',
        paragraphs: [
          'A scorecard weighted entirely towards revenue teaches people what the firm actually rewards. CLEAR Talent lets you weight the two streams deliberately: goals and KPIs on one side, competency assessment on the other, combining into a single composite rating.',
          'The KPI library holds reusable, role-aware metric templates with performance bands and guardrails, so "exceeded target" means the same thing across the business. Weightings are configurable per level, so a control function and a front-office desk can sit on the same platform without being measured the same way.',
        ],
      },
      {
        id: 'calibration',
        eyebrow: 'Defensibility',
        heading: 'Calibration your second line can interrogate',
        paragraphs: [
          'Calibration sessions bring managers to a common standard before ratings go final. Reviewers work from what is already in the platform — goal scores, competency ratings with their evidence, and structured 360° input — rather than from memory and advocacy.',
          'Individual, team and organisation-level reports export to PDF or Excel in one click, so risk, compliance and remuneration committees review the same underlying data HR is working from.',
        ],
        cta: { text: 'Read the calibration guide', href: '/blog/performance-calibration-session-guide' },
      },
      {
        id: 'security',
        eyebrow: 'Security review',
        heading: 'Built for firms that send a security questionnaire first',
        bullets: [
          {
            title: 'Employee data only',
            description:
              'CLEAR Talent holds performance and development data. It is not a payroll engine or a system of record for customer data, which keeps it out of the most sensitive parts of your estate.',
          },
          {
            title: 'Encrypted in transit',
            description:
              'End-to-end TLS, with generated files served through signed URLs that expire after 24 hours.',
          },
          {
            title: 'Enterprise cloud hosting',
            description:
              'Google Cloud Platform infrastructure with continuous threat monitoring through GCP Security Command Center Premium.',
          },
          {
            title: 'A+ security rating',
            description: 'Top Mozilla Observatory score across the platform, on every plan, by default.',
          },
        ],
        variant: 'dark',
      },
    ],
    faqs: [
      {
        q: 'How do we evidence that someone is competent for a regulated role?',
        a: 'Build a role profile holding the competencies the role requires with behavioural indicators at each level, then assess against it on the cadence you set. Ratings can require written evidence, assessments route through sign-off, and the audit log records every step. CLEAR Talent supplies the mechanism; your compliance function decides which roles and how often.',
      },
      {
        q: 'Does CLEAR Talent replace our HRIS or payroll system?',
        a: 'No. It is a specialist performance and development layer that runs alongside your existing system of record, with API access plus CSV and Excel bulk import and export for employee and organisation data.',
      },
      {
        q: 'Can we stop revenue targets dominating the overall rating?',
        a: 'Yes. Goal and KPI weightings are configurable per level, and competency assessment is a separate stream in the composite rating — so conduct and capability carry the weight you decide rather than whatever is easiest to measure.',
      },
      {
        q: 'Can different entities and regions run different frameworks?',
        a: 'Yes. Departments, locations, roles and reporting lines are first-class concepts with multi-entity support, and competency scales can differ by job family. The platform supports 16 languages for teams spread across markets.',
      },
      {
        q: "Who can see an employee's ratings?",
        a: 'Access is role-based across administrator, manager and employee roles, with ownership checks on individual records and audit logging on access. Managers work within their own reporting line.',
      },
      {
        q: 'Can we produce the full history for an audit?',
        a: 'Yes. The audit log records every rating, comment and approval with the user and timestamp behind it, and individual, team and organisation reports export to PDF or Excel in one click.',
      },
    ],
    internalLinks: [
      { label: 'Competency Management Software', href: '/solutions/competency-management-software' },
      { label: 'Employee Performance Review Software', href: '/solutions/employee-performance-review-software' },
      { label: 'OKR & Goal Setting Software', href: '/solutions/okr-goal-setting-software' },
      { label: 'Performance Management Software', href: '/solutions/performance-management-software' },
      {
        label: 'Walkthrough: rolling this out in a regulated firm',
        href: '/in-practice/financial-services-performance-management-rollout',
      },
      { label: 'Talk to a financial services specialist', href: '/contact?reason=financial-services' },
    ],
  },

  // ─────────────────────────────────────────────
  // 12. Performance Management for Technology Companies
  // ─────────────────────────────────────────────
  {
    slug: 'performance-management-technology-companies',
    category: 'industries',
    primaryKeyword: 'Performance Management Software for Technology Companies',
    secondaryKeywords: [
      'engineering performance management',
      'software company performance reviews',
      'engineering career ladder software',
      'tech company OKR software',
      'performance management for startups',
      'engineering levelling framework',
    ],
    seoTitle: 'Performance Management for Tech Companies | CLEAR Talent',
    metaDescription:
      'Performance management for engineering and product teams: quarterly OKRs, skills-based career ladders, 360° feedback and fair calibration. Book a demo.',
    eyebrow: 'For Technology',
    h1: 'Performance Management Software for Technology Companies',
    heroIntro:
      'Most engineering organisations run two performance systems: a levelling framework that lives in a wiki, and a review cycle that lives somewhere else entirely. CLEAR Talent puts them in the same place — so the ladder that defines a senior engineer is the ladder people are actually assessed and promoted against.',
    heroImage: '/screenshots/05-roles-main.webp',
    heroImageAlt:
      'CLEAR Talent Roles & Departments screen showing role profiles with their department, role level and number of required competencies',
    heroStats: [
      { value: 'Quarterly', label: 'Or annual goal cycles' },
      { value: 'Role-specific', label: 'Competency scales' },
      { value: 'Web + mobile', label: 'iOS & Android access' },
    ],
    primaryCta: { text: 'Book a technology demo', href: '/demo?source=technology' },
    secondaryCta: { text: 'See the platform tour', href: '/showcase' },
    sections: [
      {
        id: 'ladder',
        eyebrow: 'The ladder',
        heading: 'The career ladder and the review, in one system',
        paragraphs: [
          'Engineering ladders usually start well and then drift. The document describing what a staff engineer does gets refined in one place while promotion decisions get argued in another, and after two reorganisations nobody is confident the two still agree.',
          'CLEAR Talent holds the ladder as role profiles: the competencies each level requires, with behavioural indicators describing what that level looks like in practice. Assessments run against the profile, so a promotion case becomes a comparison against a published standard rather than a persuasive document.',
        ],
      },
      {
        id: 'cadence',
        eyebrow: 'Cadence',
        heading: 'Quarterly goals, annual decisions',
        bullets: [
          {
            title: 'Quarterly and annual cycles side by side',
            description:
              'Run OKRs on a quarterly rhythm and keep the annual cycle for compensation and promotion. Goal scores roll up automatically instead of being retyped at year end.',
          },
          {
            title: 'Enforced linkage',
            description:
              'Strategic Linked Goals requires every individual goal to connect to a parent objective, so a squad cannot quietly optimise for something the company stopped asking for.',
          },
          {
            title: 'Weighted priorities',
            description:
              'Configurable weightings and automatic scoring make it explicit which goal matters most, instead of leaving five equal-looking objectives on a page.',
          },
          {
            title: 'Context between cycles',
            description:
              'Continuous feedback and structured check-ins keep the discussion attached to the goal while it is still live.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'multi-rater',
        eyebrow: 'Peer input',
        heading: 'Peer feedback engineers take seriously',
        paragraphs: [
          'Engineering teams already run informal peer review; the hard part is turning it into something comparable. CLEAR Talent structures 360° input against the competencies in the role profile, with configurable rater groups covering managers, peers, direct reports and cross-functional stakeholders.',
          'Raters are anonymised within their group and minimum-rater thresholds apply, so feedback stays candid and the output is a prioritised set of strengths and development areas rather than a wall of quotes.',
        ],
      },
      {
        id: 'levelling',
        eyebrow: 'Consistency',
        heading: 'Level consistently across squads',
        paragraphs: [
          'The same title means different things to different managers, and nobody notices until promotion season. Calibration sessions bring managers to a shared standard, working from the goal scores, evidence-backed competency ratings and 360° input already held in the platform.',
          'Ava assembles that evidence into a first-draft narrative and summarises multi-rater feedback into themes — which removes the blank page without removing the decision. Every AI output is labelled, editable and approved by a human, and every edit is logged.',
        ],
        cta: { text: 'See how Ava assists reviews', href: '/solutions/ai-performance-management' },
      },
      {
        id: 'distributed',
        eyebrow: 'Distributed teams',
        heading: 'Built for teams spread across time zones',
        bullets: [
          {
            title: 'Web, iOS and Android',
            description:
              'Reviews, feedback and check-ins from a laptop or a phone, with biometric sign-in, push notifications and offline support.',
          },
          {
            title: '16 languages',
            description:
              'AI-generated feedback, summaries and recommendations reach each person in the language they prefer to work in.',
          },
          {
            title: 'An org chart that keeps up',
            description:
              'Departments, locations, roles and reporting lines are first-class concepts, with bulk import and staff transfers for when the structure changes again.',
          },
          {
            title: 'Multi-entity support',
            description:
              'Run several legal entities on one platform — useful for groups that have grown by acquisition as well as by hiring.',
          },
        ],
      },
    ],
    faqs: [
      {
        q: 'Can we run separate IC and management tracks?',
        a: 'Yes. Competencies map to roles, each role carries its own required set and target levels, and scales are configurable from three to seven levels — so an individual-contributor ladder and a management ladder can sit side by side with genuinely different expectations.',
      },
      {
        q: 'Do you support quarterly OKRs alongside an annual review?',
        a: 'Yes. Cadences can be mixed per department or level, and goal scores from each quarter roll up into the annual review automatically.',
      },
      {
        q: 'What happens to goals and assessments when we reorganise?',
        a: 'Reporting lines, departments and locations are first-class concepts, and bulk import plus staff transfers keep the structure current without rebuilding it. Historical assessments stay in the employee record and the audit log captures the change.',
      },
      {
        q: 'Does Ava write the review for us?',
        a: 'Ava produces a first draft from the goal scores, competency ratings and feedback that already exist, and summarises 360° input into themes. Managers accept, amend or reject it — every output is labelled and editable, and every edit is logged.',
      },
      {
        q: 'Will this fit alongside our existing HR stack?',
        a: 'CLEAR Talent runs alongside your HRIS rather than replacing it, with API access plus CSV and Excel bulk import and export for employee and organisation data. Enterprise integrations are scoped per rollout.',
      },
    ],
    internalLinks: [
      { label: 'OKR & Goal Setting Software', href: '/solutions/okr-goal-setting-software' },
      { label: 'Competency Management Software', href: '/solutions/competency-management-software' },
      { label: '360° Feedback Software', href: '/solutions/360-degree-feedback-software' },
      { label: 'Continuous Performance Management', href: '/solutions/continuous-performance-management' },
      { label: 'Guide: cascading company goals', href: '/blog/how-to-cascade-company-goals' },
      {
        label: 'Walkthrough: moving an engineering ladder off the wiki',
        href: '/in-practice/engineering-performance-management-rollout',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 13. Performance Management for Retail
  // ─────────────────────────────────────────────
  {
    slug: 'performance-management-retail',
    category: 'industries',
    primaryKeyword: 'Performance Management Software for Retail',
    secondaryKeywords: [
      'retail HR software',
      'store performance management',
      'frontline performance reviews',
      'multi-site performance management',
      'deskless workforce performance software',
      'retail competency framework',
    ],
    seoTitle: 'Performance Management for Retail Teams | CLEAR Talent',
    metaDescription:
      'Performance management built for store teams: mobile reviews between shifts, role-based competencies, multi-site reporting and clear goals. Book a demo.',
    eyebrow: 'For Retail',
    h1: 'Performance Management Software for Retail Teams',
    heroIntro:
      'Retail performance management usually fails for practical reasons rather than philosophical ones. Colleagues have no desk and no company laptop, shifts end when the store closes, area managers cover a dozen sites, and head office still needs one consistent standard across all of them. CLEAR Talent is configured for that reality.',
    heroImage: '/screenshots/11-teams-main.webp',
    heroImageAlt:
      'CLEAR Talent admin dashboard showing counts of competencies, role profiles, active goals and IDPs, with quick actions for the competency library, role profiles, goal suggestions, skill gap analysis and development plans',
    heroStats: [
      { value: 'Mobile', label: 'iOS & Android for store teams' },
      { value: 'Offline', label: 'Works when the signal drops' },
      { value: 'Multi-site', label: 'Locations & reporting lines' },
    ],
    primaryCta: { text: 'Book a retail demo', href: '/demo?source=retail' },
    secondaryCta: {
      text: 'Talk to us about your store network',
      href: '/contact?reason=retail&source=retail',
    },
    sections: [
      {
        id: 'frontline',
        eyebrow: 'The frontline problem',
        heading: 'A process built for a desk excludes most of your workforce',
        paragraphs: [
          'If completing an assessment requires a laptop, a login nobody remembers and forty uninterrupted minutes, it will happen in head office and almost nowhere else. The result is a performance record that is thorough for the people who need it least.',
          'CLEAR Talent puts the same process on a phone: native iOS and Android apps with biometric sign-in, push notifications when something needs attention, and offline support for stockrooms and sites where the signal drops.',
        ],
      },
      {
        id: 'consistency',
        eyebrow: 'One standard',
        heading: 'The same standard in every store',
        bullets: [
          {
            title: 'A central competency library',
            description:
              'Define each competency once, with behavioural indicators at every proficiency level, then reuse it across every store, region and role.',
          },
          {
            title: 'Role profiles per position',
            description:
              'Sales assistant, supervisor, store manager, distribution and head office each get their own required competencies and target levels, built from reusable templates.',
          },
          {
            title: 'AI-assisted framework generation',
            description:
              'Ava drafts role-specific frameworks from your role and organisation context, ready for your team to edit and approve.',
          },
          {
            title: 'Evidence-based ratings',
            description:
              'Assessments are backed by observable behaviour rather than gut feel — which is what makes a promotion decision explainable to the colleague who did not get it.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'goals-kpis',
        eyebrow: 'Goals & KPIs',
        heading: 'Targets that actually reach the shop floor',
        paragraphs: [
          'Strategic Linked Goals cascades an organisational objective down through region, store and individual, and every individual goal has to connect to a parent — so nobody spends a quarter working towards something the business moved on from.',
          'The KPI library holds reusable, role-aware metric templates with configurable weightings, performance bands and guardrails, plus evidence linking so every score has a paper trail. Quarterly and annual cycles run side by side, which matters when the year has a peak.',
        ],
      },
      {
        id: 'visibility',
        eyebrow: 'Visibility',
        heading: 'See the whole estate, and one store',
        paragraphs: [
          'Reports run at individual, team and organisation level, with AI-generated summaries that turn ratings, evidence and feedback into a narrative rather than another spreadsheet. One click exports to PDF or Excel.',
          'Area and regional managers see their own patch through role-based access; head office sees the network. Both are reading the same underlying assessments, which is what makes a conversation about an underperforming site productive.',
        ],
      },
      {
        id: 'churn',
        eyebrow: 'A workforce that changes',
        heading: 'Built for seasonal peaks and constant movement',
        bullets: [
          {
            title: 'Bulk import and staff transfers',
            description:
              'Keep the organisation structure current through seasonal hiring and internal moves without rebuilding it each time.',
          },
          {
            title: 'Sites modelled properly',
            description:
              'Locations, departments and reporting lines are first-class concepts, with multi-entity support for groups running more than one trading brand.',
          },
          {
            title: 'Early-warning risk signals',
            description:
              'Ava surfaces attrition and performance risk signals, so a regional manager knows which store to visit next.',
          },
          {
            title: 'Colleagues in their own language',
            description:
              'Sixteen languages, including English, Spanish, French, German, Portuguese, Filipino, Chinese, Japanese, Korean, Arabic and Hindi.',
          },
        ],
        variant: 'dark',
      },
    ],
    faqs: [
      {
        q: 'Can store colleagues complete reviews without a company laptop?',
        a: 'Yes. The iOS and Android apps cover assessments, feedback and one-to-one preparation, with biometric sign-in, push notifications and offline support for when the signal drops.',
      },
      {
        q: 'How do we keep standards consistent across hundreds of sites?',
        a: 'Competencies are defined once in a central library and applied through role profiles, so the same position is assessed against the same behavioural indicators and the same target levels in every store.',
      },
      {
        q: 'Can store, distribution and head office use different frameworks?',
        a: 'Yes. Competency scales can differ by job family and each role carries its own required set, so a shop-floor role and a head-office role are measured properly on the same platform.',
      },
      {
        q: 'Does it work in the languages our colleagues speak?',
        a: 'CLEAR Talent supports 16 languages, and AI-generated feedback, summaries and recommendations are delivered in the language each employee prefers.',
      },
      {
        q: 'What does an area manager see compared with head office?',
        a: 'Access is role-based, so managers work within their own reporting line while head office gets organisation-level reporting. Individual, team and organisation reports export to PDF or Excel.',
      },
      {
        q: 'How does this cope with seasonal hiring?',
        a: 'Bulk import and staff transfers keep the organisation structure current as people join, move and leave, and role profiles make expectations explicit from the first shift rather than at the first review.',
      },
    ],
    internalLinks: [
      { label: 'Competency Management Software', href: '/solutions/competency-management-software' },
      { label: 'Employee Performance Review Software', href: '/solutions/employee-performance-review-software' },
      { label: 'Continuous Performance Management', href: '/solutions/continuous-performance-management' },
      { label: 'Performance Management Software', href: '/solutions/performance-management-software' },
      {
        label: 'Walkthrough: rolling this out to a store network',
        href: '/in-practice/retail-performance-management-rollout',
      },
      { label: 'Talk to a retail specialist', href: '/contact?reason=retail' },
    ],
  },

  // ─────────────────────────────────────────────
  // 14. Performance Management Software for Ireland and Europe
  // ─────────────────────────────────────────────
  // Every claim here is one the site already makes elsewhere: GDPR tooling from
  // the healthcare page and /integrations, the DPA from the performance
  // management FAQ, hosting from the security sections, languages from the
  // technology and retail pages, the AI posture from the AI page. Hosting is
  // stated as Google Cloud Platform with regional requirements discussed per
  // deployment — the repository records no EU-region commitment, so the page
  // makes none. Prices are USD because that is the only published price list.
  // There is no Irish or EU customer story to cite, so the page cites none.
  {
    slug: 'performance-management-software-europe',
    category: 'solutions',
    primaryKeyword: 'Performance Management Software for Ireland and Europe',
    secondaryKeywords: [
      'performance management software Ireland',
      'GDPR compliant performance management software',
      'EU performance management software',
      'performance review software Europe',
      'multilingual performance management',
      'works council performance management',
    ],
    seoTitle: 'GDPR-Ready Performance Management for Europe | CLEAR Talent',
    metaDescription:
      'Performance management software for Irish and EU HR teams: DSAR and erasure workflows, a DPA, 16 languages and AI that keeps a human in charge. Book a demo.',
    eyebrow: 'For Ireland & Europe',
    h1: 'Performance Management Software for Irish and European HR Teams',
    heroIntro:
      'For an HR team in Ireland or elsewhere in the EU, choosing performance software is partly a data-protection decision. Your DPO will ask where the data lives and how a subject access request is answered; in some countries the works council will ask what managers can see before anything goes live. CLEAR Talent gives you straight answers to those questions, and a review process your people can complete in the language they work in.',
    heroImage: '/screenshots/07-reports-main.webp',
    heroImageAlt:
      'CLEAR Talent Reports & Analytics screen with individual, team, department and executive summary reports, filtered by staff member and date range',
    heroStats: [
      { value: '16', label: 'Languages for AI-generated content' },
      { value: 'DSAR', label: 'Exports & erasure workflows' },
      { value: 'A+', label: 'Security rating' },
    ],
    primaryCta: { text: 'Book a demo', href: '/demo?source=europe' },
    secondaryCta: { text: 'See plans and pricing', href: '/pricing' },
    sections: [
      {
        id: 'why-regional',
        eyebrow: 'The European buying question',
        heading: 'In Europe, the review process is also a data-processing activity',
        paragraphs: [
          'Ratings, written feedback, development notes and 360° comments are personal data about identifiable employees, and in Ireland and across the EU the GDPR applies to all of it. That changes what a sensible evaluation looks like. Before anyone asks whether the review form is pleasant to use, someone will ask what the lawful basis is, who can see what, how long records are kept and how an employee gets a copy.',
          'CLEAR Talent is a specialist performance and development platform that runs alongside your HRIS. It holds performance data — goals, competency assessments, reviews, feedback and development plans — not payroll, and it was built with the data-rights side of that record in mind rather than retrofitted.',
        ],
      },
      {
        id: 'gdpr',
        eyebrow: 'GDPR',
        heading: 'GDPR tooling built into the platform',
        intro:
          'A data protection programme is yours to run; the software should make the parts that touch performance data straightforward. These are the controls CLEAR Talent provides on every plan.',
        bullets: [
          {
            title: 'Subject access requests from the platform',
            description:
              'Data subject access request exports let HR answer a DSAR from the system rather than searching review forms, inboxes and spreadsheets by hand.',
          },
          {
            title: 'Right-to-be-forgotten workflows',
            description:
              'Erasure requests are handled through a defined workflow, so they are dealt with consistently instead of depending on who happens to pick them up.',
          },
          {
            title: 'A data processing agreement',
            description:
              'DPAs are available, so the processor relationship is documented before any employee data is loaded.',
          },
          {
            title: 'Audit logs on access as well as edits',
            description:
              'Rating changes, approvals, sign-ins, exports and consent records are logged against the user responsible, with the before-and-after values on every change.',
          },
        ],
        variant: 'tinted',
      },
      {
        id: 'hosting',
        eyebrow: 'Hosting & security',
        heading: 'Where the data lives, and how it is protected',
        paragraphs: [
          'CLEAR Talent runs on Google Cloud Platform infrastructure, with continuous threat monitoring through GCP Security Command Center Premium, end-to-end TLS encryption and an A+ Mozilla Observatory score. Documents and exports are served through signed links that expire after 24 hours, so a forwarded file stops working on its own.',
          'If your organisation needs employee data held in a particular region, such as the EU, raise it at the start of the conversation. Regional requirements are agreed with the team for each deployment, and you should get that answer in writing alongside the DPA rather than take it from a web page.',
        ],
        variant: 'dark',
      },
      {
        id: 'works-councils',
        eyebrow: 'Employee representatives',
        heading: 'What to bring to a works council or employee forum',
        intro:
          'In several EU countries, Germany and the Netherlands among them, introducing a system that evaluates employee performance can require consultation with, or the agreement of, the works council before it goes live. Irish employers may have information and consultation arrangements of their own. Your employment counsel will tell you what applies; the platform makes the answers to the usual questions concrete.',
        bullets: [
          {
            title: 'Who sees what',
            description:
              'Access is role-based across administrator, manager and employee roles, with ownership checks on individual records. Managers work within their own reporting line.',
          },
          {
            title: 'Anonymous means anonymous',
            description:
              '360° raters are anonymised within their group and minimum-rater thresholds apply, so nobody can work out who wrote a comment from a group of one.',
          },
          {
            title: 'People decide, not the AI',
            description:
              'Every AI output is labelled, editable and approved by a human, every edit is logged, and AI can be switched off per cycle, per team or per module.',
          },
          {
            title: 'Closed cycles stay closed',
            description:
              'Each stage — goal setting, self-assessment, manager review, calibration and final rating — opens and closes on the dates you set, so a rating cannot be quietly amended after the cycle ends.',
          },
        ],
      },
      {
        id: 'languages',
        eyebrow: 'Multilingual teams',
        heading: 'One review process across 16 languages',
        paragraphs: [
          'A European workforce rarely shares a first language, and a review written in someone’s second language is a weaker record of what they actually think. CLEAR Talent delivers AI-generated feedback, summaries and recommendations in 16 languages — including English, Spanish, French, German and Portuguese — in the language each employee prefers.',
          'The same process runs on the web and in native iOS and Android apps, with biometric sign-in, push notifications and offline support, so colleagues on a site, in a store or on the road complete the same cycle as head office. If particular screens need to appear in a particular language for your rollout, ask to see them in the demo.',
        ],
        variant: 'tinted',
      },
      {
        id: 'ai-act',
        eyebrow: 'EU AI Act',
        heading: 'AI assistance that keeps managers accountable',
        paragraphs: [
          'The EU AI Act treats AI used to evaluate workers’ performance as a high-risk use, which puts human oversight, transparency and record-keeping at the centre of how HR should use it. Ava, the AI assistant built into CLEAR Talent, was designed around those principles: she drafts review narratives from the evidence already in the platform and summarises 360° feedback, while the manager writes and owns the review.',
          'Your employees’ data is never used to train models for other customers, and Ava runs on isolated tenants. CLEAR Talent does not certify your compliance with the Act; the team will work through specifics with your compliance function.',
        ],
        cta: { text: 'See how Ava keeps people in charge', href: '/solutions/ai-performance-management' },
      },
      {
        id: 'ireland',
        eyebrow: 'For Irish employers',
        heading: 'A performance record that stands up to fair procedures',
        paragraphs: [
          'When an Irish employer manages underperformance, the process is judged on fair procedures as much as on the outcome: whether the expected standard was clear, whether the employee knew about the shortfall and whether they had support and time to improve. That depends on a dated record built up across the year, not notes assembled after the decision.',
          'CLEAR Talent is not a disciplinary or PIP system, and the formal case file belongs in your HR process. What it holds is the evidence that file relies on — goals and check-ins, competency ratings with the behaviour behind them, 1-on-1 development notes and a performance journal — and, as the EU Pay Transparency Directive asks employers to explain pay progression with objective, gender-neutral criteria, the documented reasoning behind each rating.',
        ],
        cta: { text: 'Read the guide to managing underperformance in Ireland', href: '/blog/managing-underperformance-ireland' },
      },
      {
        id: 'pricing',
        eyebrow: 'Pricing',
        heading: 'A published price list, before you talk to sales',
        paragraphs: [
          `CLEAR Talent publishes its plans. The ${BASIC_TIER.name} plan is USD ${BASIC_TIER.monthlyUsd} per month for ${BASIC_TIER.includedAdmins} admin user and ${BASIC_TIER.includedStaff} staff users; the ${PRO_TIER.name} plan is USD ${PRO_TIER.monthlyUsd} per month for ${PRO_TIER.includedAdmins} admin users and ${PRO_TIER.includedStaff} staff users, with published rates for each additional user. Annual billing saves ${ANNUAL_DISCOUNT_PERCENT}%, there is a ${TRIAL_POLICY.toLowerCase()}, and larger organisations move to a quoted Enterprise plan.`,
          'Prices are published in US dollars, and there is no separate euro price list. If your finance team needs to budget or be invoiced in euro, raise it with the team before you commit.',
        ],
        cta: { text: 'Compare the plans', href: '/pricing' },
      },
    ],
    faqs: [
      {
        q: 'Is CLEAR Talent GDPR compliant?',
        a: 'CLEAR Talent provides the controls a GDPR programme needs for performance data: data subject access request exports, right-to-be-forgotten workflows, consent audit logs, role-based access and a data processing agreement. Compliance itself also depends on how you configure and use the platform, so your DPO should review the DPA and settings as part of the evaluation.',
      },
      {
        q: 'Where is our employee data hosted?',
        a: 'On Google Cloud Platform enterprise infrastructure, monitored around the clock by GCP Security Command Center Premium. If you need data held in a specific region, such as the EU, tell the team at the start — regional requirements are agreed for each deployment and confirmed alongside the DPA.',
      },
      {
        q: 'Which languages does CLEAR Talent support?',
        a: 'AI-generated feedback, summaries and recommendations are delivered in 16 languages, including English, Spanish, French, German and Portuguese, in the language each employee prefers. If specific screens need to appear in a specific language for your rollout, ask to see them in the demo.',
      },
      {
        q: 'Will we need works council approval to introduce it?',
        a: 'That depends on the country and on your existing agreements: in some EU countries, including Germany and the Netherlands, a performance evaluation system can require works council consultation or consent. Take advice from your employment counsel. CLEAR Talent helps by making access rules, 360° anonymity thresholds, AI controls and audit logging specific enough to put in front of employee representatives.',
      },
      {
        q: 'How does Ava fit with the EU AI Act?',
        a: 'Ava drafts and summarises; managers decide. Every AI output is labelled, editable and approved by a person, every edit is logged, and AI can be switched off per cycle, team or module. CLEAR Talent does not certify your compliance, and the team will work through specifics with your compliance function.',
      },
      {
        q: 'Can we pay in euro?',
        a: `Published prices are in US dollars — the ${BASIC_TIER.name} plan starts at USD ${BASIC_TIER.monthlyUsd} per month, with a ${TRIAL_POLICY.toLowerCase()}. There is no separate euro price list, so if you need to budget or be invoiced in euro, raise it with the team before you commit.`,
      },
    ],
    internalLinks: [
      { label: 'AI Performance Management Software', href: '/solutions/ai-performance-management' },
      { label: 'Integrations, imports and DSAR exports', href: '/integrations' },
      { label: 'Trust and security: GDPR, the DPA, hosting and AI governance', href: '/trust' },
      { label: 'Plans and pricing', href: '/pricing' },
      {
        label: 'Guide: the EU Pay Transparency Directive and performance reviews',
        href: '/blog/pay-transparency-directive-performance-reviews',
      },
      {
        label: 'Guide: managing underperformance in Ireland',
        href: '/blog/managing-underperformance-ireland',
      },
      { label: 'Talk to the team about your requirements', href: '/contact?reason=europe' },
    ],
    schemaFeatures: [
      'Data subject access request exports',
      'Right-to-be-forgotten workflows',
      'Consent audit logs and audit logging of sign-ins, exports and rating changes',
      'Role-based access across administrator, manager and employee roles',
      'Anonymised 360° feedback with minimum-rater thresholds',
      'Human-approved AI drafting and summaries, switchable per cycle, team or module',
      'AI-generated feedback, summaries and recommendations in 16 languages',
      'Web, iOS and Android access with biometric sign-in and offline support',
      'Signed document links that expire after 24 hours',
    ],
    areaServed: [
      { '@type': 'Country', name: 'Ireland' },
      { '@type': 'AdministrativeArea', name: 'European Union' },
    ],
    dateModified: '2026-10-03',
  },
];

export const SOLUTION_PAGE_BY_SLUG: Record<string, SolutionPage> = SOLUTION_PAGES.reduce(
  (acc, page) => ({ ...acc, [page.slug]: page }),
  {} as Record<string, SolutionPage>,
);

export const getSchemaForPage = (page: SolutionPage) => buildSchema(page);

/**
 * How each solution and industry page is described when another one links to
 * it. The anchor names the category a buyer searches for, so every inbound link
 * tells a crawler what the target page is about; the summary restates what that
 * page itself says, never more.
 */
export const RELATED_SOLUTION_ANCHORS: Record<string, { anchor: string; summary: string }> = {
  'performance-management-software': {
    anchor: 'Performance management software',
    summary: 'Goals, 360° reviews, competency assessment and development plans in one connected platform.',
  },
  '360-degree-feedback-software': {
    anchor: '360-degree feedback software',
    summary:
      'Anonymised peer, manager and direct-report feedback, structured against the competencies that matter for each role.',
  },
  'okr-goal-setting-software': {
    anchor: 'OKR and goal-setting software',
    summary:
      'Cascade organisation objectives to every individual goal, with enforced parent-goal linkage and at-risk flags.',
  },
  'employee-performance-review-software': {
    anchor: 'Employee performance review software',
    summary: 'Self-review, 360° input, manager review and calibration, combined into one defensible rating.',
  },
  'performance-calibration-software': {
    anchor: 'Performance calibration software',
    summary:
      'Rating heatmaps across managers, outlier and bias flags, optional forced distribution and an audit log of every rating change.',
  },
  'competency-management-software': {
    anchor: 'Competency management software',
    summary: 'A reusable competency library with behavioural anchors, proficiency levels and skills-gap dashboards.',
  },
  'continuous-performance-management': {
    anchor: 'Continuous performance management',
    summary: '1-on-1s, goal check-ins, feedback and a performance journal that carry into the year-end review.',
  },
  'ai-performance-management': {
    anchor: 'AI performance management software',
    summary:
      'Ava drafts reviews from evidence, summarises 360° feedback and flags potential rating bias. Managers make the decisions.',
  },
  'succession-planning-software': {
    anchor: 'Succession planning software',
    summary: 'Identify and develop successors for critical roles with 9-box grids, readiness ratings and IDPs.',
  },
  'employee-development-software': {
    anchor: 'Employee development and IDP software',
    summary: 'Turn competency gaps into individual development plans that managers track at every check-in.',
  },
  'talent-management-software': {
    anchor: 'Talent management software',
    summary:
      'Performance, competencies, development plans and succession on one competency framework, with Ava drafting and people deciding.',
  },
  'skills-management-software': {
    anchor: 'Skills management software',
    summary:
      'A skills matrix built from assessments against each role’s required levels, with coverage and gaps by team and department.',
  },
  'employee-engagement-survey-software': {
    anchor: 'Employee engagement and pulse survey software',
    summary:
      'No survey product: how survey findings become 1-on-1 actions, goals and evidence, with anonymity and GDPR kept in order.',
  },
  'performance-management-healthcare': {
    anchor: 'Performance management software for healthcare',
    summary: 'Clinical competency sign-off, scheduled reassessment and an audit trail on every rating.',
  },
  'performance-management-financial-services': {
    anchor: 'Performance management for financial services',
    summary:
      'Evidence-based competency records, audit-logged reviews and role-based access for banks, insurers and asset managers.',
  },
  'performance-management-technology-companies': {
    anchor: 'Performance management for technology companies',
    summary:
      'Quarterly OKRs, skills-based career ladders, 360° feedback and calibration for engineering and product teams.',
  },
  'performance-management-retail': {
    anchor: 'Performance management software for retail',
    summary: 'Mobile reviews between shifts, role-based competencies and multi-site reporting for store teams.',
  },
  'performance-management-software-europe': {
    anchor: 'Performance management software for Ireland and Europe',
    summary:
      'DSAR exports, erasure workflows, a DPA and 16 languages, with works-council and EU AI Act questions answered plainly.',
  },
};

/**
 * The curated hub-and-spoke graph behind each page's "Related solutions" block:
 * three or four neighbours a buyer on that page is most likely to evaluate next.
 * Curated rather than generated, so the links follow how the products connect —
 * competency feeds development plans and goals, 360° feedback feeds continuous
 * performance management — and every page, industry pages included, receives
 * at least one link from another page's block, not only from the nav and the
 * /solutions hub. Keys and targets must be slugs in SOLUTION_PAGES; the
 * prerender tests hold the graph to that.
 */
export const RELATED_SOLUTION_SLUGS: Record<string, string[]> = {
  'performance-management-software': [
    '360-degree-feedback-software',
    'okr-goal-setting-software',
    'continuous-performance-management',
    'ai-performance-management',
  ],
  '360-degree-feedback-software': [
    'continuous-performance-management',
    'employee-engagement-survey-software',
    'competency-management-software',
    'employee-development-software',
  ],
  'okr-goal-setting-software': [
    'competency-management-software',
    'employee-development-software',
    'continuous-performance-management',
    'performance-management-technology-companies',
  ],
  // Calibration is the review page's closest neighbour; OKR software keeps its
  // inbound links from the performance, development, engagement and technology
  // pages.
  'employee-performance-review-software': [
    'performance-calibration-software',
    '360-degree-feedback-software',
    'competency-management-software',
    'performance-management-financial-services',
  ],
  // The cycle calibration sits in, the AI prompts that flag bias before it, the
  // 9-box talent review after it, and the EU governance questions around it.
  'performance-calibration-software': [
    'employee-performance-review-software',
    'ai-performance-management',
    'succession-planning-software',
    'performance-management-software-europe',
  ],
  'competency-management-software': [
    'employee-development-software',
    'skills-management-software',
    'performance-management-healthcare',
    'succession-planning-software',
  ],
  'continuous-performance-management': [
    '360-degree-feedback-software',
    'employee-engagement-survey-software',
    'employee-development-software',
    'performance-management-retail',
  ],
  // Ava's bias prompts hand over to calibration; continuous performance
  // management keeps its inbound links from six other pages.
  'ai-performance-management': [
    'employee-performance-review-software',
    'performance-calibration-software',
    '360-degree-feedback-software',
    'performance-management-software',
  ],
  'succession-planning-software': [
    'competency-management-software',
    'employee-development-software',
    'employee-performance-review-software',
    'talent-management-software',
  ],
  'employee-development-software': [
    'competency-management-software',
    'okr-goal-setting-software',
    'succession-planning-software',
    '360-degree-feedback-software',
  ],
  // The umbrella page links down to the four processes it connects; the
  // spokes link back from their own "Related solutions" block or link list.
  'talent-management-software': [
    'performance-management-software',
    'competency-management-software',
    'employee-development-software',
    'succession-planning-software',
  ],
  'skills-management-software': [
    'competency-management-software',
    'employee-development-software',
    'succession-planning-software',
    'performance-management-technology-companies',
  ],
  'employee-engagement-survey-software': [
    'continuous-performance-management',
    '360-degree-feedback-software',
    'okr-goal-setting-software',
    'employee-development-software',
  ],
  'performance-management-healthcare': [
    'competency-management-software',
    'employee-performance-review-software',
    'continuous-performance-management',
  ],
  'performance-management-financial-services': [
    'competency-management-software',
    'employee-performance-review-software',
    'succession-planning-software',
    'performance-management-software-europe',
  ],
  'performance-management-technology-companies': [
    'okr-goal-setting-software',
    '360-degree-feedback-software',
    'competency-management-software',
    'performance-calibration-software',
  ],
  'performance-management-retail': [
    'competency-management-software',
    'continuous-performance-management',
    'employee-performance-review-software',
  ],
  'performance-management-software-europe': [
    'ai-performance-management',
    'performance-management-software',
    '360-degree-feedback-software',
    'employee-performance-review-software',
  ],
};

export interface RelatedSolutionLink {
  href: string;
  anchor: string;
  summary: string;
}

export const getRelatedSolutions = (page: SolutionPage): RelatedSolutionLink[] =>
  (RELATED_SOLUTION_SLUGS[page.slug] ?? []).flatMap((slug) => {
    const target = SOLUTION_PAGE_BY_SLUG[slug];
    const copy = RELATED_SOLUTION_ANCHORS[slug];
    if (!target || !copy || slug === page.slug) return [];
    return [{ href: `/${target.category}/${target.slug}`, ...copy }];
  });
